// Persistencia 360 Dashboard - Cache bust: 2026-04-17-v2
// Build timestamp: 1744790400
// Force Cloudflare cache invalidation - April 2026 - DEPLOY 19
import React, { useState, useEffect, useCallback, useRef } from "react";

// Cache invalidation marker - DO NOT REMOVE
const BUILD_VERSION = "2026-04-17-v2";
window.__persistencia360_build_version = BUILD_VERSION;
console.log("[Persistencia360] Build version:", BUILD_VERSION);
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend,
  ResponsiveContainer, ReferenceLine, ComposedChart, Bar, BarChart, PieChart, Pie, Cell
} from "recharts";
import {
  Upload, Users, TrendingUp, AlertTriangle, Activity, CheckCircle,
  Search, ChevronLeft, ChevronRight, ChevronDown, Plus, Building2, BarChart3, Layers,
  FileSpreadsheet, Loader2, X, RefreshCw, Download, FileDown, Filter, Info, HelpCircle,
  Eye, Clock, BookOpen, Award, MessageSquare, FileX, GraduationCap, Mail, Hash,
  History, Trash2, Link, Monitor, CalendarDays, XCircle, Settings, LogOut, BarChart2, Pencil, Edit,
  Headphones, Phone, Send, Zap, LayoutDashboard, MapPin, Trophy, ClipboardCheck, GitBranch, Target,
  Database, ShieldCheck
} from "lucide-react";
import { Toaster, toast } from "sonner";

// User Management Component
import UserManagement from './components/UserManagement';
import PDFPreview, { generatePDFDirect } from './components/PDFPreview';
import NPS360Dashboard from './components/NPS360Dashboard';
import Atencion360Dashboard from './components/Atencion360Dashboard';
import DerivacionesPorEquipo from './components/DerivacionesPorEquipo';
import ReincidenciaView from './components/ReincidenciaView';
import FCRPorIncidenciaView from './components/FCRPorIncidenciaView';
import AgentQualityCalibration from './components/AgentQualityCalibration';
import Enrolados360Dashboard from './components/Enrolados360Dashboard';
import PersistenciaEngine from './components/PersistenciaEngine';
import PersistenciaSyncSemaforo from './components/persistencia/PersistenciaSyncSemaforo';
import AcademiaPorPeriodo from './components/AcademiaPorPeriodo';
import SecureExportModal from './components/SecureExportModal';
import { secureExportDownload } from './utils/secureExport';
import ExportAuditLog from './components/ExportAuditLog';
import { SEMAFORO_COLORS } from './constants/semaforo';

// Logo oficial de Capabilia
import capabiliaLogo from './assets/capabilia-logo.png';

// Estilos del logo para fondo verde (convierte negro a transparente)
const logoStyleWhite = {
  filter: 'invert(1)',
  mixBlendMode: 'screen'
};

const logoStyleColor = {
  // Filtro para convertir logo blanco a verde Capabilia
  filter: 'brightness(0) saturate(100%) invert(45%) sepia(85%) saturate(1500%) hue-rotate(130deg) brightness(95%)'
};

// Shadcn UI Components
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

import "@/App.css";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Colores oficiales de Capabilia
const CAPABILIA = {
  green: '#00A878',
  greenDark: '#007A55',
  white: '#FFFFFF',
  grayLight: '#F5F5F5',
  textDark: '#1A1A1A'
};

// ============================================================
// CONSTANTS
// ============================================================

// SISTEMA DE SEMÁFORO DE 8 ESTADOS (Labels unificados para todos los partners)
// Fuente movida a ./constants/semaforo.js para permitir reutilización sin imports circulares.

// Alias para compatibilidad con código existente
const RISK_COLORS = SEMAFORO_COLORS;

const CHART_COLORS = {
  green: "#10B981",
  blue: "#3B82F6",
  purple: "#8B5CF6",
  orange: "#F97316",
  yellow: "#FBBF24",
  red: "#EF4444",
  dark_gray: "#6B7280",
  black: "#111827"
};

// SISTEMA DE SEMÁFORO - Helper function for risk labels (unificado)
const RISK_LABELS = {
  green: "Activo con hitos",
  blue: "Acompañar",
  purple: "Finalizado",
  orange: "Sin actividad",
  yellow: "Activo sin hitos",
  red: "En riesgo",
  dark_gray: "Inactivo",
  black: "No iniciado"
};

const getRiskLabelStatic = (color) => RISK_LABELS[color] || "Sin datos";

// ============================================================
// RISK CRITERIA LEGEND COMPONENT - SISTEMA PENTACOLOR
// ============================================================

const RiskCriteriaLegend = ({ variant = "popover", partnerSlug = null }) => {
  const isHitosPartner = ['areandina', 'cordoba-crea-futuro', 'barcelona', 'conmebol', 'siglo-21'].includes(partnerSlug);
  
  const hitosContent = (
    <div className="space-y-3 text-sm">
      <h4 className="font-semibold text-slate-800 flex items-center gap-2">
        <HelpCircle className="w-4 h-4" />
        Criterios del Semáforo (Hitos)
      </h4>
      <div className="space-y-2">
        <div className="flex items-start gap-2">
          <span className="w-3 h-3 rounded-full mt-1 flex-shrink-0" style={{ backgroundColor: CHART_COLORS.purple }}></span>
          <div>
            <span className="font-medium text-purple-700">🟣 Finalizado:</span>
            <span className="text-slate-600"> Completó todos sus cursos (programa terminado).</span>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <span className="w-3 h-3 rounded-full mt-1 flex-shrink-0" style={{ backgroundColor: CHART_COLORS.green }}></span>
          <div>
            <span className="font-medium text-green-700">🟢 Activo con hitos:</span>
            <span className="text-slate-600"> Ingresó hace ≤7 días + tiene nota, views o sin faltantes.</span>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <span className="w-3 h-3 rounded-full mt-1 flex-shrink-0" style={{ backgroundColor: CHART_COLORS.blue }}></span>
          <div>
            <span className="font-medium text-blue-700">🔵 Acompañar:</span>
            <span className="text-slate-600"> Ingresó hace 7-30 días + tiene hitos cumplidos.</span>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <span className="w-3 h-3 rounded-full mt-1 flex-shrink-0" style={{ backgroundColor: CHART_COLORS.yellow }}></span>
          <div>
            <span className="font-medium text-yellow-700">🟡 Activo sin hitos:</span>
            <span className="text-slate-600"> Ingresó hace ≤7 días + sin nota, sin views, con faltantes.</span>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <span className="w-3 h-3 rounded-full mt-1 flex-shrink-0" style={{ backgroundColor: CHART_COLORS.orange }}></span>
          <div>
            <span className="font-medium text-orange-700">🟠 Sin actividad:</span>
            <span className="text-slate-600"> Ingresó hace 7-10 días + sin hitos.</span>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <span className="w-3 h-3 rounded-full mt-1 flex-shrink-0" style={{ backgroundColor: CHART_COLORS.red }}></span>
          <div>
            <span className="font-medium text-red-700">🔴 En riesgo:</span>
            <span className="text-slate-600"> Ingresó hace 10-30 días + sin hitos.</span>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <span className="w-3 h-3 rounded-full mt-1 flex-shrink-0" style={{ backgroundColor: CHART_COLORS.dark_gray }}></span>
          <div>
            <span className="font-medium text-gray-600">⚫ Inactivo:</span>
            <span className="text-slate-600"> Sin ingresar hace más de 30 días.</span>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <span className="w-3 h-3 rounded-full mt-1 flex-shrink-0" style={{ backgroundColor: CHART_COLORS.black }}></span>
          <div>
            <span className="font-medium text-gray-800">⬛ No iniciado:</span>
            <span className="text-slate-600"> Nunca ingresó a la plataforma.</span>
          </div>
        </div>
      </div>
    </div>
  );
  
  const content = (
    <div className="space-y-3 text-sm">
      <h4 className="font-semibold text-slate-800 flex items-center gap-2">
        <HelpCircle className="w-4 h-4" />
        Criterios del Semáforo
      </h4>
      <div className="space-y-2">
        <div className="flex items-start gap-2">
          <span className="w-3 h-3 rounded-full mt-1 flex-shrink-0" style={{ backgroundColor: CHART_COLORS.green }}></span>
          <div>
            <span className="font-medium text-green-700">🟢 Activo con hitos:</span>
            <span className="text-slate-600"> Ingresó hace ≤7 días + tiene nota, views o sin faltantes.</span>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <span className="w-3 h-3 rounded-full mt-1 flex-shrink-0" style={{ backgroundColor: CHART_COLORS.blue }}></span>
          <div>
            <span className="font-medium text-blue-700">🔵 Acompañar:</span>
            <span className="text-slate-600"> Ingresó hace 7-30 días + tiene hitos cumplidos.</span>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <span className="w-3 h-3 rounded-full mt-1 flex-shrink-0" style={{ backgroundColor: CHART_COLORS.yellow }}></span>
          <div>
            <span className="font-medium text-yellow-700">🟡 Activo sin hitos:</span>
            <span className="text-slate-600"> Ingresó hace ≤7 días + sin nota, sin views, con faltantes.</span>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <span className="w-3 h-3 rounded-full mt-1 flex-shrink-0" style={{ backgroundColor: CHART_COLORS.orange }}></span>
          <div>
            <span className="font-medium text-orange-700">🟠 Sin actividad:</span>
            <span className="text-slate-600"> Ingresó hace 7-10 días + sin hitos.</span>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <span className="w-3 h-3 rounded-full mt-1 flex-shrink-0" style={{ backgroundColor: CHART_COLORS.red }}></span>
          <div>
            <span className="font-medium text-red-700">🔴 En riesgo:</span>
            <span className="text-slate-600"> Ingresó hace 10-30 días + sin hitos.</span>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <span className="w-3 h-3 rounded-full mt-1 flex-shrink-0" style={{ backgroundColor: CHART_COLORS.dark_gray }}></span>
          <div>
            <span className="font-medium text-gray-600">⚫ Inactivo:</span>
            <span className="text-slate-600"> Sin nota + más de 30 días + tuvo actividad previa.</span>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <span className="w-3 h-3 rounded-full mt-1 flex-shrink-0" style={{ backgroundColor: CHART_COLORS.black }}></span>
          <div>
            <span className="font-medium text-gray-800">⬛ No Iniciado:</span>
            <span className="text-slate-600"> Sin nota + más de 30 días + sin actividad registrada.</span>
          </div>
        </div>
      </div>
    </div>
  );
  
  const activeContent = isHitosPartner ? hitosContent : content;

  if (variant === "inline") {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
        {activeContent}
      </div>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-700 gap-1.5">
          <Info className="w-4 h-4" />
          <span className="text-xs">Ver criterios</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96" align="start">
        {activeContent}
      </PopoverContent>
    </Popover>
  );
};

// ============================================================
// UTILITY FUNCTIONS
// ============================================================

const formatNumber = (num) => {
  if (num === null || num === undefined) return "—";
  return new Intl.NumberFormat("es-AR").format(num);
};

const formatPercent = (num) => {
  if (num === null || num === undefined) return "—";
  return `${num.toFixed(1)}%`;
};

// ============================================================
// API SERVICE
// ============================================================

// Helper para obtener token del localStorage
const getAuthToken = () => localStorage.getItem('auth_token');

// Helper para crear headers con autenticación
const getAuthHeaders = (contentType = null) => {
  const headers = {};
  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (contentType) {
    headers['Content-Type'] = contentType;
  }
  return headers;
};

// Helper para manejar respuestas con redirección a login si 401
const handleResponse = async (res) => {
  if (res.status === 401) {
    // Solo limpiar si hay un token guardado (evita loop en login inicial)
    const hasToken = localStorage.getItem('auth_token');
    if (hasToken) {
      console.log('[handleResponse] 401 recibido, limpiando token y recargando');
      // Token expirado o inválido, limpiar y recargar
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      window.location.reload();
    }
    throw new Error("Sesión expirada");
  }
  return res;
};

const api = {
  async getPartners() {
    const res = await fetch(`${API_URL}/api/partners`, {
      headers: getAuthHeaders()
    });
    await handleResponse(res);
    if (!res.ok) throw new Error("Error fetching partners");
    return res.json();
  },
  
  async createPartner(name) {
    const res = await fetch(`${API_URL}/api/partners`, {
      method: "POST",
      headers: getAuthHeaders('application/json'),
      body: JSON.stringify({ name })
    });
    await handleResponse(res);
    if (!res.ok) throw new Error("Error creating partner");
    return res.json();
  },
  
  async getDashboard(partnerId, program = null, section = null, course = null, dateFrom = null, dateTo = null, activePeriodDays = 90, egresadosPeriodDays = null, periodo = null, etapa = null) {
    const params = new URLSearchParams();
    if (program && program !== "all") params.append("program", program);
    if (section && section !== "all") params.append("section", section);
    if (course && course !== "all") params.append("course", course);
    if (dateFrom) params.append("date_from", dateFrom);
    if (dateTo) params.append("date_to", dateTo);
    if (activePeriodDays) params.append("active_period_days", activePeriodDays);
    if (egresadosPeriodDays !== null) params.append("egresados_period_days", egresadosPeriodDays);
    if (periodo) params.append("periodo", periodo);
    if (etapa) params.append("etapa", etapa);
    const queryString = params.toString() ? `?${params.toString()}` : "";
    const res = await fetch(`${API_URL}/api/partners/${partnerId}/dashboard${queryString}`, {
      headers: getAuthHeaders()
    });
    await handleResponse(res);
    if (!res.ok) throw new Error("Error fetching dashboard");
    return res.json();
  },
  
  async getCertificatesSummary(partnerId, view = "todos", sectionFilter = null) {
    const params = new URLSearchParams();
    if (view) params.append("view", view);
    if (sectionFilter && sectionFilter !== "all") params.append("section_filter", sectionFilter);
    const queryString = params.toString() ? `?${params.toString()}` : "";
    const res = await fetch(`${API_URL}/api/partners/${partnerId}/certificates_summary${queryString}`, {
      headers: getAuthHeaders()
    });
    await handleResponse(res);
    if (!res.ok) throw new Error("Error fetching certificates summary");
    return res.json();
  },
  
  async getSections(partnerId) {
    const res = await fetch(`${API_URL}/api/partners/${partnerId}/sections`, {
      headers: getAuthHeaders()
    });
    await handleResponse(res);
    if (!res.ok) throw new Error("Error fetching sections");
    return res.json();
  },
  
  async getCourses(partnerId, section = null) {
    const params = section && section !== "all" ? `?section=${encodeURIComponent(section)}` : "";
    const res = await fetch(`${API_URL}/api/partners/${partnerId}/courses${params}`, {
      headers: getAuthHeaders()
    });
    await handleResponse(res);
    if (!res.ok) throw new Error("Error fetching courses");
    return res.json();
  },
  
  async getEvolution(partnerId) {
    const res = await fetch(`${API_URL}/api/partners/${partnerId}/evolution`, {
      headers: getAuthHeaders()
    });
    await handleResponse(res);
    if (!res.ok) throw new Error("Error fetching evolution");
    return res.json();
  },
  
  async getStudents(partnerId, params = {}) {
    const query = new URLSearchParams(params).toString();
    const res = await fetch(`${API_URL}/api/partners/${partnerId}/students?${query}`, {
      headers: getAuthHeaders()
    });
    await handleResponse(res);
    if (!res.ok) throw new Error("Error fetching students");
    return res.json();
  },
  
  // Constantes para chunked upload
  CHUNK_SIZE: 1 * 1024 * 1024,  // 1MB por chunk (más seguro para K8s Ingress/proxy)
  SINGLE_FILE_THRESHOLD: 4 * 1024 * 1024,  // 4MB umbral por archivo individual
  TOTAL_SIZE_THRESHOLD: 8 * 1024 * 1024,  // 8MB umbral para total combinado
  
  async processFiles(partnerId, files, referenceDate, onProgress) {
    // Calcular tamaño total de todos los archivos
    const fileList = [
      { key: 'sis', file: files.sis, type: 'sis_file' },
      { key: 'lastAccess', file: files.lastAccess, type: 'last_access_file' },
      { key: 'grades', file: files.grades, type: 'grades_file' },
      { key: 'submissions', file: files.submissions, type: 'submissions_file' },
      { key: 'engagement', file: files.engagement, type: 'engagement_file' },
      { key: 'provisioning', file: files.provisioning, type: 'provisioning_file' },
      { key: 'baseEnrolados', file: files.baseEnrolados, type: 'base_enrolados_file' },
    ].filter(f => f.file);
    
    const totalSize = fileList.reduce((sum, f) => sum + f.file.size, 0);
    const hasLargeFile = fileList.some(f => f.file.size > this.SINGLE_FILE_THRESHOLD);
    
    console.log(`📊 Total archivos: ${fileList.length}, Tamaño total: ${(totalSize / (1024*1024)).toFixed(1)} MB`);
    
    // Usar chunked upload si cualquier archivo es grande O el total supera el umbral
    if (hasLargeFile || totalSize > this.TOTAL_SIZE_THRESHOLD) {
      console.log(`📦 Usando upload chunked (archivos grandes o total > 8MB)`);
      return this.processFilesChunked(partnerId, files, referenceDate, onProgress);
    }
    
    // Upload normal para archivos pequeños (total < 8MB)
    const formData = new FormData();
    fileList.forEach(f => formData.append(f.type, f.file));
    // Agregar fecha de referencia
    if (referenceDate) {
      formData.append('reference_date', referenceDate);
    }
    
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/api/partners/${partnerId}/process`);
      
      const token = getAuthToken();
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      
      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch (e) {
            resolve({ status: 'ok' });
          }
        } else {
          let errorMessage = "Error procesando archivos";
          try {
            const err = JSON.parse(xhr.responseText);
            errorMessage = err.detail || errorMessage;
          } catch {
            errorMessage = `Error del servidor (${xhr.status}): ${xhr.statusText}`;
          }
          reject(new Error(errorMessage));
        }
      };
      
      xhr.onerror = function() {
        reject(new Error("Error de conexión con el servidor. Verifica tu conexión."));
      };
      
      xhr.ontimeout = function() {
        reject(new Error("Timeout: El archivo es muy grande. Intenta con archivos más pequeños."));
      };
      
      xhr.timeout = 600000;
      xhr.send(formData);
    });
  },
  
  // Upload chunked para archivos grandes - sube TODOS los archivos individualmente
  async processFilesChunked(partnerId, files, referenceDate, onProgress) {
    const uploadId = crypto.randomUUID();
    
    // Lista de todos los archivos a subir
    const fileList = [
      { key: 'sis', file: files.sis, type: 'sis' },
      { key: 'lastAccess', file: files.lastAccess, type: 'last_access' },
      { key: 'grades', file: files.grades, type: 'grades' },
      { key: 'submissions', file: files.submissions, type: 'submissions' },
      { key: 'engagement', file: files.engagement, type: 'engagement' },
      { key: 'provisioning', file: files.provisioning, type: 'provisioning' },
      { key: 'baseEnrolados', file: files.baseEnrolados, type: 'base_enrolados' },
    ].filter(f => f.file);
    
    const totalFiles = fileList.length;
    let filesUploaded = 0;
    
    console.log(`🚀 Iniciando upload chunked de ${totalFiles} archivos`);
    
    // Subir cada archivo en chunks
    for (const fileInfo of fileList) {
      const file = fileInfo.file;
      const fileType = fileInfo.type;
      const totalChunks = Math.ceil(file.size / this.CHUNK_SIZE);
      
      console.log(`📤 Subiendo ${fileType}: ${(file.size / (1024*1024)).toFixed(1)} MB en ${totalChunks} chunks`);
      
      for (let i = 0; i < totalChunks; i++) {
        const start = i * this.CHUNK_SIZE;
        const end = Math.min(start + this.CHUNK_SIZE, file.size);
        const chunk = file.slice(start, end);
        
        const chunkFormData = new FormData();
        chunkFormData.append("upload_id", uploadId);
        chunkFormData.append("chunk_index", i.toString());
        chunkFormData.append("total_chunks", totalChunks.toString());
        chunkFormData.append("file_type", fileType);
        chunkFormData.append("chunk", chunk, file.name);
        
        await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', `${API_URL}/api/partners/${partnerId}/upload_chunk`);
          xhr.timeout = 120000;
          
          const token = getAuthToken();
          if (token) {
            xhr.setRequestHeader('Authorization', `Bearer ${token}`);
          }
          
          xhr.onload = function() {
            if (xhr.status >= 200 && xhr.status < 300) {
              if (onProgress) {
                const overallProgress = Math.round(((filesUploaded + (i + 1) / totalChunks) / totalFiles) * 80);
                onProgress({ 
                  phase: 'uploading', 
                  progress: overallProgress,
                  message: `Subiendo ${fileType} (${i + 1}/${totalChunks})...`
                });
              }
              resolve();
            } else {
              // Incluir más detalles del error
              let errorDetail = xhr.statusText;
              try {
                const errBody = JSON.parse(xhr.responseText);
                errorDetail = errBody.detail || errBody.message || xhr.statusText;
              } catch (e) {
                errorDetail = xhr.responseText?.substring(0, 200) || xhr.statusText;
              }
              console.error(`❌ Chunk upload failed: status=${xhr.status}, detail=${errorDetail}`);
              reject(new Error(`Error subiendo ${fileType} chunk ${i + 1}: ${errorDetail} (HTTP ${xhr.status})`));
            }
          };
          
          xhr.onerror = () => reject(new Error(`Error de red subiendo ${fileType}`));
          xhr.ontimeout = () => reject(new Error(`Timeout subiendo ${fileType}`));
          xhr.send(chunkFormData);
        });
      }
      
      filesUploaded++;
      console.log(`✅ ${fileType} subido completamente`);
    }
    
    console.log(`✅ Todos los archivos subidos. Iniciando procesamiento...`);
    if (onProgress) {
      onProgress({ phase: 'processing', progress: 85, message: 'Procesando archivos...' });
    }
    
    // Solicitar procesamiento (sin archivos adicionales, ya todos están en el servidor)
    const processFormData = new FormData();
    processFormData.append("upload_id", uploadId);
    // Agregar fecha de referencia
    if (referenceDate) {
      processFormData.append("reference_date", referenceDate);
    }
    
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API_URL}/api/partners/${partnerId}/process_chunked`);
      xhr.timeout = 600000;
      
      // Agregar token de autenticación
      const token = getAuthToken();
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      
      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch (e) {
            resolve({ status: 'ok' });
          }
        } else {
          let errorMessage = "Error procesando archivos";
          try {
            const err = JSON.parse(xhr.responseText);
            errorMessage = err.detail || errorMessage;
          } catch {
            errorMessage = `Error del servidor (${xhr.status}): ${xhr.statusText}`;
          }
          reject(new Error(errorMessage));
        }
      };
      
      xhr.onerror = () => reject(new Error("Error de conexión procesando archivos."));
      xhr.ontimeout = () => reject(new Error("Timeout procesando archivos."));
      xhr.send(processFormData);
    });
  },
  
  // Get programs for a partner
  async getPrograms(partnerId) {
    const res = await fetch(`${API_URL}/api/partners/${partnerId}/programs`, {
      headers: getAuthHeaders()
    });
    await handleResponse(res);
    if (!res.ok) throw new Error("Failed to fetch programs");
    return res.json();
  },
  
  // Get program stats for comparison table
  async getProgramStats(partnerId) {
    const res = await fetch(`${API_URL}/api/partners/${partnerId}/programs/stats`, {
      headers: getAuthHeaders()
    });
    await handleResponse(res);
    if (!res.ok) throw new Error("Failed to fetch program stats");
    return res.json();
  },
  
  // Export students to CSV/Excel (retorna URL con token)
  getExportUrl(partnerId, params = {}) {
    const query = new URLSearchParams(params).toString();
    const token = getAuthToken();
    const tokenParam = token ? `&token=${token}` : '';
    return `${API_URL}/api/partners/${partnerId}/export?${query}${tokenParam}`;
  },
  
  // Export base de alumnos para persistencia (CASA) - superadmin/gestor_academico/casa
  getStudentsCasaExportUrl(partnerId, params = {}) {
    const query = new URLSearchParams(params).toString();
    const token = getAuthToken();
    const tokenParam = token ? `&token=${token}` : '';
    return `${API_URL}/api/partners/${partnerId}/students/export?${query}${tokenParam}`;
  },
  
  // Get batches for a partner
  async getBatches(partnerId) {
    const res = await fetch(`${API_URL}/api/partners/${partnerId}/batches`, {
      headers: getAuthHeaders()
    });
    await handleResponse(res);
    if (!res.ok) throw new Error("Failed to fetch batches");
    return res.json();
  },
  
  // Get import history with snapshots
  async getImportHistory(partnerId) {
    const res = await fetch(`${API_URL}/api/partners/${partnerId}/import-history`, {
      headers: getAuthHeaders()
    });
    await handleResponse(res);
    if (!res.ok) throw new Error("Failed to fetch import history");
    return res.json();
  },
  
  // Delete a batch (rollback)
  async deleteBatch(batchId) {
    const res = await fetch(`${API_URL}/api/batches/${batchId}`, {
      method: "DELETE",
      headers: getAuthHeaders()
    });
    await handleResponse(res);
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || "Failed to delete batch");
    }
    // DELETE exitoso - no intentar leer body de nuevo
    return { success: true };
  },
  
  // Get student 360 profile
  async getStudentProfile360(studentId) {
    const res = await fetch(`${API_URL}/api/students/${studentId}/profile360`, {
      headers: getAuthHeaders()
    });
    await handleResponse(res);
    if (!res.ok) throw new Error("Error fetching student profile");
    return res.json();
  },
  
  // Upload NPS file
  async uploadNPS(npsFile) {
    const formData = new FormData();
    formData.append("nps_file", npsFile);
    
    const res = await fetch(`${API_URL}/api/nps/upload`, {
      method: "POST",
      headers: { 'Authorization': `Bearer ${getAuthToken()}` },
      body: formData
    });
    
    await handleResponse(res);
    if (!res.ok) {
      let errorMessage = "Error procesando archivo NPS";
      try {
        const err = await res.json();
        errorMessage = err.detail || errorMessage;
      } catch {
        errorMessage = `Error del servidor (${res.status})`;
      }
      throw new Error(errorMessage);
    }
    
    return res.json();
  },
  
  // NPS Batches
  async getNpsBatches() {
    const res = await fetch(`${API_URL}/api/nps/batches`, {
      headers: getAuthHeaders()
    });
    await handleResponse(res);
    if (!res.ok) throw new Error("Error fetching NPS batches");
    return res.json();
  },
  
  async deleteNpsBatch(batchId) {
    const res = await fetch(`${API_URL}/api/nps/batches/${batchId}`, {
      method: "DELETE",
      headers: getAuthHeaders()
    });
    await handleResponse(res);
    if (!res.ok) throw new Error("Error eliminando batch NPS");
    return res.json();
  },
  
  async getNpsStats(partnerId, instance = null) {
    const params = new URLSearchParams();
    if (partnerId) params.append("partner_id", partnerId);
    if (instance) params.append("instance", instance);
    const queryString = params.toString() ? `?${params.toString()}` : "";
    const res = await fetch(`${API_URL}/api/nps/stats${queryString}`, {
      headers: getAuthHeaders()
    });
    await handleResponse(res);
    if (!res.ok) throw new Error("Error fetching NPS stats");
    return res.json();
  },
  
  // NPS Overview (comparativo global)
  async getNpsOverview(partnerId = null, monthFilter = null) {
    let url = `${API_URL}/api/nps/overview`;
    const queryParams = [];
    if (partnerId && partnerId !== "all") {
      queryParams.push(`partner_id=${partnerId}`);
    }
    if (monthFilter && monthFilter !== "all") {
      queryParams.push(`month_filter=${monthFilter}`);
    }
    if (queryParams.length > 0) {
      url += `?${queryParams.join("&")}`;
    }
    const res = await fetch(url, {
      headers: getAuthHeaders()
    });
    await handleResponse(res);
    if (!res.ok) throw new Error("Error fetching NPS overview");
    return res.json();
  },
  
  // NPS Evolution (time series)
  async getNpsEvolution(instance = null, months = 12) {
    let url = `${API_URL}/api/nps/evolution?months=${months}`;
    if (instance && instance !== "all") {
      url += `&instance=${encodeURIComponent(instance)}`;
    }
    const res = await fetch(url, {
      headers: getAuthHeaders()
    });
    await handleResponse(res);
    if (!res.ok) throw new Error("Error fetching NPS evolution");
    return res.json();
  },
  
  // NPS Available Months
  async getNpsAvailableMonths(partnerId = null) {
    let url = `${API_URL}/api/nps/available_months`;
    if (partnerId && partnerId !== "all") {
      url += `?partner_id=${partnerId}`;
    }
    const res = await fetch(url, {
      headers: getAuthHeaders()
    });
    await handleResponse(res);
    if (!res.ok) throw new Error("Error fetching NPS months");
    return res.json();
  },
  
  // Export NPS Detractors
  getExportDetractorsUrl(instanceName = null) {
    const token = getAuthToken();
    let url = `${API_URL}/api/nps/export_detractors?token=${token}`;
    if (instanceName && instanceName !== "all") {
      url += `&instance_name=${encodeURIComponent(instanceName)}`;
    }
    return url;
  },
  
  // NPS Instance Details (drill-down)
  async getNpsInstanceDetails(instanceName) {
    const res = await fetch(`${API_URL}/api/nps/instance/${encodeURIComponent(instanceName)}/details`, {
      headers: getAuthHeaders()
    });
    await handleResponse(res);
    if (!res.ok) throw new Error("Error fetching instance details");
    return res.json();
  },
  
  // NPS Responses (Panel completo con filtros)
  async getNpsResponses(instance = null, npsFilter = null, search = null, page = 1) {
    const params = new URLSearchParams();
    if (instance && instance !== "all") params.append("instance", instance);
    if (npsFilter && npsFilter !== "all") params.append("nps_filter", npsFilter);
    if (search) params.append("search", search);
    params.append("page", page);
    params.append("page_size", 50);
    const queryString = params.toString();
    const res = await fetch(`${API_URL}/api/nps/responses?${queryString}`, {
      headers: getAuthHeaders()
    });
    await handleResponse(res);
    if (!res.ok) throw new Error("Error fetching NPS responses");
    return res.json();
  },
  
  // Re-vincular NPS responses a partners correctos
  async relinkNpsResponses() {
    const res = await fetch(`${API_URL}/api/nps/relink`, { 
      method: "POST",
      headers: getAuthHeaders()
    });
    await handleResponse(res);
    if (!res.ok) throw new Error("Error relinking NPS responses");
    return res.json();
  },
  
  // HubSpot Tickets (Centro de Atención)
  async uploadTickets(partnerId, file) {
    const formData = new FormData();
    formData.append("file", file);
    const res = await fetch(`${API_URL}/api/partners/${partnerId}/upload_tickets`, {
      method: "POST",
      headers: { 'Authorization': `Bearer ${getAuthToken()}` },
      body: formData,
      cache: 'no-store'
    });
    const resClone = res.clone();
    await handleResponse(res);
    if (!res.ok) {
      let err;
      try {
        err = await resClone.json();
      } catch {
        err = { detail: "Error uploading tickets" };
      }
      throw new Error(err.detail || "Error uploading tickets");
    }
    return resClone.json();
  },
  
  async getTickets(partnerId) {
    const res = await fetch(`${API_URL}/api/partners/${partnerId}/tickets`, {
      headers: getAuthHeaders(),
      cache: 'no-store'
    });
    const resClone = res.clone();
    await handleResponse(res);
    if (!res.ok) throw new Error("Error fetching tickets");
    return resClone.json();
  },

  // ============================================================
  // CANVAS SYNC API - Sincronización automática con Canvas LMS
  // ============================================================
  
  async getCanvasStatus() {
    const res = await fetch(`${API_URL}/api/canvas/status`, {
      headers: getAuthHeaders()
    });
    await handleResponse(res);
    if (!res.ok) throw new Error("Error fetching Canvas status");
    return res.json();
  },
  
  async testCanvasConnection() {
    const res = await fetch(`${API_URL}/api/canvas/test-connection`, {
      headers: getAuthHeaders()
    });
    await handleResponse(res);
    if (!res.ok) throw new Error("Error testing Canvas connection");
    return res.json();
  },
  
  async syncPartnerWithCanvas(partnerId) {
    const res = await fetch(`${API_URL}/api/canvas/sync/${partnerId}`, {
      method: "POST",
      headers: getAuthHeaders()
    });
    await handleResponse(res);
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: "Error sync Canvas" }));
      throw new Error(err.detail || "Error sincronizando con Canvas");
    }
    return res.json();
  },
  
  async getCanvasSyncStatus(partnerId) {
    const res = await fetch(`${API_URL}/api/canvas/sync/${partnerId}/status`, {
      headers: getAuthHeaders()
    });
    await handleResponse(res);
    if (!res.ok) throw new Error("Error fetching Canvas sync status");
    return res.json();
  },
  
  async getCanvasSchedules() {
    const res = await fetch(`${API_URL}/api/canvas/schedules`, {
      headers: getAuthHeaders()
    });
    await handleResponse(res);
    if (!res.ok) throw new Error("Error fetching Canvas schedules");
    return res.json();
  },
  
  // Qualtrics NPS Sync
  async syncQualticsNPS() {
    const res = await fetch(`${API_URL}/api/qualtrics/sync`, {
      method: "POST",
      headers: getAuthHeaders()
    });
    await handleResponse(res);
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: "Error sync Qualtrics" }));
      throw new Error(err.error || err.detail || "Error sincronizando con Qualtrics");
    }
    return res.json();
  },
  
  async getQualtricsSyncStatus() {
    const res = await fetch(`${API_URL}/api/qualtrics/sync/status`, {
      headers: getAuthHeaders()
    });
    await handleResponse(res);
    if (!res.ok) throw new Error("Error fetching Qualtrics sync status");
    return res.json();
  },
  
  async getQualticsConfig() {
    const res = await fetch(`${API_URL}/api/qualtrics/config`, {
      headers: getAuthHeaders()
    });
    await handleResponse(res);
    if (!res.ok) throw new Error("Error fetching Qualtrics config");
    return res.json();
  },
  
  async setCanvasSchedule(partnerId, scheduleType, hour = 6) {
    const params = new URLSearchParams();
    params.append("schedule_type", scheduleType);
    params.append("hour", hour);
    const res = await fetch(`${API_URL}/api/canvas/schedules/${partnerId}?${params.toString()}`, {
      method: "POST",
      headers: getAuthHeaders()
    });
    await handleResponse(res);
    if (!res.ok) throw new Error("Error setting Canvas schedule");
    return res.json();
  }
};

// ============================================================
// COMPONENTS
// ============================================================

// KPI Card
const KPICard = ({ title, value, subtitle, icon: Icon, color, trend }) => (
  <Card className="bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-slate-500 font-medium uppercase tracking-wider mb-1">{title}</p>
          <p className="text-3xl font-bold text-slate-900 font-manrope tabular-nums">{value}</p>
          {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
        </div>
        <div className="p-2 rounded-lg" style={{ backgroundColor: `${color}15` }}>
          <Icon className="w-6 h-6" style={{ color }} />
        </div>
      </div>
    </CardContent>
  </Card>
);

// KPI Card con selector de período (para Activos en Período)
const KPICardWithPeriodSelector = ({ 
  title, value, icon: Icon, color, 
  activePeriod, setActivePeriod, 
  customDateFrom, setCustomDateFrom, 
  customDateTo, setCustomDateTo,
  periodOptions 
}) => {
  const [showCustom, setShowCustom] = React.useState(false);
  
  return (
    <Card className="bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow col-span-2 md:col-span-1">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm text-slate-500 font-medium uppercase tracking-wider mb-1">{title}</p>
            <p className="text-3xl font-bold text-slate-900 font-manrope tabular-nums">{value}</p>
            <div className="mt-2">
              <select 
                value={activePeriod}
                onChange={(e) => {
                  setActivePeriod(e.target.value);
                  setShowCustom(e.target.value === "custom");
                }}
                className="text-xs bg-slate-100 border-0 rounded px-2 py-1 text-slate-600 cursor-pointer hover:bg-slate-200"
              >
                <option value="30d">Últimos 30 días</option>
                <option value="60d">Últimos 60 días</option>
                <option value="90d">Últimos 90 días</option>
                <option value="365d">Último año</option>
                <option value="custom">Rango personalizado</option>
              </select>
            </div>
            {(activePeriod === "custom" || showCustom) && (
              <div className="mt-2 flex gap-2">
                <input 
                  type="date" 
                  value={customDateFrom}
                  onChange={(e) => setCustomDateFrom(e.target.value)}
                  className="text-xs border rounded px-2 py-1 w-28"
                  placeholder="Desde"
                />
                <input 
                  type="date" 
                  value={customDateTo}
                  onChange={(e) => setCustomDateTo(e.target.value)}
                  className="text-xs border rounded px-2 py-1 w-28"
                  placeholder="Hasta"
                />
              </div>
            )}
          </div>
          <div className="p-2 rounded-lg" style={{ backgroundColor: `${color}15` }}>
            <Icon className="w-6 h-6" style={{ color }} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Risk Badge
const RiskBadge = ({ color, partnerSlug = null }) => {
  const isHitos = ['areandina', 'cordoba-crea-futuro', 'barcelona', 'conmebol', 'siglo-21'].includes(partnerSlug);
  const config = RISK_COLORS[color] || RISK_COLORS.green;
  
  const hitosLabels = {
    green: "Activo con hitos",
    blue: "Acompañar",
    yellow: "Activo sin hitos",
    orange: "Sin actividad",
    red: "En riesgo",
    dark_gray: "Inactivo",
    black: "No iniciado"
  };
  
  const label = isHitos ? (hitosLabels[color] || config.label) : config.label;
  
  return (
    <Badge
      style={{ backgroundColor: config.bg, color: config.text, borderColor: config.text }}
      variant="outline"
      className="text-xs font-medium"
    >
      {label}
    </Badge>
  );
};

// NPS Badge con Tooltip para mostrar comentario
const NPSBadge = ({ npsData }) => {
  if (!npsData) {
    return <span className="text-slate-400 text-xs">—</span>;
  }
  
  const { nps_group, score, comment } = npsData;
  
  const getBadgeStyle = () => {
    switch (nps_group) {
      case "Promoter":
        return "bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-200";
      case "Passive":
        return "bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200";
      case "Detractor":
        return "bg-red-100 text-red-800 border-red-300 hover:bg-red-200";
      default:
        return "bg-gray-100 text-gray-600 border-gray-300";
    }
  };
  
  const getLabel = () => {
    switch (nps_group) {
      case "Promoter": return "Promotor";
      case "Passive": return "Neutral";
      case "Detractor": return "Detractor";
      default: return nps_group || "N/A";
    }
  };
  
  const badgeContent = (
    <Badge variant="outline" className={`text-xs font-medium cursor-help transition-colors ${getBadgeStyle()}`}>
      <span className={`font-bold mr-1.5 ${nps_group === "Detractor" ? "text-red-900" : ""}`}>
        {score}
      </span>
      <span className="font-normal">{getLabel()}</span>
    </Badge>
  );
  
  // Si hay comentario válido, mostrar tooltip
  if (comment && comment.trim() && comment.trim().toLowerCase() !== "nan" && comment.trim().toLowerCase() !== "none") {
    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            {badgeContent}
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-sm bg-slate-900 text-white p-3 rounded-lg shadow-xl z-50">
            <div className="space-y-1">
              <p className="text-[11px] text-slate-400 uppercase tracking-wider">Comentario del alumno:</p>
              <p className="text-sm italic leading-relaxed">"{comment}"</p>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }
  
  return badgeContent;
};

// File Dropzone
const FileDropzone = ({ label, accept, file, onFileSelect, required }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) onFileSelect(droppedFile);
  };
  
  return (
    <div
      className={`border-2 border-dashed rounded-lg p-4 text-center transition-all ${
        isDragOver ? "border-blue-500 bg-blue-50" : file ? "border-green-500 bg-green-50" : "border-slate-200 hover:border-slate-300"
      }`}
      onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={handleDrop}
    >
      <p className="text-sm font-medium text-slate-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </p>
      {file ? (
        <div className="flex items-center justify-center gap-2">
          <CheckCircle className="w-4 h-4 text-green-600" />
          <span className="text-sm text-green-700">{file.name}</span>
          <button onClick={() => onFileSelect(null)} className="text-slate-400 hover:text-red-500">
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <>
          <p className="text-xs text-slate-400 mb-2">Arrastra o selecciona</p>
          <label className="cursor-pointer">
            <input
              type="file"
              accept={accept}
              className="hidden"
              onChange={(e) => e.target.files[0] && onFileSelect(e.target.files[0])}
            />
            <Button variant="outline" size="sm" className="pointer-events-none">
              <Upload className="w-3 h-3 mr-1" /> Seleccionar
            </Button>
          </label>
        </>
      )}
    </div>
  );
};

// ============================================================
// STUDENT 360 PROFILE COMPONENT (Slide-over Panel)
// Clean, professional SaaS design
// ============================================================

const Student360Profile = ({ student, open, onOpenChange, partnerSlug = null }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (open && student) {
      loadProfile();
    }
  }, [open, student]);
  
  const loadProfile = async () => {
    setLoading(true);
    try {
      const data = await api.getStudentProfile360(student.id);
      setProfile(data);
    } catch (err) {
      console.error(err);
      toast.error("Error cargando perfil del alumno");
    }
    setLoading(false);
  };
  
  // SISTEMA PENTACOLOR - Badge classes for 5 risk levels
  const getRiskBadgeClass = (color) => {
    switch (color) {
      case "black": return "bg-gray-800 text-white border border-gray-900";
      case "red": return "bg-red-100 text-red-700 border border-red-300";
      case "orange": return "bg-orange-100 text-orange-700 border border-orange-300";
      case "blue": return "bg-blue-100 text-blue-700 border border-blue-300";
      case "green": return "bg-emerald-100 text-emerald-700 border border-emerald-300";
      case "yellow": return "bg-amber-100 text-amber-700 border border-amber-300"; // Legacy
      default: return "bg-gray-100 text-gray-600 border border-gray-300";
    }
  };
  
  // SISTEMA PENTACOLOR - Background for CourseCards
  const getCourseCardBgClass = (color) => {
    switch (color) {
      case "black": return "bg-gray-900 border-gray-700";
      case "red": return "bg-red-50 border-red-200";
      case "orange": return "bg-orange-50 border-orange-200";
      case "blue": return "bg-blue-50 border-blue-200";
      case "green": return "bg-emerald-50 border-emerald-200";
      default: return "bg-gray-50 border-gray-200";
    }
  };
  
  // Text color for dark backgrounds
  const getCourseCardTextClass = (color) => {
    return color === "black" ? "text-white" : "text-gray-900";
  };
  
  // SISTEMA DE SEMÁFORO DE 8 ESTADOS - Labels (unificado para todos los partners)
  const getRiskLabel = (color, partnerSlug = null) => {
    const labels = {
      green: "Activo con hitos",
      blue: "Acompañar",
      purple: "Finalizado",
      yellow: "Activo sin hitos",
      orange: "Sin actividad",
      red: "En riesgo",
      dark_gray: "Inactivo",
      black: "No iniciado"
    };
    return labels[color] || "Sin datos";
  };
  
  // SISTEMA DE SEMÁFORO DE 8 ESTADOS - Emoji
  const getRiskEmoji = (color) => {
    const emojis = { 
      green: "🟢", blue: "🔵", purple: "🟣", orange: "🟠", 
      yellow: "🟡", red: "🔴", dark_gray: "⚫", black: "⬛" 
    };
    return emojis[color] || "⚪";
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return "Sin registro";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString("es-ES", { 
        day: "2-digit", 
        month: "short", 
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "UTC"  // Siempre mostrar en UTC
      });
    } catch {
      return dateStr;
    }
  };

  const getScoreColor = (score) => {
    if (score === null || score === undefined) return "text-gray-500";
    if (score >= 60) return "text-emerald-600";
    if (score >= 30) return "text-amber-600";
    return "text-red-600";
  };
  
  // Días inactivos con formato y color
  const getDaysInactiveDisplay = (days) => {
    if (days === null || days === undefined) return { text: "Sin registro", color: "text-gray-500" };
    if (days > 30) return { text: `${days} días`, color: "text-gray-800" };
    if (days >= 11) return { text: `${days} días`, color: "text-red-600" };
    return { text: `${days} días`, color: "text-emerald-600" };
  };
  
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        className="w-full sm:max-w-xl overflow-y-auto bg-white border-l border-gray-200" 
        side="right"
        data-testid="student-360-sheet"
      >
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : profile ? (
          <div className="space-y-6 py-2">
            
            {/* ===== CABECERA GLOBAL DEL ALUMNO ===== */}
            <SheetHeader className="pb-4 border-b border-gray-200">
              {/* Nombre y Badge de Riesgo Máximo */}
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex-1 min-w-0">
                  <SheetTitle className="text-xl font-bold text-gray-900" data-testid="student-name">
                    {profile.student.name || "Sin nombre"}
                  </SheetTitle>
                  <SheetDescription className="flex items-center gap-2 text-gray-600 text-sm mt-1">
                    <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span className="truncate">{profile.student.email || "Sin email"}</span>
                  </SheetDescription>
                </div>
                
                {/* Badge de Riesgo Máximo */}
                <div className="flex flex-col items-end gap-1">
                  <Badge 
                    className={`text-sm font-semibold px-4 py-2 rounded-xl ${getRiskBadgeClass(profile.risk?.global_risk_color)}`}
                    data-testid="global-risk-badge"
                  >
                    {getRiskEmoji(profile.risk?.global_risk_color)} {getRiskLabel(profile.risk?.global_risk_color, partnerSlug)}
                  </Badge>
                  <span className="text-xs text-gray-400">Riesgo Máximo</span>
                </div>
              </div>
              
              {/* Datos Globales: IP, Último Acceso, Días Inactivo */}
              <div className="grid grid-cols-3 gap-3 bg-slate-50 rounded-xl p-4 border border-slate-200">
                {/* Último Acceso */}
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1.5 mb-1">
                    <Clock className="w-4 h-4 text-slate-500" />
                  </div>
                  <p className="text-xs text-slate-500 mb-0.5">Último acceso</p>
                  <p className="text-sm font-medium text-slate-800" data-testid="last-access">
                    {profile.student.last_access_at 
                      ? new Date(profile.student.last_access_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short", timeZone: "UTC" })
                      : "Sin registro"}
                  </p>
                </div>
                
                {/* Días Inactivo */}
                <div className="text-center border-x border-slate-200">
                  <div className="flex items-center justify-center gap-1.5 mb-1">
                    <AlertTriangle className="w-4 h-4 text-slate-500" />
                  </div>
                  <p className="text-xs text-slate-500 mb-0.5">Días inactivo</p>
                  <p className={`text-sm font-bold ${getDaysInactiveDisplay(profile.student.days_inactive).color}`} data-testid="days-inactive">
                    {getDaysInactiveDisplay(profile.student.days_inactive).text}
                  </p>
                </div>
                
                {/* Última IP */}
                <div className="text-center">
                  <div className="flex items-center justify-center gap-1.5 mb-1">
                    <Monitor className="w-4 h-4 text-slate-500" />
                  </div>
                  <p className="text-xs text-slate-500 mb-0.5">Última IP</p>
                  <p className="text-sm font-mono text-slate-700" data-testid="last-ip">
                    {profile.student.last_ip || "N/A"}
                  </p>
                </div>
              </div>
              
              {/* Alerta de escalamiento por NPS */}
              {profile.risk?.nps_risk_impact === "escalated" && (
                <div className="mt-3 flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                  <span className="text-sm text-red-700">Riesgo escalado por NPS Detractor</span>
                </div>
              )}
            </SheetHeader>
            
            {/* ===== KPIs RESUMEN ===== */}
            <div>
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3">
                Resumen Global
              </h3>
              <div className="grid grid-cols-3 gap-3">
                {/* Cursos */}
                <div className="bg-gray-50 rounded-xl p-3 text-center border border-gray-100">
                  <BookOpen className="w-5 h-5 mx-auto text-blue-500 mb-1" />
                  <p className="text-2xl font-bold text-gray-900">{profile.kpis?.total_courses || 0}</p>
                  <p className="text-xs text-gray-500">Cursos</p>
                </div>
                {/* Promedio */}
                <div className="bg-gray-50 rounded-xl p-3 text-center border border-gray-100">
                  <Award className="w-5 h-5 mx-auto text-emerald-500 mb-1" />
                  <p className={`text-2xl font-bold ${getScoreColor(profile.kpis?.average_score)}`}>
                    {profile.kpis?.average_score ?? "N/A"}{profile.kpis?.average_score !== null ? "%" : ""}
                  </p>
                  <p className="text-xs text-gray-500">Promedio</p>
                </div>
                {/* Entregas Faltantes */}
                <div className="bg-gray-50 rounded-xl p-3 text-center border border-gray-100">
                  <FileX className={`w-5 h-5 mx-auto mb-1 ${
                    (profile.kpis?.total_missing_submissions || 0) > 0 ? "text-red-500" : "text-gray-400"
                  }`} />
                  <p className={`text-2xl font-bold ${
                    (profile.kpis?.total_missing_submissions || 0) > 0 ? "text-red-600" : "text-gray-900"
                  }`}>
                    {profile.kpis?.total_missing_submissions || 0}
                  </p>
                  <p className="text-xs text-gray-500">Sin Entregar</p>
                </div>
              </div>
            </div>
            
            {/* ===== ENGAGEMENT: VISUALIZACIONES Y PARTICIPACIONES ===== */}
            {(profile.engagement?.total_views > 0 || profile.engagement?.total_participations > 0) && (
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-4 border border-purple-100">
                <h3 className="text-xs font-medium text-purple-600 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  Engagement Total
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  {/* Visualizaciones */}
                  <div className="bg-white/70 rounded-lg p-3 text-center">
                    <Eye className="w-5 h-5 mx-auto text-purple-500 mb-1" />
                    <p className="text-2xl font-bold text-purple-700" data-testid="total-views">
                      {(profile.engagement?.total_views || 0).toLocaleString('es-AR')}
                    </p>
                    <p className="text-xs text-purple-500">Visualizaciones</p>
                  </div>
                  {/* Participaciones */}
                  <div className="bg-white/70 rounded-lg p-3 text-center">
                    <Activity className="w-5 h-5 mx-auto text-indigo-500 mb-1" />
                    <p className="text-2xl font-bold text-indigo-700" data-testid="total-participations">
                      {(profile.engagement?.total_participations || 0).toLocaleString('es-AR')}
                    </p>
                    <p className="text-xs text-indigo-500">Participaciones</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* ===== FEEDBACK NPS ===== */}
            {profile.nps && (
              <div className={`rounded-xl p-4 border ${
                profile.nps.nps_group === "Detractor" 
                  ? "bg-red-50 border-red-200" 
                  : profile.nps.nps_group === "Passive"
                    ? "bg-amber-50 border-amber-200"
                    : "bg-emerald-50 border-emerald-200"
              }`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold ${
                    profile.nps.nps_group === "Detractor"
                      ? "bg-red-100 text-red-700"
                      : profile.nps.nps_group === "Passive"
                        ? "bg-amber-100 text-amber-700"
                        : "bg-emerald-100 text-emerald-700"
                  }`}>
                    {profile.nps.score}
                  </div>
                  <div>
                    <Badge className={`text-xs font-medium px-2 py-1 rounded-full ${
                      profile.nps.nps_group === "Detractor"
                        ? "bg-red-200 text-red-800"
                        : profile.nps.nps_group === "Passive"
                          ? "bg-amber-200 text-amber-800"
                          : "bg-emerald-200 text-emerald-800"
                    }`}>
                      {profile.nps.nps_group === "Detractor" ? "Detractor" 
                        : profile.nps.nps_group === "Passive" ? "Pasivo" 
                        : "Promotor"}
                    </Badge>
                    <p className="text-xs text-gray-500 mt-1">NPS</p>
                  </div>
                </div>
                {profile.nps.comment && (
                  <p className="text-sm text-gray-700 italic bg-white/60 rounded-lg p-3">
                    "{profile.nps.comment}"
                  </p>
                )}
              </div>
            )}
            
            {/* ===== SECCIÓN 1: CURSOS ACTIVOS (PARA ACCIÓN) ===== */}
            <div>
              <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                <Activity className="w-4 h-4" />
                Cursos Activos ({profile.courses?.length || 0})
                <span className="text-gray-400 font-normal normal-case">— requieren intervención</span>
              </h3>
              
              <div className="space-y-4" data-testid="active-courses-container">
                {profile.courses && profile.courses.length > 0 ? (
                  profile.courses.map((course, idx) => (
                    <div 
                      key={idx} 
                      className={`rounded-xl p-4 border-2 transition-all ${getCourseCardBgClass(course.risk_color)}`}
                      data-testid={`course-card-${idx}`}
                    >
                      {/* Course Header con Semáforo Individual */}
                      <div className="flex items-start justify-between gap-3 mb-4">
                        <div className="flex-1 min-w-0">
                          <h4 className={`font-semibold text-sm leading-tight ${getCourseCardTextClass(course.risk_color)}`}>
                            {course.course_name}
                          </h4>
                          {course.section_name && (
                            <p className={`text-xs mt-1 ${course.risk_color === "black" ? "text-gray-400" : "text-gray-500"}`}>
                              Sección: {course.section_name}
                            </p>
                          )}
                        </div>
                        
                        {/* Semáforo Individual del Curso */}
                        <Badge 
                          className={`text-xs font-medium px-3 py-1.5 rounded-lg flex-shrink-0 ${getRiskBadgeClass(course.risk_color)}`}
                          data-testid={`course-risk-badge-${idx}`}
                        >
                          {getRiskEmoji(course.risk_color)} {getRiskLabel(course.risk_color, partnerSlug)}
                        </Badge>
                      </div>
                      
                      {/* Métricas del Curso en Grid */}
                      <div className={`grid gap-3 ${(course.times_viewed > 0 || course.times_participated > 0) ? 'grid-cols-4' : 'grid-cols-2'}`}>
                        {/* Calificación */}
                        <div className={`flex items-center gap-2 p-2 rounded-lg ${course.risk_color === "black" ? "bg-gray-800" : "bg-white/70"}`}>
                          <Award className={`w-4 h-4 flex-shrink-0 ${getScoreColor(course.current_score)}`} />
                          <div>
                            <p className={`text-xs ${course.risk_color === "black" ? "text-gray-400" : "text-gray-500"}`}>Nota</p>
                            <p className={`font-bold ${getScoreColor(course.current_score)}`}>
                              {course.current_score !== null ? `${course.current_score}%` : "N/A"}
                            </p>
                          </div>
                        </div>
                        
                        {/* Entregas Faltantes */}
                        <div className={`flex items-center gap-2 p-2 rounded-lg ${course.risk_color === "black" ? "bg-gray-800" : "bg-white/70"}`}>
                          <FileX className={`w-4 h-4 flex-shrink-0 ${
                            (course.missing_submissions || 0) > 0 ? "text-red-500" : course.risk_color === "black" ? "text-gray-400" : "text-gray-400"
                          }`} />
                          <div>
                            <p className={`text-xs ${course.risk_color === "black" ? "text-gray-400" : "text-gray-500"}`}>Faltantes</p>
                            <p className={`font-bold ${
                              (course.missing_submissions || 0) > 0 ? "text-red-600" : course.risk_color === "black" ? "text-gray-300" : "text-gray-900"
                            }`}>
                              {course.missing_submissions || 0}
                            </p>
                          </div>
                        </div>
                        
                        {/* Visualizaciones - Solo si hay datos */}
                        {(course.times_viewed > 0 || course.times_participated > 0) && (
                          <>
                            <div className={`flex items-center gap-2 p-2 rounded-lg ${course.risk_color === "black" ? "bg-gray-800" : "bg-purple-50"}`}>
                              <Eye className={`w-4 h-4 flex-shrink-0 ${course.risk_color === "black" ? "text-purple-400" : "text-purple-500"}`} />
                              <div>
                                <p className={`text-xs ${course.risk_color === "black" ? "text-gray-400" : "text-purple-500"}`}>Vistas</p>
                                <p className={`font-bold ${course.risk_color === "black" ? "text-purple-300" : "text-purple-700"}`}>
                                  {(course.times_viewed || 0).toLocaleString('es-AR')}
                                </p>
                              </div>
                            </div>
                            
                            {/* Participaciones */}
                            <div className={`flex items-center gap-2 p-2 rounded-lg ${course.risk_color === "black" ? "bg-gray-800" : "bg-indigo-50"}`}>
                              <Activity className={`w-4 h-4 flex-shrink-0 ${course.risk_color === "black" ? "text-indigo-400" : "text-indigo-500"}`} />
                              <div>
                                <p className={`text-xs ${course.risk_color === "black" ? "text-gray-400" : "text-indigo-500"}`}>Particip.</p>
                                <p className={`font-bold ${course.risk_color === "black" ? "text-indigo-300" : "text-indigo-700"}`}>
                                  {(course.times_participated || 0).toLocaleString('es-AR')}
                                </p>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 bg-gray-50 rounded-xl border border-gray-200">
                    <CheckCircle className="w-10 h-10 mx-auto text-emerald-400 mb-2" />
                    <p className="text-gray-500 font-medium">Sin cursos activos</p>
                    <p className="text-xs text-gray-400 mt-1">Este alumno no tiene cursos que requieran intervención.</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* ===== SECCIÓN 2: HISTORIAL (CONTEXTO ESTADÍSTICO) ===== */}
            {profile.concluded_courses && profile.concluded_courses.length > 0 && (
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Historial ({profile.concluded_courses.length} cursos concluidos)
                </h3>
                
                {/* Lista compacta de cursos concluidos */}
                <div className="space-y-2 mb-4" data-testid="concluded-courses-container">
                  {profile.concluded_courses.map((course, idx) => {
                    const finalScore = course.final_score ?? course.current_score;
                    // CORRECCIÓN: final_score = 0 o NULL significa "Sin nota", no "Desaprobado"
                    const hasScore = finalScore !== null && finalScore !== undefined && finalScore > 0;
                    const isApproved = hasScore && finalScore >= 60;
                    
                    return (
                      <div 
                        key={idx}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100"
                        data-testid={`concluded-course-${idx}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">
                            {course.course_name}
                          </p>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          {/* Nota Final */}
                          <span className={`text-sm font-bold ${
                            hasScore 
                              ? (isApproved ? "text-emerald-600" : "text-red-600")
                              : "text-gray-400"
                          }`}>
                            {hasScore ? `${finalScore}%` : "—"}
                          </span>
                          {/* Estado */}
                          <Badge className={`text-xs px-2 py-0.5 ${
                            hasScore
                              ? (isApproved 
                                  ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
                                  : "bg-red-100 text-red-700 border border-red-200")
                              : "bg-gray-100 text-gray-500 border border-gray-200"
                          }`}>
                            {hasScore ? (isApproved ? "Aprobado" : "Desaprobado") : "Sin nota"}
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {/* KPIs de Persistencia */}
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-100">
                  <h4 className="text-xs font-medium text-indigo-600 uppercase tracking-wider mb-3">
                    Indicadores de Persistencia
                  </h4>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    {/* Tasa de Finalización */}
                    <div>
                      <p className="text-2xl font-bold text-indigo-700">
                        {profile.analytics?.completion_rate !== null 
                          ? `${profile.analytics.completion_rate}%`
                          : "—"}
                      </p>
                      <p className="text-xs text-indigo-500">Finalización</p>
                      <p className="text-xs text-gray-400">
                        {profile.analytics?.total_concluded || 0} / {profile.analytics?.total_all_courses || 0}
                      </p>
                    </div>
                    {/* Tasa de Aprobación */}
                    <div className="border-x border-indigo-200">
                      <p className="text-2xl font-bold text-purple-700">
                        {profile.analytics?.approval_rate !== null 
                          ? `${profile.analytics.approval_rate}%`
                          : "—"}
                      </p>
                      <p className="text-xs text-purple-500">Aprobación</p>
                      <p className="text-xs text-gray-400">sobre finalizados</p>
                    </div>
                    {/* Promedio General */}
                    <div>
                      <p className="text-2xl font-bold text-emerald-700">
                        {profile.analytics?.average_final_score !== null 
                          ? `${profile.analytics.average_final_score}%`
                          : "—"}
                      </p>
                      <p className="text-xs text-emerald-500">Promedio</p>
                      <p className="text-xs text-gray-400">con nota</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-400">
            Error cargando perfil
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

// Create Partner Dialog
const CreatePartnerDialog = ({ open, onOpenChange, onCreated }) => {
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  
  const handleCreate = async () => {
    if (!name.trim()) return;
    setLoading(true);
    try {
      const partner = await api.createPartner(name.trim());
      toast.success(`Partner "${partner.name}" creado`);
      onCreated(partner);
      setName("");
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message);
    }
    setLoading(false);
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Nuevo Partner</DialogTitle>
          <DialogDescription>Ingresa el nombre del nuevo partner/cliente</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <Input
            placeholder="Nombre del partner (ej: Universidad Siglo 21)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCreate()}
          />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button 
              onClick={handleCreate} 
              disabled={!name.trim() || loading}
              style={{ backgroundColor: CAPABILIA.green }}
              className="hover:opacity-90"
            >
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Crear Partner
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================
// MAIN APP
// ============================================================

function App({ authUser = null, onLogout = null }) {
  // Auth-related state
  const canUploadData = authUser?.permissions?.can_upload_data || false;
  const canManageUsers = authUser?.permissions?.can_manage_users || false;
  const canViewAllPartners = authUser?.permissions?.can_view_all_partners !== false;
  const isReadOnly = authUser?.permissions?.is_readonly || false;
  const userPartnerIdOnly = authUser?.role === 'partner' ? authUser?.partner_id : null;
  
  // Permiso para Dashboard Global: solo superadmin y gestor_academico
  const canViewGlobalDashboard = authUser?.role === 'superadmin' || authUser?.role === 'gestor_academico';
  
  // Estado para Dashboard Global
  const [showGlobalDashboard, setShowGlobalDashboard] = useState(false);
  const [globalDashboardData, setGlobalDashboardData] = useState(null);
  const [loadingGlobalDashboard, setLoadingGlobalDashboard] = useState(false);
  
  // Mapeo de certificados CCF para filtro en Módulos
  const CERTIFICADOS_CCF = {
    "Soporte y Atención al Cliente": [10634, 10635, 10636, 10637, 10676],
    "Marketing y Publicidad Digital": [10638, 10639, 10640, 10641, 10677],
    "Marketing Digital e Inteligencia Artificial": [10647, 10648, 10649, 10650, 10679],
    "Negocios Digitales y Comercio Electrónico": [10651, 10652, 10653, 10654, 10680],
    "Operador en Ciberseguridad": [10655, 10656, 10657, 10658, 10681],
    "Operador en Inteligencia Artificial": [10659, 10660, 10661, 10662, 10682],
    "Seguridad de la Información y Redes": [10663, 10664, 10686, 10665, 10683],
    "Aplicaciones Avanzadas de IA en Entornos Productivos": [10666, 10667, 10687, 10688, 10684],
    "Coding Inicial para Proyectos y Negocios": [10723, 10728, 10729, 10646, 10731],
    "Coding Avanzado para Proyectos y Negocios": [10732, 10733, 10734, 10735, 10736],
    "Cursos Sello": [10672, 10673]
  };
  
  // State
  const [partners, setPartners] = useState([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [modulosCertFilter, setModulosCertFilter] = useState("all");  // Filtro por certificado en Módulos (CCF)
  
  // Dashboard data
  const [dashboard, setDashboard] = useState(null);
  const [semaforoView, setSemaforoView] = useState("mes"); // "mes" o "todos"
  const [cursosView, setCursosView] = useState("todos"); // "mes" o "todos" para Cursos/Secciones
  const [cursosSectionFilter, setCursosSectionFilter] = useState("all"); // Filtro por sección en Cursos
  const [certificates, setCertificates] = useState(null);  // Nuevo: Reporte por Certificado
  const [certificatesLoading, setCertificatesLoading] = useState(false);
  const [programsCCF, setProgramsCCF] = useState(null);  // Certificados por programa (solo CCF)
  const [programsCCFLoading, setProgramsCCFLoading] = useState(false);
  const [expandedCerts, setExpandedCerts] = useState({});  // Control de filas expandidas en tabla de certificados
  const [certSectionFilter, setCertSectionFilter] = useState("all");  // Filtro de secciones por certificado (pestaña Secciones)
  const [coursesSortBy, setCoursesSortBy] = useState(null);  // Ordenamiento tabla Cursos: null, "activos", "terminados"
  const [coursesSortOrder, setCoursesSortOrder] = useState("desc");
  const [sectionsSortBy, setSectionsSortBy] = useState(null);  // Ordenamiento tabla Secciones: null, "activos", "terminados"
  const [sectionsSortOrder, setSectionsSortOrder] = useState("desc");
  // Paginación para Siglo-21 (Cursos y Secciones)
  const [coursesPage, setCoursesPage] = useState(1);
  const [sectionsPage, setSectionsPage] = useState(1);
  const SIGLO21_PAGE_SIZE = 20;
  const [evolution, setEvolution] = useState([]);
  const [students, setStudents] = useState([]);
  const [studentsTotal, setStudentsTotal] = useState(0);
  // FIX 2026-09-30: breakdown por color de la MISMA respuesta de /students (search/nps/
  // período/etapa ya aplicados) - los chips leen de acá, no de /dashboard (alcance de filtros distinto).
  const [studentsColorCounts, setStudentsColorCounts] = useState({});
  const [studentsPage, setStudentsPage] = useState(1);
  const [riskFilter, setRiskFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  // FIX 2026-10-26 (Punto 1, Cande/CTO): readOnly hasta el primer focus - frena el
  // autofill de Chrome al cargar la vista/tab (autoComplete="off" NO alcanza, Chrome
  // lo ignora a propósito para datos guardados tipo email). Se libera recién cuando
  // el usuario clickea/tabula al campo.
  const [searchFieldLocked, setSearchFieldLocked] = useState(true);
  const searchInputRef = useRef(null);
  // Failsafe: si Chrome autocompleta el campo saltándose el evento onChange de React
  // (puede pasar con su autofill de direcciones), lo limpiamos antes de que el
  // usuario llegue a verlo. Se re-chequea en cada cambio de partner (vista "nueva").
  useEffect(() => {
    setSearchFieldLocked(true);
    const t = setTimeout(() => {
      if (searchInputRef.current && searchTerm === "" && searchInputRef.current.value !== "") {
        searchInputRef.current.value = "";
      }
    }, 400);
    return () => clearTimeout(t);
  }, [selectedPartnerId]);
  const [npsFilter, setNpsFilter] = useState("all");  // Filtro NPS
  const [periodFilter, setPeriodFilter] = useState("all");  // Filtro Período
  const [availablePeriods, setAvailablePeriods] = useState([]);  // Períodos disponibles
  const [sortBy, setSortBy] = useState(null);  // Campo de ordenamiento (null, "nps")
  const [sortOrder, setSortOrder] = useState("asc");  // Orden (asc, desc)
  const [detractorsCount, setDetractorsCount] = useState(0);  // Contador de detractores para header
  
  // NPS Batches state
  const [npsBatches, setNpsBatches] = useState([]);
  // NPS Stats removido - usar NPS 360 (Qualtrics) como única fuente
  
  // NPS Overview state
  const [npsOverview, setNpsOverview] = useState(null);
  const [loadingNpsOverview, setLoadingNpsOverview] = useState(false);
  const [npsEvolution, setNpsEvolution] = useState([]);  // Datos de evolución
  const [npsWithoutSection, setNpsWithoutSection] = useState(0);  // Respuestas sin sección (excluidas del gráfico)
  const [selectedNpsInstance, setSelectedNpsInstance] = useState(null);
  const [npsInstanceDetails, setNpsInstanceDetails] = useState(null);
  const [loadingInstanceDetails, setLoadingInstanceDetails] = useState(false);
  
  // NPS Panel state (nuevo panel inclusivo)
  const [npsPanel, setNpsPanel] = useState(null);
  const [loadingNpsPanel, setLoadingNpsPanel] = useState(false);
  const [npsPanelInstance, setNpsPanelInstance] = useState("all");
  const [npsPanelFilter, setNpsPanelFilter] = useState("all");
  const [npsPanelSearch, setNpsPanelSearch] = useState("");
  const [npsPanelPage, setNpsPanelPage] = useState(1);
  const [npsAllInstances, setNpsAllInstances] = useState([]);  // Lista fija de todas las instancias
  
  // Program filter state
  const [programs, setPrograms] = useState([]);
  const [programFilter, setProgramFilter] = useState("all");
  const [programStats, setProgramStats] = useState([]);
  
  // KPI History (Histórico)
  const [kpiHistory, setKpiHistory] = useState(null);
  const [kpiHistoryLoading, setKpiHistoryLoading] = useState(false);
  
  // PDF Preview state (temporal para mockup)
  const [showPDFPreview, setShowPDFPreview] = useState(false);
  
  // RE-ARQUITECTURA: Nuevos filtros de integridad
  const [sections, setSections] = useState([]);
  // Default: "current_only" para mostrar solo cohortes ya iniciadas (fecha_inicio ≤ hoy)
  const [sectionFilter, setSectionFilter] = useState("current_only");
  const [courses, setCourses] = useState([]);
  const [courseFilter, setCourseFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  
  // Filtro de período para Activos
  const [activePeriod, setActivePeriod] = useState("90d");  // Default: 90 días
  const [customDateFrom, setCustomDateFrom] = useState("");
  const [customDateTo, setCustomDateTo] = useState("");
  
  // Filtro de período para Alumnos que Terminaron (egresados)
  const [egresadosPeriod, setEgresadosPeriod] = useState("all");  // Default: Todo el tiempo
  const [mesFinalizacion, setMesFinalizacion] = useState("all");  // Filtro por mes end_date (Areandina)
  
  // Filtro de período de inicio (solo CCF)
  const [periodoCCF, setPeriodoCCF] = useState("");  // "" = todos los períodos
  const [periodosCCF, setPeriodosCCF] = useState([]);  // Lista de períodos disponibles

  // Filtro Etapa 1/Etapa 2 (solo CCF, 2026-09-18)
  const [etapaCCF, setEtapaCCF] = useState("todas");  // "todas" | "etapa_1" | "etapa_2"
  
  // Upload state
  const [uploadFiles, setUploadFiles] = useState({
    sis: null,        // SIS.zip (BASE MADRE - obligatorio)
    lastAccess: null, // Ultimo Acceso.csv (GLOBAL)
    grades: null,     // Calificaciones.csv (POR CURSO)
    submissions: null, // Sin Entregas.csv (POR CURSO)
    engagement: null,  // 5TO ARCHIVO: user_course_access_log.csv (Visualizaciones y Participaciones) - OPCIONAL
    provisioning: null, // 6TO ARCHIVO: Provisioning.zip con sections.csv (start_date para cohortes) - OPCIONAL
    baseEnrolados: null, // 7MO ARCHIVO: Base de Enrolados (Email, Mes de inicio, Programa) - OPCIONAL
    geolocalizacion: null // 8VO ARCHIVO: Geolocalización (Correo, Localidad) - SOLO CCF
  });
  const [referenceDate, setReferenceDate] = useState(new Date().toISOString().split('T')[0]); // Fecha de referencia para procesamiento
  const [showUploadHelp, setShowUploadHelp] = useState(true);  // Card de ayuda de exportación Canvas
  const [npsFile, setNpsFile] = useState(null);
  const [processingNps, setProcessingNps] = useState(false);
  const [npsUploadResult, setNpsUploadResult] = useState(null);  // Modal de resumen NPS
  const [processingGeo, setProcessingGeo] = useState(false);  // Estado para upload geolocalización
  const [geoUploadResult, setGeoUploadResult] = useState(null);  // Resultado upload geo
  const [geoData, setGeoData] = useState(null);  // Datos de geografía (semáforo por localidad)
  const [loadingGeo, setLoadingGeo] = useState(false);  // Cargando datos de geografía
  const [geoSortColumn, setGeoSortColumn] = useState('total');  // Columna de ordenamiento Geografía
  const [geoSortDir, setGeoSortDir] = useState('desc');  // Dirección de ordenamiento Geografía
  const [actividadBecas, setActividadBecas] = useState(null);  // Actividad por beca (solo CCF)
  const [processing, setProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [processingProgress, setProcessingProgress] = useState(0);
  const [activeBatchId, setActiveBatchId] = useState(null);
  const [lastResult, setLastResult] = useState(null);  // Guardar resultado del último procesamiento
  
  // Ingestion batches state (for rollback)
  const [batches, setBatches] = useState([]);
  
  // HubSpot Tickets state (Centro de Atención - solo CCF)
  const [ticketsFile, setTicketsFile] = useState(null);
  const [processingTickets, setProcessingTickets] = useState(false);
  const [ticketsData, setTicketsData] = useState(null);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [ticketsFilter, setTicketsFilter] = useState('all');  // 'all', 'consultas', 'campanas'
  const [ticketsPage, setTicketsPage] = useState(1);  // Paginación tabla alumnos
  const TICKETS_PAGE_SIZE = 15;
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [deletingBatchId, setDeletingBatchId] = useState(null);
  
  // Canvas Sync state (Sincronización automática con Canvas LMS)
  const [canvasSyncing, setCanvasSyncing] = useState(false);
  const [canvasSyncResult, setCanvasSyncResult] = useState(null);
  const [canvasSyncError, setCanvasSyncError] = useState(null);
  const [canvasSyncStatus, setCanvasSyncStatus] = useState(null); // Estado del polling
  const [canvasSyncPolling, setCanvasSyncPolling] = useState(false);
  
  // Qualtrics NPS Sync state
  const [qualtricsSyncing, setQualtricsSyncing] = useState(false);
  const [qualtricsSyncResult, setQualtricsSyncResult] = useState(null);
  const [qualtricsSyncError, setQualtricsSyncError] = useState(null);
  const [qualtricsSyncStatus, setQualtricsSyncStatus] = useState(null);
  
  // HubSpot Tickets Sync state
  const [hubspotSyncing, setHubspotSyncing] = useState(false);
  const [hubspotSyncResult, setHubspotSyncResult] = useState(null);
  const [hubspotSyncError, setHubspotSyncError] = useState(null);
  const [hubspotSyncStatus, setHubspotSyncStatus] = useState(null);
  
  // Backup de seguridad state
  const [backupStatus, setBackupStatus] = useState(null);
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupDownloading, setBackupDownloading] = useState(false);
  
  // Base de Enrolados update state
  const [baseEnroladosSyncing, setBaseEnroladosSyncing] = useState(false);
  const [baseEnroladosResult, setBaseEnroladosResult] = useState(null);
  const [baseEnroladosError, setBaseEnroladosError] = useState(null);
  const [baseEnroladosStatus, setBaseEnroladosStatus] = useState(null);
  
  // Master de Servicio state
  const [masterServicio, setMasterServicio] = useState([]);
  const [masterServicioLoading, setMasterServicioLoading] = useState(false);
  const [masterServicioEditing, setMasterServicioEditing] = useState(null);
  const [showMasterServicio, setShowMasterServicio] = useState(false);
  const [fcrRealData, setFcrRealData] = useState(null);
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // CONFIG: Objetivos de Atención (FRT/TTR/FCR/SLA)
  // ═══════════════════════════════════════════════════════════════════════════════
  const [configObjectives, setConfigObjectives] = useState(null);
  const [configObjectivesLoading, setConfigObjectivesLoading] = useState(false);
  const [configObjectivesEditing, setConfigObjectivesEditing] = useState(false);
  const [configObjectivesSaving, setConfigObjectivesSaving] = useState(false);
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // CONFIG: Forecast y Ticket Promedio
  // ═══════════════════════════════════════════════════════════════════════════════
  const [forecastConfig, setForecastConfig] = useState(null);
  const [realManualConfig, setRealManualConfig] = useState(null);
  const [forecastPlaceholders, setForecastPlaceholders] = useState(null);
  const [forecastTotals, setForecastTotals] = useState({ implementados: 0, realManual: 0, placeholders: 0, empresa: 0 });
  const [forecastConfigLoading, setForecastConfigLoading] = useState(false);
  const [ticketPromedioConfig, setTicketPromedioConfig] = useState(null);
  const [ticketPromedioLoading, setTicketPromedioLoading] = useState(false);
  const [editingForecastSlug, setEditingForecastSlug] = useState(null);
  const [editingForecastValues, setEditingForecastValues] = useState({ monthly: Array(12).fill(0), ticket: 0 });
  const [editingForecastIsPlaceholder, setEditingForecastIsPlaceholder] = useState(false);
  const [editingForecastIsRealManual, setEditingForecastIsRealManual] = useState(false);
  const [savingForecast, setSavingForecast] = useState(false);
  
  // Student 360 Profile state
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showProfile360, setShowProfile360] = useState(false);
  
  // Commercial metrics state (Nuevos Enrolados)
  const [commercialData, setCommercialData] = useState(null);
  const [commercialLoading, setCommercialLoading] = useState(false);
  const [commercialSinPeriodoSections, setCommercialSinPeriodoSections] = useState([]);
  const [commercialFilter, setCommercialFilter] = useState({
    month: new Date().getMonth() + 1,  // Mes actual (1-12)
    year: new Date().getFullYear()     // Año actual
  });
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // FUNCIONES CONFIG: Objetivos de Atención
  // ═══════════════════════════════════════════════════════════════════════════════
  const loadConfigObjectives = async () => {
    setConfigObjectivesLoading(true);
    try {
      const authToken = getAuthToken();
      const res = await fetch(`${API_URL}/api/atencion360/objectives`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      const data = await res.json();
      setConfigObjectives(data.objectives || {});
    } catch (err) {
      console.error('Error loading objectives:', err);
      toast.error('Error cargando objetivos');
    }
    setConfigObjectivesLoading(false);
  };
  
  const saveConfigObjectives = async (newValues) => {
    setConfigObjectivesSaving(true);
    try {
      const authToken = getAuthToken();
      const res = await fetch(`${API_URL}/api/atencion360/objectives`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newValues)
      });
      if (!res.ok) throw new Error('Error guardando objetivos');
      const data = await res.json();
      setConfigObjectives(data.objectives || newValues);
      setConfigObjectivesEditing(false);
      toast.success('Objetivos guardados correctamente');
    } catch (err) {
      console.error('Error saving objectives:', err);
      toast.error('Error guardando objetivos');
    }
    setConfigObjectivesSaving(false);
  };
  
  // ═══════════════════════════════════════════════════════════════════════════════
  // FUNCIONES CONFIG: Forecast y Ticket Promedio
  // ═══════════════════════════════════════════════════════════════════════════════
  const loadForecastConfig = async () => {
    setForecastConfigLoading(true);
    try {
      const authToken = getAuthToken();
      const res = await fetch(`${API_URL}/api/enrolados360/forecast/config?year=2026`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      const data = await res.json();
      setForecastConfig(data.forecasts || {});
      setRealManualConfig(data.real_manual || {});
      setForecastPlaceholders(data.placeholders || {});
      setForecastTotals({
        implementados: data.total_implementados_canvas || 0,
        realManual: data.total_real_manual || 0,
        placeholders: data.total_placeholders || 0,
        empresa: data.total_empresa || 0
      });
    } catch (err) {
      console.error('Error loading forecast config:', err);
      toast.error('Error cargando forecast');
    }
    setForecastConfigLoading(false);
  };
  
  const loadTicketPromedioConfig = async () => {
    setTicketPromedioLoading(true);
    try {
      const authToken = getAuthToken();
      const res = await fetch(`${API_URL}/api/enrolados360/ticket-promedio/config`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      const data = await res.json();
      setTicketPromedioConfig(data.tickets || {});
    } catch (err) {
      console.error('Error loading ticket promedio config:', err);
      toast.error('Error cargando ticket promedio');
    }
    setTicketPromedioLoading(false);
  };
  
  const openForecastEditor = (slug, forecastData, ticketUsd, isPlaceholder = false, isRealManual = false) => {
    setEditingForecastSlug(slug);
    setEditingForecastIsPlaceholder(isPlaceholder);
    setEditingForecastIsRealManual(isRealManual);
    setEditingForecastValues({
      monthly: forecastData?.monthly || forecastData?.forecast_monthly || Array(12).fill(0),
      real_monthly: forecastData?.real_monthly || Array(12).fill(0),
      ticket: ticketUsd || 0,
      name: forecastData?.name || slug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    });
  };
  
  const saveForecastAndTicket = async () => {
    if (!editingForecastSlug) return;
    setSavingForecast(true);
    try {
      const authToken = getAuthToken();
      
      // Determinar endpoint según tipo
      let endpoint;
      let params;
      
      if (editingForecastIsRealManual) {
        // Partner con real manual (Barcelona, Conmebol, Masterchef, Claeh)
        endpoint = `${API_URL}/api/enrolados360/real-manual/${editingForecastSlug}`;
        const realParams = editingForecastValues.real_monthly.map(v => `real_monthly=${v}`).join('&');
        const forecastParams = editingForecastValues.monthly.map(v => `forecast_monthly=${v}`).join('&');
        params = `${realParams}&${forecastParams}&name=${encodeURIComponent(editingForecastValues.name)}&year=2026`;
      } else if (editingForecastIsPlaceholder) {
        // Placeholder (solo forecast)
        endpoint = `${API_URL}/api/enrolados360/forecast/placeholder/${editingForecastSlug}`;
        params = editingForecastValues.monthly.map(v => `monthly=${v}`).join('&') + 
                 `&name=${encodeURIComponent(editingForecastValues.name)}&year=2026`;
      } else {
        // Partner implementado Canvas/HubSpot
        endpoint = `${API_URL}/api/enrolados360/forecast/config/${editingForecastSlug}`;
        params = editingForecastValues.monthly.map(v => `monthly=${v}`).join('&') + '&year=2026';
      }
      
      // Guardar forecast/real
      const forecastRes = await fetch(`${endpoint}?${params}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (!forecastRes.ok) throw new Error('Error guardando forecast');
      
      // Solo guardar ticket promedio para partners implementados Canvas (no placeholders ni real_manual)
      if (!editingForecastIsPlaceholder && !editingForecastIsRealManual) {
        const ticketRes = await fetch(`${API_URL}/api/enrolados360/ticket-promedio/config/${editingForecastSlug}?ticket_usd=${editingForecastValues.ticket}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        if (!ticketRes.ok) throw new Error('Error guardando ticket promedio');
      }
      
      // Recargar configs
      await loadForecastConfig();
      await loadTicketPromedioConfig();
      
      setEditingForecastSlug(null);
      toast.success('Configuración guardada correctamente');
    } catch (err) {
      console.error('Error saving forecast/ticket:', err);
      toast.error('Error guardando configuración');
    }
    setSavingForecast(false);
  };
  
  // Load partners
  useEffect(() => {
    loadPartners();
  }, []);
  
  const loadPartners = async () => {
    try {
      const data = await api.getPartners();
      setPartners(data.partners);
      if (data.partners.length > 0 && !selectedPartnerId) {
        setSelectedPartnerId(data.partners[0].id);
      }
    } catch (err) {
      toast.error("Error cargando partners");
    }
    setLoading(false);
  };
  
  // Load commercial data (Nuevos Enrolados)
  const loadCommercialData = useCallback(async () => {
    if (!selectedPartnerId) return;
    
    setCommercialData(null);  // Limpiar datos anteriores antes de cargar
    setCommercialSinPeriodoSections([]);
    setCommercialLoading(true);
    try {
      const params = new URLSearchParams();
      if (commercialFilter.month && commercialFilter.year) {
        params.append('month', commercialFilter.month);
        params.append('year', commercialFilter.year);
      }
      
      const response = await fetch(
        `${API_URL}/api/partners/${selectedPartnerId}/commercial/enrollments?${params.toString()}`,
        { headers: getAuthHeaders() }
      );
      
      await handleResponse(response);
      if (response.ok) {
        const data = await response.json();
        setCommercialData(data);
        // ROLLBACK 2026-10-23 (URGENTE, CTO): server.py volvió a devolver los
        // campos alumno-único (ver comentario ahí) y ya no expone
        // historico_sin_periodo_por_seccion. Vuelve a traer /sections como
        // antes del fix NI-grano del 10/22 (Fix #5b, mismo dato ya existente).
        if (data?.historico_sin_periodo > 0) {
          try {
            const sectionsResp = await api.getSections(selectedPartnerId);
            const sinFecha = (sectionsResp.sections || [])
              .filter(s => !s.start_date)
              .sort((a, b) => b.student_count - a.student_count);
            setCommercialSinPeriodoSections(sinFecha);
          } catch (err) {
            console.error("Error cargando secciones sin período:", err);
          }
        }
      }
    } catch (err) {
      console.error("Error cargando datos comerciales:", err);
    }
    setCommercialLoading(false);
  }, [selectedPartnerId, commercialFilter]);
  
  // Load dashboard when partner changes
  // =========================================================
  // CARGA INICIAL: Solo lo esencial para el Dashboard
  // =========================================================
  useEffect(() => {
    if (selectedPartnerId) {
      // Areandina: resetear filtro comercial (usa filtro de 12 meses del backend)
      const partner = partners.find(p => p.id === selectedPartnerId);
      if (partner?.slug === 'areandina') {
        setCommercialFilter({});  // Sin filtro = backend aplica 12 meses
      }
      
      // Carga INMEDIATA: Solo lo que ve el usuario en Dashboard
      loadDashboard();
      loadCommercialData();
      loadBatches();
      // loadNpsStats removido - NPS solo en pestaña NPS 360
      
      // Cargar períodos si es CCF
      if (partner?.slug === 'cordoba-crea-futuro') {
        loadPeriodosCCF();
        loadGeoData();
        loadActividadBecas();
      } else {
        setPeriodosCCF([]);
        setPeriodoCCF("");
        setGeoData(null);
        setActividadBecas(null);
      }
      
      // Reset filters when partner changes
      setProgramFilter("all");
      setSectionFilter("all");
      setCourseFilter("all");
      setDateFrom("");
      setDateTo("");
      setCoursesPage(1);
      setSectionsPage(1);
      setPeriodoCCF("");  // Reset período CCF
      setEtapaCCF("todas");  // Reset etapa CCF
    }
  }, [selectedPartnerId]);
  
  // =========================================================
  // CARGA DIFERIDA: Por tab (lazy loading)
  // =========================================================
  
  // Explorador → loadStudents
  useEffect(() => {
    if (activeTab === "explorer" && selectedPartnerId) {
      loadStudents();
      loadPeriods();  // Cargar períodos disponibles
    }
  }, [activeTab, selectedPartnerId]);
  
  // Secciones → loadSections
  useEffect(() => {
    if (activeTab === "sections" && selectedPartnerId && sections.length === 0) {
      loadSections();
    }
  }, [activeTab, selectedPartnerId]);
  
  // Evolución → loadEvolution
  useEffect(() => {
    if (activeTab === "evolution" && selectedPartnerId && evolution.length === 0) {
      loadEvolution();
    }
  }, [activeTab, selectedPartnerId]);
  
  // KPI History → loadKpiHistory
  useEffect(() => {
    if (activeTab === "kpi-history" && selectedPartnerId) {
      setKpiHistoryLoading(true);
      const token = getAuthToken();
      fetch(`${API_URL}/api/partners/${selectedPartnerId}/kpi-history`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(r => r.json())
        .then(data => setKpiHistory(data))
        .catch(console.error)
        .finally(() => setKpiHistoryLoading(false));
    }
  }, [activeTab, selectedPartnerId]);
  
  // Cursos → loadPrograms, loadProgramStats
  useEffect(() => {
    if (activeTab === "programs" && selectedPartnerId && programs.length === 0) {
      loadPrograms();
      loadProgramStats();
    }
  }, [activeTab, selectedPartnerId]);
  
  // NPS Overview → loadNpsBatches
  useEffect(() => {
    if (activeTab === "nps-overview" && selectedPartnerId && npsBatches.length === 0) {
      loadNpsBatches();
    }
  }, [activeTab, selectedPartnerId]);
  
  // Reload commercial data when filter changes
  useEffect(() => {
    if (selectedPartnerId) {
      loadCommercialData();
    }
  }, [commercialFilter, loadCommercialData]);
  
  // Load backup status when entering config tab (superadmin only)
  useEffect(() => {
    if (activeTab === "config" && authUser?.role === "superadmin") {
      const fetchBackupStatus = async () => {
        try {
          const authToken = getAuthToken();
          const res = await fetch(`${API_URL}/api/admin/backup/status`, {
            headers: { 'Authorization': `Bearer ${authToken}` }
          });
          if (res.ok) {
            const data = await res.json();
            setBackupStatus(data);
          }
        } catch (err) {
          console.error('Error fetching backup status:', err);
        }
      };
      fetchBackupStatus();
    }
  }, [activeTab, authUser?.role]);
  
  // Load courses when section filter changes
  useEffect(() => {
    if (selectedPartnerId) {
      loadCourses();
    }
  }, [sectionFilter]);
  
  // Reload dashboard when any filter changes
  useEffect(() => {
    if (selectedPartnerId) {
      loadDashboard();
      // Students se recarga solo si estamos en el tab explorer
      if (activeTab === "explorer") {
        loadStudents();
      }
    }
  }, [programFilter, sectionFilter, courseFilter, dateFrom, dateTo, activePeriod, customDateFrom, customDateTo, egresadosPeriod, periodoCCF, etapaCCF]);
  
  // Reload students when filters change
  useEffect(() => {
    if (selectedPartnerId) {
      loadStudents();
    }
  }, [studentsPage, riskFilter, searchTerm, npsFilter, sortBy, sortOrder, periodFilter]);
  
  // Cargar NPS Overview/Panel cuando se activa la pestaña
  useEffect(() => {
    if (activeTab === "nps-overview") {
      // Cargar lista completa de instancias si está vacía
      if (npsAllInstances.length === 0) {
        loadNpsInstances();
      }
      loadNpsOverview();
      loadNpsPanel("all", "all", "", 1);
      loadNpsEvolutionData();  // Cargar datos de evolución NPS
    }
  }, [activeTab]);
  
  // Cargar Certificados cuando se activa la pestaña o cambia cursosView o cursosSectionFilter
  useEffect(() => {
    if ((activeTab === "certificates" || activeTab === "sections") && selectedPartnerId) {
      loadCertificates();
    }
  }, [activeTab, selectedPartnerId, cursosView, cursosSectionFilter]);
  
  // Recargar panel NPS cuando cambian los filtros
  useEffect(() => {
    if (activeTab === "nps-overview") {
      loadNpsPanel(npsPanelInstance, npsPanelFilter, npsPanelSearch, npsPanelPage);
    }
  }, [npsPanelInstance, npsPanelFilter, npsPanelPage]);
  
  // Cargar certificados por programa cuando se activa la tab (solo CCF)
  useEffect(() => {
    if (activeTab === "programs" && selectedPartnerId && partners.length > 0) {
      const partner = partners.find(p => p.id === selectedPartnerId);
      if (partner?.slug === 'cordoba-crea-futuro') {
        loadProgramsCCF();
      }
    }
  }, [activeTab, selectedPartnerId, partners]);
  
  // Cargar estado de Canvas sync al seleccionar partner (para mostrar última actualización)
  useEffect(() => {
    if (selectedPartnerId && authUser?.role === 'superadmin') {
      api.getCanvasSyncStatus(selectedPartnerId)
        .then(status => {
          if (status.last_successful_sync) {
            setCanvasSyncResult({
              message: 'Última sincronización exitosa',
              last_sync: status.last_successful_sync
            });
          } else {
            setCanvasSyncResult(null);
          }
          // Si hay un proceso en curso, reanudar polling
          if (status.status === 'processing') {
            setCanvasSyncing(true);
            setCanvasSyncStatus(status);
          }
        })
        .catch(() => {/* ignorar errores */});
    }
  }, [selectedPartnerId, authUser?.role]);
  
  const loadPrograms = async () => {
    try {
      const data = await api.getPrograms(selectedPartnerId);
      setPrograms(data.programs || []);
    } catch (err) {
      console.error(err);
      setPrograms([]);
    }
  };
  
  // Cargar períodos disponibles para CCF
  const loadPeriodosCCF = async () => {
    try {
      const res = await fetch(`${API_URL}/api/partners/${selectedPartnerId}/enrollment_periods`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setPeriodosCCF(data.periods || []);
      }
    } catch (err) {
      console.error('Error cargando períodos CCF:', err);
      setPeriodosCCF([]);
    }
  };
  
  // RE-ARQUITECTURA: Cargar secciones (con info de fecha de inicio)
  const loadSections = async () => {
    try {
      const data = await api.getSections(selectedPartnerId);
      // data.sections ahora es array de objetos {name, student_count, start_date, is_future, label}
      setSections(data.sections || []);
    } catch (err) {
      console.error(err);
      setSections([]);
    }
  };
  
  // RE-ARQUITECTURA: Cargar cursos (opcional por sección)
  const loadCourses = async () => {
    try {
      const data = await api.getCourses(selectedPartnerId, sectionFilter);
      setCourses(data.courses || []);
    } catch (err) {
      console.error(err);
      setCourses([]);
    }
  };
  
  const loadProgramStats = async () => {
    try {
      const data = await api.getProgramStats(selectedPartnerId);
      setProgramStats(data.programs || []);
    } catch (err) {
      console.error(err);
      setProgramStats([]);
    }
  };
  
  const loadBatches = async () => {
    setLoadingBatches(true);
    try {
      // Cargar batches originales
      const data = await api.getBatches(selectedPartnerId);
      
      // También cargar historial de snapshots
      const historyData = await api.getImportHistory(selectedPartnerId);
      
      // Combinar: usar snapshots como fuente principal si existen
      if (historyData.imports && historyData.imports.length > 0) {
        // Mapear snapshots al formato de batches pero con semáforo
        const snapshotBatches = historyData.imports.map(imp => ({
          id: imp.id,
          timestamp: imp.created_at,
          filename_grades: `Procesamiento ${imp.date}`,
          student_count: imp.total_students,
          snapshot_count: imp.active_enrollments + imp.concluded_enrollments,
          status: 'completed',
          distribution: imp.distribution,
          kpis: imp.kpis,
          files_processed: imp.files_processed,
          is_snapshot: true
        }));
        setBatches(snapshotBatches);
      } else {
        setBatches(data.batches || []);
      }
    } catch (err) {
      console.error("Error loading batches:", err);
      setBatches([]);
    }
    setLoadingBatches(false);
  };
  
  // Polling para verificar estado de procesamiento
  const startProcessingPolling = async (batchId) => {
    let completed = false;
    let attempts = 0;
    const maxAttempts = 240; // 12 minutos (polling cada 3 seg)
    
    while (!completed && attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 3000)); // 3 segundos
      attempts++;
      
      try {
        const statusRes = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/processing/${batchId}/status`,
          { headers: { 'Authorization': `Bearer ${getAuthToken()}` } }
        );
        const status = await statusRes.json();
        
        // Actualizar progreso
        setProcessingProgress(status.progress || 0);
        setProcessingMessage(status.message || 'Procesando...');
        
        if (status.status === "completed") {
          completed = true;
          setLastResult(status.result || {});
          setProcessingMessage('✓ Procesamiento completado');
          
          toast.success(
            `✅ Completado: ${status.result?.students_processed || status.result?.students || 0} alumnos`,
            { duration: 5000 }
          );
          
          // Reset y recargar datos después de 3 segundos
          setTimeout(() => {
            setProcessing(false);
            setProcessingMessage('');
            setProcessingProgress(0);
            setActiveBatchId(null);
          }, 3000);
          
          setUploadFiles({ sis: null, lastAccess: null, grades: null, submissions: null, engagement: null, provisioning: null, baseEnrolados: null, geolocalizacion: null });
          
          // Recargar datos (sin useCallback para evitar dependencias circulares)
          try {
            const batchesData = await api.getBatches(selectedPartnerId);
            setBatches(batchesData.batches || []);
            const dashData = await api.getDashboard(selectedPartnerId);
            setDashboard(dashData);
          } catch (e) { console.warn("Error reloading data:", e); }
          
        } else if (status.status === "failed" || status.status === "error") {
          completed = true;
          setProcessingMessage(`Error: ${status.message || 'Error desconocido'}`);
          toast.error(`Error: ${status.message}`, { duration: 8000 });
          
          setTimeout(() => {
            setProcessing(false);
            setProcessingMessage('');
            setProcessingProgress(0);
            setActiveBatchId(null);
          }, 3000);
        }
      } catch (pollError) {
        console.warn("Error checking status:", pollError);
      }
    }
    
    if (!completed) {
      setProcessingMessage('El procesamiento está tardando. Recarga en unos minutos.');
      toast.warning('El procesamiento está tardando más de lo esperado.', { duration: 10000 });
      setProcessing(false);
      setProcessingProgress(0);
      setActiveBatchId(null);
    }
  };
  
  // Verificar si hay procesamiento activo para este partner
  const checkActiveProcessing = async () => {
    if (!selectedPartnerId) return;
    
    try {
      const data = await api.getBatches(selectedPartnerId);
      const activeBatch = data.batches?.find(b => 
        b.status === 'processing' || b.status === 'pending' || b.status === 'queued'
      );
      
      if (activeBatch) {
        setActiveBatchId(activeBatch.id);
        setProcessing(true);
        setProcessingMessage('Procesamiento en curso detectado...');
        startProcessingPolling(activeBatch.id);
      }
    } catch (err) {
      console.warn("Error checking active processing:", err);
    }
  };
  
  // Verificar procesamiento activo al entrar a pestaña Carga
  useEffect(() => {
    if (activeTab === "carga" && selectedPartnerId && !processing) {
      checkActiveProcessing();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedPartnerId]);
  
  // Cargar batches NPS
  const loadNpsBatches = async () => {
    try {
      const data = await api.getNpsBatches();
      setNpsBatches(data.batches || []);
    } catch (err) {
      console.error("Error loading NPS batches:", err);
      setNpsBatches([]);
    }
  };
  
  // Cargar NPS Stats para el partner (sin fallback a global)
  // loadNpsStats removido - NPS centralizado en pestaña NPS 360 (Qualtrics)
  
  // Cargar NPS Overview (comparativo global o filtrado por partner)
  const loadNpsOverview = async (partnerId = npsPanelInstance) => {
    setLoadingNpsOverview(true);
    try {
      const data = await api.getNpsOverview(partnerId);
      setNpsOverview(data);
      
      // Guardar la lista completa de instancias solo la primera vez (cuando no hay filtro)
      // o si la lista está vacía
      if ((partnerId === "all" || !partnerId) && data?.by_instance?.length > 0) {
        setNpsAllInstances(data.by_instance);
      } else if (npsAllInstances.length === 0 && data?.by_instance?.length > 0) {
        // Si la lista está vacía, cargar las instancias del overview actual
        setNpsAllInstances(data.by_instance);
      }
    } catch (err) {
      console.error("Error loading NPS overview:", err);
      setNpsOverview(null);
    }
    setLoadingNpsOverview(false);
  };
  
  // Cargar lista completa de instancias NPS (una sola vez)
  const loadNpsInstances = async () => {
    try {
      // Cargar overview sin filtro para obtener todas las instancias
      const data = await api.getNpsOverview("all");
      if (data?.by_instance?.length > 0) {
        setNpsAllInstances(data.by_instance);
      }
    } catch (err) {
      console.error("Error loading NPS instances:", err);
    }
  };
  
  // Cargar evolución NPS
  const loadNpsEvolutionData = async (partnerId = npsPanelInstance) => {
    try {
      const evolutionData = await api.getNpsEvolution(partnerId, 12);
      setNpsEvolution(evolutionData.evolution || []);
      setNpsWithoutSection(evolutionData.records_without_section_month || 0);
    } catch (err) {
      console.error("Error loading NPS evolution:", err);
    }
  };
  
  // Recargar Overview NPS cuando cambia el filtro de partner
  useEffect(() => {
    if (activeTab === "nps-overview") {
      loadNpsOverview(npsPanelInstance);
      loadNpsEvolutionData(npsPanelInstance);
    }
  }, [npsPanelInstance]);
  
  // Cargar datos de tickets cuando se activa la tab Atención
  useEffect(() => {
    if (activeTab === "atencion" && selectedPartnerId) {
      loadTicketsData();
    }
  }, [activeTab, selectedPartnerId]);
  
  // Cargar Dashboard Global cuando se abre
  useEffect(() => {
    if (showGlobalDashboard && canViewGlobalDashboard && !globalDashboardData) {
      loadGlobalDashboard();
    }
  }, [showGlobalDashboard]);
  
  // Cargar Panel NPS completo con filtros
  const loadNpsPanel = async (instance = npsPanelInstance, filter = npsPanelFilter, search = npsPanelSearch, page = npsPanelPage) => {
    setLoadingNpsPanel(true);
    try {
      const data = await api.getNpsResponses(instance, filter, search, page);
      setNpsPanel(data);
    } catch (err) {
      console.error("Error loading NPS panel:", err);
      setNpsPanel(null);
    }
    setLoadingNpsPanel(false);
  };
  
  // Cargar detalles de una instancia NPS (drill-down)
  const loadNpsInstanceDetails = async (instanceName) => {
    setLoadingInstanceDetails(true);
    setSelectedNpsInstance(instanceName);
    try {
      const data = await api.getNpsInstanceDetails(instanceName);
      setNpsInstanceDetails(data);
    } catch (err) {
      console.error("Error loading instance details:", err);
      toast.error("Error cargando detalles de la instancia");
      setNpsInstanceDetails(null);
    }
    setLoadingInstanceDetails(false);
  };
  
  const handleDeleteBatch = async (batchId) => {
    if (!window.confirm(
      "¿Estás seguro? Esto eliminará todos los datos de esta carga y afectará los gráficos de evolución."
    )) {
      return;
    }
    
    setDeletingBatchId(batchId);
    try {
      await api.deleteBatch(batchId);
      toast.success("Importación eliminada correctamente");
      // Recargar todo
      loadBatches();
      loadDashboard();
      loadEvolution();
      loadStudents();
      loadPrograms();
      loadProgramStats();
    } catch (err) {
      toast.error(`Error eliminando: ${err.message}`);
    }
    setDeletingBatchId(null);
  };
  
  // Eliminar batch NPS
  const handleDeleteNpsBatch = async (batchId) => {
    if (!window.confirm(
      "¿Estás seguro? Esto eliminará todas las respuestas NPS de esta carga."
    )) {
      return;
    }
    
    try {
      const result = await api.deleteNpsBatch(batchId);
      toast.success(`Batch NPS eliminado: ${result.responses_deleted} respuestas`);
      loadNpsBatches();
      loadStudents();
      loadProgramStats();
    } catch (err) {
      toast.error(`Error eliminando: ${err.message}`);
    }
  };
  
  // Cargar datos de tickets HubSpot (Centro de Atención)
  const loadTicketsData = async () => {
    if (!selectedPartnerId) return;
    setLoadingTickets(true);
    try {
      const data = await api.getTickets(selectedPartnerId);
      setTicketsData(data);
    } catch (err) {
      console.error("Error loading tickets:", err);
      setTicketsData(null);
    }
    setLoadingTickets(false);
  };
  
  // Upload tickets HubSpot
  const handleUploadTickets = async () => {
    if (!ticketsFile || !selectedPartnerId) return;
    setProcessingTickets(true);
    try {
      const result = await api.uploadTickets(selectedPartnerId, ticketsFile);
      toast.success(result.message);
      setTicketsFile(null);
      loadTicketsData();  // Recargar datos
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    }
    setProcessingTickets(false);
  };
  
  const loadDashboard = async () => {
    try {
      // Calcular días del período según selección
      let periodDays = 90;  // Default
      if (activePeriod === "30d") periodDays = 30;
      else if (activePeriod === "60d") periodDays = 60;
      else if (activePeriod === "90d") periodDays = 90;
      else if (activePeriod === "365d") periodDays = 365;
      else if (activePeriod === "custom" && customDateFrom && customDateTo) {
        // Calcular días entre fechas custom
        const from = new Date(customDateFrom);
        const to = new Date(customDateTo);
        periodDays = Math.ceil((to - from) / (1000 * 60 * 60 * 24));
      }
      
      // Calcular días del período para egresados
      let egresadosDays = null;  // null = todo el tiempo
      if (egresadosPeriod === "30d") egresadosDays = 30;
      else if (egresadosPeriod === "60d") egresadosDays = 60;
      else if (egresadosPeriod === "90d") egresadosDays = 90;
      else if (egresadosPeriod === "365d") egresadosDays = 365;
      // "all" mantiene null
      
      const data = await api.getDashboard(
        selectedPartnerId, 
        programFilter, 
        sectionFilter, 
        courseFilter, 
        dateFrom || null, 
        dateTo || null,
        periodDays,
        egresadosDays,
        periodoCCF || null,  // Filtro por período de inicio (solo CCF)
        etapaCCF !== "todas" ? etapaCCF : null  // Filtro Etapa 1/Etapa 2 (solo CCF)
      );
      setDashboard(data);
    } catch (err) {
      console.error(err);
    }
  };
  
  // Cargar Dashboard Global (todos los partners)
  const loadGlobalDashboard = async () => {
    if (!canViewGlobalDashboard) return;
    setLoadingGlobalDashboard(true);
    try {
      const authToken = getAuthToken();
      // Obtener lista de partners
      const partnersRes = await fetch(`${API_URL}/api/partners`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const partnersData = await partnersRes.json();
      const activePartners = (partnersData.partners || []).filter(p => p.students_count > 0);
      
      // Cargar dashboard, NPS y tickets de cada partner en paralelo
      const dashboardPromises = activePartners.map(async (partner) => {
        try {
          // Llamada principal al dashboard
          const dashboardRes = await fetch(`${API_URL}/api/partners/${partner.id}/dashboard`, {
            headers: { Authorization: `Bearer ${authToken}` }
          });
          const dashboardData = await dashboardRes.json();
          
          // Llamada separada para NPS - USAR MISMA LÓGICA QUE NPS 360 (filtro por instance_name)
          let npsScore = null;
          let npsResponses = null;
          try {
            // Usar /api/nps360/summary con instance=slug (misma fuente que NPS 360)
            const npsRes = await fetch(`${API_URL}/api/nps360/summary?instance=${partner.slug}`, {
              headers: { Authorization: `Bearer ${authToken}` }
            });
            const npsData = await npsRes.json();
            // Estructura: { nps: { score, total, ... }, ejes: {...} }
            npsScore = npsData?.nps?.score ?? null;
            npsResponses = npsData?.nps?.total ?? null;
          } catch (e) {
            console.warn(`NPS not available for ${partner.name}`);
          }
          
          // Llamada separada para tickets
          let ticketsTotal = 0;
          try {
            const ticketsRes = await fetch(`${API_URL}/api/partners/${partner.id}/tickets`, {
              headers: { Authorization: `Bearer ${authToken}` }
            });
            const ticketsData = await ticketsRes.json();
            ticketsTotal = ticketsData?.total || 0;
          } catch (e) {
            console.warn(`Tickets not available for ${partner.name}`);
          }
          
          return {
            partner_id: partner.id,
            partner_name: partner.name,
            partner_slug: partner.slug,
            students_count: partner.students_count,
            dashboard: dashboardData,
            nps_score: npsScore,
            tickets_total: ticketsTotal
          };
        } catch (err) {
          console.error(`Error loading dashboard for ${partner.name}:`, err);
          return null;
        }
      });
      
      const results = await Promise.all(dashboardPromises);
      const validResults = results.filter(r => r !== null);
      
      // Cargar evolución de cada partner (últimos 12 meses)
      const evolutionPromises = activePartners.map(async (partner) => {
        try {
          const res = await fetch(`${API_URL}/api/partners/${partner.id}/evolution`, {
            headers: { Authorization: `Bearer ${authToken}` }
          });
          const data = await res.json();
          return {
            partner_id: partner.id,
            partner_name: partner.name,
            evolution: data.data_points || data.evolution || []
          };
        } catch (err) {
          return { partner_id: partner.id, partner_name: partner.name, evolution: [] };
        }
      });
      
      const evolutionResults = await Promise.all(evolutionPromises);
      
      setGlobalDashboardData({
        partners: validResults,
        evolution: evolutionResults,
        loaded_at: new Date().toISOString()
      });
    } catch (err) {
      console.error("Error loading global dashboard:", err);
      toast.error("Error al cargar Dashboard Global");
    } finally {
      setLoadingGlobalDashboard(false);
    }
  };
  
  const loadCertificates = async () => {
    if (!selectedPartnerId) return;
    setCertificatesLoading(true);
    try {
      const data = await api.getCertificatesSummary(selectedPartnerId, cursosView, cursosSectionFilter);
      setCertificates(data);
    } catch (err) {
      console.error("Error loading certificates:", err);
    } finally {
      setCertificatesLoading(false);
    }
  };
  
  // Cargar certificados por programa (solo CCF)
  const loadProgramsCCF = async () => {
    if (!selectedPartnerId) return;
    setProgramsCCFLoading(true);
    try {
      const authToken = getAuthToken();
      const res = await fetch(`${API_URL}/api/partners/${selectedPartnerId}/certificates_by_program`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setProgramsCCF(data);
      }
    } catch (err) {
      console.error("Error loading programs CCF:", err);
    } finally {
      setProgramsCCFLoading(false);
    }
  };
  
  const loadEvolution = async () => {
    try {
      const data = await api.getEvolution(selectedPartnerId);
      setEvolution(data.data_points || []);
    } catch (err) {
      console.error(err);
    }
  };
  
  const loadStudents = async () => {
    try {
      const params = { page: studentsPage, page_size: 15 };
      if (riskFilter !== "all") params.risk_filter = riskFilter;
      if (searchTerm) params.search = searchTerm;
      if (programFilter && programFilter !== "all") params.program = programFilter;
      // RE-ARQUITECTURA: Nuevos filtros
      if (sectionFilter && sectionFilter !== "all") params.section = sectionFilter;
      if (courseFilter && courseFilter !== "all") params.course = courseFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      // Filtro NPS
      if (npsFilter && npsFilter !== "all") params.nps_filter = npsFilter;
      // Filtro Período
      if (periodFilter && periodFilter !== "all") params.period_filter = periodFilter;
      // Filtro Etapa 1/Etapa 2 - solo CCF
      if (selectedPartner?.slug === 'cordoba-crea-futuro' && etapaCCF !== "todas") params.etapa = etapaCCF;
      // Ordenamiento
      if (sortBy) {
        params.sort_by = sortBy;
        params.sort_order = sortOrder;
      }
      
      const data = await api.getStudents(selectedPartnerId, params);
      setStudents(data.students);
      setStudentsTotal(data.total);
      setStudentsColorCounts(data.color_counts || {});
      setDetractorsCount(data.detractors_count || 0);  // NUEVO: Capturar contador de detractores
    } catch (err) {
      console.error(err);
    }
  };
  
  // Cargar períodos disponibles para el filtro
  const loadPeriods = async () => {
    if (!selectedPartnerId) return;
    try {
      const authToken = getAuthToken();
      const res = await fetch(`${API_URL}/api/partners/${selectedPartnerId}/enrollment_periods`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAvailablePeriods(data.periods || []);
      }
    } catch (err) {
      console.error("Error loading periods:", err);
    }
  };
  
  const handleProcessFiles = async () => {
    if (!uploadFiles.sis) {
      toast.error("El archivo SIS.zip es obligatorio (contiene enrollments.csv como Base Madre)");
      return;
    }
    
    setProcessing(true);
    setProcessingMessage('Subiendo archivos...');
    setProcessingProgress(0);
    setLastResult(null);
    
    // Calcular tamaño total de todos los archivos
    const allFiles = [uploadFiles.sis, uploadFiles.lastAccess, uploadFiles.grades, 
                      uploadFiles.submissions, uploadFiles.engagement, 
                      uploadFiles.provisioning, uploadFiles.baseEnrolados].filter(f => f);
    const totalSize = allFiles.reduce((sum, f) => sum + f.size, 0);
    const totalSizeMB = (totalSize / (1024 * 1024)).toFixed(1);
    
    // Mostrar mensaje si usará chunked upload (>8MB total o archivo individual >4MB)
    const willUseChunked = totalSize > 8 * 1024 * 1024 || allFiles.some(f => f.size > 4 * 1024 * 1024);
    if (willUseChunked) {
      toast.info(
        `📦 ${allFiles.length} archivos (${totalSizeMB} MB) — subiendo en modo segmentado`,
        { duration: 5000 }
      );
    }
    
    try {
      // Callback para mostrar progreso de upload chunked
      const onProgress = (progressInfo) => {
        if (progressInfo.phase === 'uploading') {
          setProcessingMessage(progressInfo.message);
          setProcessingProgress(progressInfo.progress || 0);
        }
      };
      
      // Iniciar procesamiento en background (pasar fecha de referencia)
      const initResult = await api.processFiles(selectedPartnerId, uploadFiles, referenceDate, onProgress);
      
      if (!initResult.batch_id) {
        throw new Error("No se recibió batch_id del servidor");
      }
      
      setActiveBatchId(initResult.batch_id);
      setProcessingMessage('Procesando archivos...');
      toast.success(`✅ Archivos subidos. Procesando...`, { duration: 3000 });
      
      // Iniciar polling usando la función dedicada
      startProcessingPolling(initResult.batch_id);
      
    } catch (err) {
      console.error("Error procesando archivos:", err);
      toast.error(
        `Error al procesar archivos: ${err.message || "Error desconocido del servidor"}. ` +
        `Verifica que los archivos tengan el formato correcto.`,
        { duration: 8000 }
      );
      setProcessing(false);
      setProcessingMessage('');
      setProcessingProgress(0);
    }
  };
  
  // Handle NPS file upload
  const handleUploadNPS = async () => {
    if (!npsFile) {
      toast.error("Selecciona un archivo NPS");
      return;
    }
    
    setProcessingNps(true);
    try {
      const result = await api.uploadNPS(npsFile);
      // Mostrar modal con resumen detallado
      setNpsUploadResult(result);
      setNpsFile(null);
      // Refrescar datos
      loadNpsBatches();  // Recargar historial de NPS
      // loadNpsStats removido - NPS solo en pestaña NPS 360
      loadStudents();
      loadProgramStats();
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    }
    setProcessingNps(false);
  };

  // Handler para subir archivo de geolocalización (SOLO CCF)
  const handleUploadGeo = async () => {
    if (!uploadFiles.geolocalizacion) {
      toast.error('Seleccioná un archivo .xlsx de geolocalización');
      return;
    }
    
    setProcessingGeo(true);
    setGeoUploadResult(null);
    
    try {
      const token = localStorage.getItem('auth_token');
      const formData = new FormData();
      formData.append('file', uploadFiles.geolocalizacion);
      
      const res = await fetch(`${API_URL}/api/partners/${selectedPartnerId}/upload_geo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Error al procesar archivo');
      }
      
      setGeoUploadResult(data);
      setUploadFiles(prev => ({ ...prev, geolocalizacion: null }));
      toast.success(`${data.matches_encontrados} alumnos actualizados con localidad`);
      // Recargar datos de geografía
      loadGeoData();
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    }
    setProcessingGeo(false);
  };

  // Cargar datos de geografía (solo CCF)
  const loadGeoData = async () => {
    if (selectedPartner?.slug !== 'cordoba-crea-futuro') return;
    
    setLoadingGeo(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/partners/${selectedPartnerId}/geo-semaforo`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setGeoData(data);
      }
    } catch (err) {
      console.error('Error cargando datos de geografía:', err);
    }
    setLoadingGeo(false);
  };
  
  // Cargar datos de actividad por beca (solo CCF)
  const loadActividadBecas = async () => {
    if (selectedPartner?.slug !== 'cordoba-crea-futuro') return;
    
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/partners/${selectedPartnerId}/actividad-becas`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setActividadBecas(data);
      }
    } catch (err) {
      console.error('Error cargando actividad por beca:', err);
    }
  };
  
  const selectedPartner = partners.find(p => p.id === selectedPartnerId);
  const totalPages = Math.ceil(studentsTotal / 15);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: CAPABILIA.green }} />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-white">
      <Toaster position="top-right" />
      <SecureExportModal />
      
      {/* Header - Blanco con acentos verdes */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Logo Capabilia en color */}
            <img 
              src={capabiliaLogo} 
              alt="Capabilia" 
              style={{ 
                width: '40px', 
                height: '40px',
                ...logoStyleColor
              }}
            />
            <div>
              <h1 className="font-semibold text-xl tracking-tight text-green-600">Persistencia 360</h1>
              <p className="text-xs text-gray-500">by Capabilia</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Partner Selector */}
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-gray-400" />
              <Select 
                value={selectedPartnerId || ""} 
                onValueChange={setSelectedPartnerId}
                disabled={!!userPartnerIdOnly}
              >
                <SelectTrigger className="w-[200px] bg-green-50 border-green-600 text-green-700 hover:bg-green-100 transition-colors">
                  <SelectValue placeholder="Seleccionar Partner" />
                </SelectTrigger>
                <SelectContent>
                  {partners.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {!isReadOnly && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-green-600 hover:bg-green-50"
                  onClick={() => setShowCreateDialog(true)}
                >
                  <Plus className="w-5 h-5" />
                </Button>
              )}
            </div>
            
            {/* Botón Dashboard Global - Solo para superadmin y gestor_academico */}
            {canViewGlobalDashboard && (
              <Button
                variant={showGlobalDashboard ? "default" : "outline"}
                size="sm"
                className={showGlobalDashboard 
                  ? "bg-green-600 hover:bg-green-700 text-white" 
                  : "border-green-600 text-green-600 hover:bg-green-50"
                }
                onClick={() => setShowGlobalDashboard(!showGlobalDashboard)}
                title="Dashboard Global"
              >
                <LayoutDashboard className="w-4 h-4 mr-2" />
                Global
              </Button>
            )}
            
            {/* User Menu */}
            {authUser && (
              <div className="flex items-center gap-3 border-l border-gray-200 pl-4 ml-2">
                {/* Security Shield */}
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      className="p-2 rounded-full hover:bg-emerald-50 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
                      aria-label="Información de seguridad"
                      data-testid="security-shield-btn"
                    >
                      <ShieldCheck className="w-5 h-5 text-emerald-600" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent 
                    className="w-80 p-4 bg-white shadow-lg border border-slate-200 rounded-lg"
                    side="bottom"
                    align="end"
                    sideOffset={8}
                  >
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
                        <ShieldCheck className="w-5 h-5 text-emerald-600" />
                        <h4 className="font-semibold text-slate-800 text-sm">
                          Nuestro compromiso con la seguridad y tus datos
                        </h4>
                      </div>
                      
                      <p className="text-xs text-slate-600 leading-relaxed">
                        En Persistencia 360 protegemos la información de nuestros alumnos y partners con estándares profesionales:
                      </p>
                      
                      <ul className="space-y-1.5 text-xs text-slate-600">
                        <li className="flex items-start gap-2">
                          <span className="text-emerald-500 mt-0.5">✓</span>
                          <span><strong>Acceso por roles:</strong> cada persona accede únicamente a lo que su función necesita.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-emerald-500 mt-0.5">✓</span>
                          <span><strong>Conexiones cifradas:</strong> toda la información viaja de forma segura (HTTPS).</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-emerald-500 mt-0.5">✓</span>
                          <span><strong>Contraseñas protegidas:</strong> se almacenan cifradas, nunca en texto legible.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-emerald-500 mt-0.5">✓</span>
                          <span><strong>Accesos restringidos:</strong> permisos mínimos con sistemas externos.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-emerald-500 mt-0.5">✓</span>
                          <span><strong>Registro de auditoría:</strong> las acciones sensibles quedan registradas.</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-emerald-500 mt-0.5">✓</span>
                          <span><strong>Resguardo de información:</strong> copias de seguridad periódicas.</span>
                        </li>
                      </ul>
                      
                      <div className="pt-2 border-t border-slate-100 space-y-1.5">
                        <p className="text-xs text-slate-500 leading-relaxed">
                          Revisiones periódicas alineadas a estándares internacionales (OWASP, principios de ISO 27001).
                        </p>
                        <p className="text-xs text-slate-500 leading-relaxed">
                          Protección de datos personales conforme a la normativa vigente (Ley 25.326).
                        </p>
                        <p className="text-xs text-emerald-600 font-medium mt-2">
                          Mejoramos continuamente. La seguridad es un proceso permanente.
                        </p>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
                
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-700">{authUser.name || authUser.email}</p>
                  <p className="text-xs text-gray-500 capitalize">{authUser.role?.replace('_', ' ')}</p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-gray-500 hover:bg-gray-100"
                  onClick={onLogout}
                  title="Cerrar sesión"
                >
                  <LogOut className="w-5 h-5" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-6 py-6">
        {/* Dashboard Global */}
        {showGlobalDashboard && canViewGlobalDashboard ? (
          <div className="space-y-6">
            {/* Header del Dashboard Global */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <LayoutDashboard className="w-8 h-8 text-green-600" />
                <div>
                  <h1 className="text-2xl font-bold text-slate-800">Dashboard Global</h1>
                  <p className="text-sm text-slate-500">Vista consolidada de todos los partners</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setGlobalDashboardData(null);
                    loadGlobalDashboard();
                  }}
                  disabled={loadingGlobalDashboard}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${loadingGlobalDashboard ? 'animate-spin' : ''}`} />
                  Actualizar
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowGlobalDashboard(false)}
                >
                  <X className="w-4 h-4 mr-2" />
                  Cerrar
                </Button>
              </div>
            </div>
            
            {loadingGlobalDashboard ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-green-600" />
                <span className="ml-3 text-slate-600">Cargando datos de todos los partners...</span>
              </div>
            ) : globalDashboardData ? (
              <>
                {/* SECCIÓN 1: Cards por Partner */}
                <div className="space-y-4">
                  <h2 className="text-lg font-semibold text-slate-700 flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Resumen por Partner
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {globalDashboardData.partners.map((p) => {
                      const semaforo = p.dashboard?.semaforo || {};
                      // FIX 1: Usar semaforo.total directamente (evita doble conteo)
                      const cursando = semaforo.total || 0;
                      const enRiesgo = (semaforo.orange || 0) + (semaforo.red || 0) + (semaforo.dark_gray || 0);
                      const pctRiesgo = cursando > 0 ? ((enRiesgo / cursando) * 100).toFixed(1) : 0;
                      // FIX 2: NPS viene de llamada separada
                      const npsScore = p.nps_score !== null ? p.nps_score : 'N/A';
                      // FIX 3: Usar finalizacion_percent directamente del endpoint
                      const tasaFinalizacion = p.dashboard?.egresados?.finalizacion_percent || 0;
                      // FIX 4: Tickets viene de llamada separada
                      const tickets = p.tickets_total || 0;
                      
                      return (
                        <Card key={p.partner_id} className="border-l-4 border-l-green-500 hover:shadow-md transition-shadow">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-base font-semibold text-slate-800 flex items-center justify-between">
                              {p.partner_name}
                              <Badge variant="outline" className="text-xs">
                                {p.students_count} alumnos
                              </Badge>
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div className="bg-slate-50 rounded-lg p-2">
                                <p className="text-slate-500 text-xs">Cursando</p>
                                <p className="font-semibold text-slate-800">{cursando}</p>
                              </div>
                              <div className={`rounded-lg p-2 ${parseFloat(pctRiesgo) > 30 ? 'bg-red-50' : parseFloat(pctRiesgo) > 15 ? 'bg-orange-50' : 'bg-green-50'}`}>
                                <p className="text-slate-500 text-xs">% En Riesgo</p>
                                <p className={`font-semibold ${parseFloat(pctRiesgo) > 30 ? 'text-red-600' : parseFloat(pctRiesgo) > 15 ? 'text-orange-600' : 'text-green-600'}`}>
                                  {pctRiesgo}%
                                </p>
                              </div>
                              <div className={`rounded-lg p-2 ${npsScore !== 'N/A' && npsScore >= 50 ? 'bg-green-50' : npsScore !== 'N/A' && npsScore >= 0 ? 'bg-yellow-50' : 'bg-slate-50'}`}>
                                <p className="text-slate-500 text-xs">NPS Score</p>
                                <p className={`font-semibold ${npsScore !== 'N/A' && npsScore >= 50 ? 'text-green-600' : npsScore !== 'N/A' && npsScore >= 0 ? 'text-yellow-600' : 'text-slate-600'}`}>
                                  {npsScore !== 'N/A' ? Number(npsScore).toFixed(0) : 'N/A'}
                                </p>
                              </div>
                              <div className="bg-indigo-50 rounded-lg p-2">
                                <p className="text-slate-500 text-xs">Licencias LMS</p>
                                <p className="font-semibold text-indigo-600">{formatNumber(p.dashboard?.licencias_lms || 0)}</p>
                              </div>
                              <div className="bg-blue-50 rounded-lg p-2">
                                <p className="text-slate-500 text-xs">Finalización</p>
                                <p className="font-semibold text-blue-600">{Number(tasaFinalizacion).toFixed(1)}%</p>
                              </div>
                            </div>
                            <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                              <div className="flex items-center gap-1 text-sm text-slate-600">
                                <Headphones className="w-4 h-4" />
                                <span>{tickets} tickets</span>
                              </div>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="text-green-600 hover:bg-green-50"
                                onClick={() => {
                                  setSelectedPartnerId(p.partner_id);
                                  setShowGlobalDashboard(false);
                                }}
                              >
                                Ver detalle →
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                </div>
                
                {/* SECCIÓN 2: Gráfico Comparativo Semáforo (Barras Horizontales Apiladas) */}
                <Card className="p-6">
                  <CardHeader className="px-0 pt-0">
                    <CardTitle className="text-lg font-semibold text-slate-700 flex items-center gap-2">
                      <BarChart2 className="w-5 h-5" />
                      Comparativo Semáforo por Partner
                    </CardTitle>
                    <CardDescription>Distribución de estados del semáforo</CardDescription>
                  </CardHeader>
                  <CardContent className="px-0">
                    <div className="space-y-3">
                      {globalDashboardData.partners.map((p) => {
                        const semaforo = p.dashboard?.semaforo || {};
                        // Usar semaforo.total directamente (evita doble conteo)
                        const total = semaforo.total || 0;
                        
                        const colors = {
                          green: { bg: '#22c55e', label: 'Con hitos' },
                          blue: { bg: '#3b82f6', label: 'Acompañar' },
                          yellow: { bg: '#eab308', label: 'Sin hitos' },
                          orange: { bg: '#f97316', label: 'Sin act.' },
                          red: { bg: '#ef4444', label: 'Riesgo' },
                          dark_gray: { bg: '#6b7280', label: 'Inactivo' },
                          black: { bg: '#1f2937', label: 'No inició' }
                        };
                        
                        return (
                          <div key={p.partner_id} className="flex items-center gap-3">
                            <div className="w-32 text-sm font-medium text-slate-700 truncate" title={p.partner_name}>
                              {p.partner_name}
                            </div>
                            <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden flex">
                              {Object.entries(colors).map(([key, config]) => {
                                const value = semaforo[key] || 0;
                                const pct = total > 0 ? (value / total) * 100 : 0;
                                if (pct < 1) return null;
                                return (
                                  <div
                                    key={key}
                                    className="h-full transition-all"
                                    style={{ width: `${pct}%`, backgroundColor: config.bg }}
                                    title={`${config.label}: ${value} (${pct.toFixed(1)}%)`}
                                  />
                                );
                              })}
                            </div>
                            <div className="w-16 text-xs text-slate-500 text-right">
                              {total} total
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {/* Leyenda */}
                    <div className="flex flex-wrap items-center justify-center gap-4 mt-6 pt-4 border-t border-slate-100">
                      <span className="flex items-center gap-1 text-xs"><span className="w-3 h-3 rounded-full" style={{backgroundColor:'#22c55e'}}></span> Con hitos</span>
                      <span className="flex items-center gap-1 text-xs"><span className="w-3 h-3 rounded-full" style={{backgroundColor:'#3b82f6'}}></span> Acompañar</span>
                      <span className="flex items-center gap-1 text-xs"><span className="w-3 h-3 rounded-full" style={{backgroundColor:'#eab308'}}></span> Sin hitos</span>
                      <span className="flex items-center gap-1 text-xs"><span className="w-3 h-3 rounded-full" style={{backgroundColor:'#f97316'}}></span> Sin act.</span>
                      <span className="flex items-center gap-1 text-xs"><span className="w-3 h-3 rounded-full" style={{backgroundColor:'#ef4444'}}></span> Riesgo</span>
                      <span className="flex items-center gap-1 text-xs"><span className="w-3 h-3 rounded-full" style={{backgroundColor:'#6b7280'}}></span> Inactivo</span>
                      <span className="flex items-center gap-1 text-xs"><span className="w-3 h-3 rounded-full" style={{backgroundColor:'#1f2937'}}></span> No inició</span>
                    </div>
                  </CardContent>
                </Card>
                
                {/* SECCIÓN 3: Gráfico Evolutivo Mensual de Enrolados */}
                <Card className="p-6">
                  <CardHeader className="px-0 pt-0">
                    <CardTitle className="text-lg font-semibold text-slate-700 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5" />
                      Evolución Mensual de Enrolados
                    </CardTitle>
                    <CardDescription>Nuevos enrolados por partner (últimos 12 meses)</CardDescription>
                  </CardHeader>
                  <CardContent className="px-0">
                    {(() => {
                      // Preparar datos para el gráfico de líneas
                      const allMonths = new Set();
                      globalDashboardData.evolution.forEach(p => {
                        (p.evolution || []).slice(-12).forEach(e => {
                          const dateField = e.date || e.snapshot_date;
                          if (dateField) allMonths.add(dateField.substring(0, 7));
                        });
                      });
                      
                      const sortedMonths = Array.from(allMonths).sort();
                      
                      // Crear datos del gráfico
                      const chartData = sortedMonths.map(month => {
                        const dataPoint = { month: month };
                        globalDashboardData.evolution.forEach(p => {
                          const snapshot = (p.evolution || []).find(e => {
                            const dateField = e.date || e.snapshot_date;
                            return dateField?.startsWith(month);
                          });
                          const total = snapshot ? 
                            (snapshot.green || 0) + (snapshot.blue || 0) + (snapshot.yellow || 0) + 
                            (snapshot.orange || 0) + (snapshot.red || 0) + (snapshot.dark_gray || 0) + (snapshot.black || 0) : 0;
                          dataPoint[p.partner_name] = total;
                        });
                        return dataPoint;
                      });
                      
                      const partnerColors = ['#22c55e', '#3b82f6', '#f97316', '#8b5cf6', '#ec4899', '#06b6d4'];
                      
                      return chartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis 
                              dataKey="month" 
                              tick={{ fontSize: 11 }}
                              tickFormatter={(v) => {
                                const [y, m] = v.split('-');
                                const months = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
                                return `${months[parseInt(m)-1]} ${y.slice(2)}`;
                              }}
                            />
                            <YAxis tick={{ fontSize: 11 }} />
                            <RechartsTooltip 
                              contentStyle={{ 
                                backgroundColor: 'white', 
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                fontSize: '12px'
                              }}
                            />
                            <Legend />
                            {globalDashboardData.evolution.map((p, idx) => (
                              <Line 
                                key={p.partner_id}
                                type="monotone"
                                dataKey={p.partner_name}
                                stroke={partnerColors[idx % partnerColors.length]}
                                strokeWidth={2}
                                dot={{ r: 3 }}
                                activeDot={{ r: 5 }}
                              />
                            ))}
                          </LineChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="text-center py-10 text-slate-500">
                          No hay datos de evolución disponibles
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card className="p-12 text-center">
                <AlertTriangle className="w-12 h-12 mx-auto text-yellow-500 mb-4" />
                <p className="text-slate-600">No se pudieron cargar los datos del Dashboard Global</p>
                <Button className="mt-4" onClick={loadGlobalDashboard}>
                  <RefreshCw className="w-4 h-4 mr-2" /> Reintentar
                </Button>
              </Card>
            )}
          </div>
        ) : !selectedPartnerId ? (
          <Card className="p-12 text-center">
            <Building2 className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <h2 className="text-xl font-semibold text-slate-700 mb-2">No hay partners</h2>
            <p className="text-slate-500 mb-4">Crea tu primer partner para comenzar</p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="w-4 h-4 mr-2" /> Crear Partner
            </Button>
          </Card>
        ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="flex w-full mb-6">
              {/* 1. Config (Configuraciones) - superadmin, gestor_academico, academia_servicio */}
              {(authUser?.role === 'superadmin' || authUser?.role === 'gestor_academico' || authUser?.role === 'academia_servicio') && authUser?.role !== 'casa' && (
                <TabsTrigger value="config" className="flex-1 gap-0.5 text-xs px-1" data-testid="tab-config">
                  <Settings className="w-3 h-3" /> Config
                </TabsTrigger>
              )}
              {/* 3. Usuarios (Admin) - Solo visible para superadmin (NO casa) */}
              {canManageUsers && authUser?.role !== 'casa' && (
                <TabsTrigger value="users" className="flex-1 gap-0.5 text-xs px-1" data-testid="tab-users">
                  <Users className="w-3 h-3" /> Usuarios
                </TabsTrigger>
              )}
              {/* Registro de descargas (hardening 2026-10) - Solo superadmin */}
              {authUser?.role === 'superadmin' && (
                <TabsTrigger value="export-audit-log" className="flex-1 gap-0.5 text-xs px-1" data-testid="tab-export-audit-log">
                  <ShieldCheck className="w-3 h-3" /> Descargas
                </TabsTrigger>
              )}
              {/* 4. Enrolados 360 - Visible para roles con acceso a datos comerciales (NO casa) */}
              {(authUser?.role === 'superadmin' || authUser?.role === 'gestor_academico' || authUser?.role === 'comercial' || authUser?.role === 'auditor' || authUser?.role === 'academia_servicio') && authUser?.role !== 'casa' && (
                <TabsTrigger value="enrolados360" className="flex-1 gap-0.5 text-xs px-1" data-testid="tab-enrolados360">
                  <TrendingUp className="w-3 h-3" /> Enrolados
                </TabsTrigger>
              )}
              {/* 5. Academia (antes Dashboard) - VISIBLE para CASA */}
              <TabsTrigger value="dashboard" className="flex-1 gap-0.5 text-xs px-1" data-testid="tab-dashboard">
                <BarChart3 className="w-3 h-3" /> Academia
              </TabsTrigger>
              {/* 6. Cursos - VISIBLE para CASA */}
              <TabsTrigger value="certificates" className="flex-1 gap-0.5 text-xs px-1" data-testid="tab-certificates">
                <Award className="w-3 h-3" /> Cursos
              </TabsTrigger>
              {/* 7. Cohortes (antes Secciones) - VISIBLE para CASA */}
              <TabsTrigger value="sections" className="flex-1 gap-0.5 text-xs px-1" data-testid="tab-sections">
                <Layers className="w-3 h-3" /> Cohortes
              </TabsTrigger>
              {/* 8. Alumno (antes Explorador) - VISIBLE para CASA */}
              <TabsTrigger value="explorer" className="flex-1 gap-0.5 text-xs px-1" data-testid="tab-explorer">
                <Users className="w-3 h-3" /> Alumno
              </TabsTrigger>
              {/* 9. Atención - NO visible para CASA */}
              {authUser?.role !== 'casa' && (
                <TabsTrigger value="atencion" className="flex-1 gap-0.5 text-xs px-1" data-testid="tab-atencion">
                  <Headphones className="w-3 h-3" /> Atención
                </TabsTrigger>
              )}
              {/* 10. NPS (antes NPS 360) - NO visible para CASA */}
              {authUser?.role !== 'casa' && (
                <TabsTrigger value="nps360" className="flex-1 gap-0.5 text-xs px-1" data-testid="tab-nps360">
                  <TrendingUp className="w-3 h-3" /> NPS
                </TabsTrigger>
              )}
              {/* 11. Calidad de Agente - NO visible para CASA */}
              {(authUser?.role === 'superadmin' || authUser?.role === 'gestor_academico' || authUser?.role === 'auditor' || authUser?.role === 'academia_servicio') && authUser?.role !== 'casa' && (
                <TabsTrigger value="agent-quality" className="flex-1 gap-0.5 text-xs px-1" data-testid="tab-agent-quality">
                  <ClipboardCheck className="w-3 h-3" /> Calidad
                </TabsTrigger>
              )}
              {/* 12. Motor de Persistencia - NO visible para CASA */}
              {(authUser?.role === 'superadmin' || authUser?.role === 'gestor_academico' || authUser?.role === 'auditor' || authUser?.role === 'academia_servicio') && authUser?.role !== 'casa' && (
                <TabsTrigger value="persistencia" className="flex-1 gap-0.5 text-xs px-1" data-testid="tab-persistencia">
                  <TrendingUp className="w-3 h-3" /> Persistencia
                </TabsTrigger>
              )}
            </TabsList>
            
            {/* ══════════════════════════════════════════════════════════════════════════════════════ */}
            {/* TAB: CONFIGURACIONES - Panel unificado de configs del sistema                         */}
            {/* Roles: superadmin (todo), academia_servicio (configs sin botones destructivos)        */}
            {/* ══════════════════════════════════════════════════════════════════════════════════════ */}
            {(authUser?.role === 'superadmin' || authUser?.role === 'gestor_academico' || authUser?.role === 'academia_servicio') && (
              <TabsContent value="config">
                <Card>
                  <CardHeader className="pb-4 border-b">
                    <CardTitle className="flex items-center gap-2">
                      <Settings className="w-5 h-5 text-slate-600" />
                      Configuraciones del Sistema
                    </CardTitle>
                    <CardDescription>
                      Panel unificado de sincronizaciones, parámetros y configuraciones operativas
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-6 space-y-6">
                    
                    {/* ══════════════════════════════════════════════════════════════════════ */}
                    {/* SECCIÓN 0: ESTADO DE SINCRONIZACIÓN / CONTROL DE DATOS - Solo superadmin */}
                    {/* ══════════════════════════════════════════════════════════════════════ */}
                    {authUser?.role === 'superadmin' && (
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2 pb-2 border-b border-slate-200">
                          <Activity className="w-5 h-5 text-emerald-600" />
                          Estado de Sincronización / Control de Datos
                        </h3>
                        <PersistenciaSyncSemaforo token={getAuthToken()} />
                      </div>
                    )}

                    {/* ══════════════════════════════════════════════════════════════════════ */}
                    {/* SECCIÓN 1: SINCRONIZACIONES DE DATOS */}
                    {/* ══════════════════════════════════════════════════════════════════════ */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2 pb-2 border-b border-slate-200">
                        <RefreshCw className="w-5 h-5 text-blue-600" />
                        Sincronizaciones de Datos
                      </h3>
                      
                      {/* CANVAS SYNC */}
                      <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-5">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-orange-100 rounded-lg">
                              <RefreshCw className="w-5 h-5 text-orange-600" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-orange-900">Canvas LMS</h4>
                              <p className="text-sm text-orange-700">Sincroniza 6 reports académicos desde Canvas</p>
                            </div>
                          </div>
                          <Button
                            onClick={async () => {
                              if (!selectedPartner?.id) {
                                toast.error('Selecciona un partner primero');
                                return;
                              }
                              setCanvasSyncing(true);
                              setCanvasSyncError(null);
                              setCanvasSyncResult(null);
                              setCanvasSyncStatus({ status: 'processing', current_step: 'Iniciando...', progress: 0 });
                              
                              try {
                                await api.syncPartnerWithCanvas(selectedPartner.id);
                                setCanvasSyncPolling(true);
                                const pollInterval = setInterval(async () => {
                                  try {
                                    const status = await api.getCanvasSyncStatus(selectedPartner.id);
                                    setCanvasSyncStatus(status);
                                    
                                    if (status.status === 'completed') {
                                      clearInterval(pollInterval);
                                      setCanvasSyncPolling(false);
                                      setCanvasSyncing(false);
                                      setCanvasSyncResult({
                                        message: 'Sincronización completada',
                                        last_sync: status.last_successful_sync
                                      });
                                      setTimeout(() => { loadDashboard(); loadBatches(); }, 2000);
                                    } else if (status.status === 'error' || status.status === 'expired') {
                                      clearInterval(pollInterval);
                                      setCanvasSyncPolling(false);
                                      setCanvasSyncing(false);
                                      setCanvasSyncError(status.last_error || 'Error desconocido');
                                    }
                                  } catch (pollErr) {
                                    console.error('Error polling status:', pollErr);
                                  }
                                }, 3000);
                                
                                setTimeout(() => {
                                  if (canvasSyncPolling) {
                                    clearInterval(pollInterval);
                                    setCanvasSyncPolling(false);
                                    setCanvasSyncing(false);
                                    setCanvasSyncError('Tiempo de espera agotado. El proceso puede continuar en background.');
                                  }
                                }, 20 * 60 * 1000);
                                
                              } catch (err) {
                                setCanvasSyncError(err.message);
                                setCanvasSyncing(false);
                                setCanvasSyncStatus(null);
                              }
                            }}
                            disabled={canvasSyncing || !selectedPartner?.id}
                            className="bg-orange-600 hover:bg-orange-700 text-white gap-2"
                            data-testid="config-canvas-sync-btn"
                          >
                            {canvasSyncing ? (
                              <><Loader2 className="w-4 h-4 animate-spin" /> Sincronizando...</>
                            ) : (
                              <><RefreshCw className="w-4 h-4" /> Sincronizar Canvas</>
                            )}
                          </Button>
                        </div>
                        
                        {canvasSyncing && canvasSyncStatus && (
                          <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                            <div className="flex items-center gap-3 mb-3">
                              <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                              <div>
                                <p className="text-blue-800 font-medium">{canvasSyncStatus.current_step || 'Procesando...'}</p>
                                <p className="text-blue-600 text-xs">Puede tardar unos minutos.</p>
                              </div>
                            </div>
                            <div className="w-full bg-blue-200 rounded-full h-2">
                              <div className="bg-blue-600 h-2 rounded-full transition-all duration-500" style={{ width: `${canvasSyncStatus.progress || 0}%` }} />
                            </div>
                          </div>
                        )}
                        
                        {canvasSyncResult && !canvasSyncing && (
                          <div className="mt-3 p-3 bg-green-100 border border-green-200 rounded-lg text-sm">
                            <p className="text-green-800 font-medium flex items-center gap-2">
                              <CheckCircle className="w-4 h-4" /> {canvasSyncResult.message}
                            </p>
                          </div>
                        )}
                        
                        {canvasSyncError && !canvasSyncing && (
                          <div className="mt-3 p-3 bg-red-100 border border-red-200 rounded-lg text-sm">
                            <p className="text-red-800 font-medium flex items-center gap-2">
                              <AlertTriangle className="w-4 h-4" /> Error: {canvasSyncError}
                            </p>
                          </div>
                        )}
                        
                        <p className="text-xs text-orange-600 mt-3">
                          <strong>Partner:</strong> {selectedPartner?.name || 'Ninguno seleccionado'}
                        </p>
                      </div>
                      
                      {/* QUALTRICS NPS SYNC */}
                      <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-purple-100 rounded-lg">
                              <Activity className="w-6 h-6 text-purple-600" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-purple-900">Qualtrics NPS</h4>
                              <p className="text-sm text-purple-700">Descarga respuestas de encuestas NPS (B2C + CCF)</p>
                            </div>
                          </div>
                          <Button
                            onClick={async () => {
                              setQualtricsSyncing(true);
                              setQualtricsSyncError(null);
                              setQualtricsSyncResult(null);
                              setQualtricsSyncStatus({ status: 'processing', current_step: 'Iniciando...', progress: 0 });
                              
                              try {
                                await api.syncQualticsNPS();
                                const pollInterval = setInterval(async () => {
                                  try {
                                    const status = await api.getQualtricsSyncStatus();
                                    setQualtricsSyncStatus(status);
                                    
                                    if (status.status === 'completed') {
                                      clearInterval(pollInterval);
                                      setQualtricsSyncing(false);
                                      setQualtricsSyncResult({
                                        message: `Sincronización completada: ${status.responses_processed} respuestas procesadas`,
                                        last_sync: status.last_successful_sync
                                      });
                                    } else if (status.status === 'error' || status.status === 'expired') {
                                      clearInterval(pollInterval);
                                      setQualtricsSyncing(false);
                                      setQualtricsSyncError(status.last_error || 'Error desconocido');
                                    }
                                  } catch (pollErr) {
                                    console.error('Error polling Qualtrics status:', pollErr);
                                  }
                                }, 3000);
                                
                                setTimeout(() => {
                                  clearInterval(pollInterval);
                                  setQualtricsSyncing(false);
                                  if (!qualtricsSyncResult && !qualtricsSyncError) {
                                    setQualtricsSyncError('Tiempo de espera agotado');
                                  }
                                }, 10 * 60 * 1000);
                                
                              } catch (err) {
                                setQualtricsSyncing(false);
                                setQualtricsSyncError(err.message);
                              }
                            }}
                            disabled={qualtricsSyncing}
                            className="bg-purple-600 hover:bg-purple-700 text-white gap-2"
                            data-testid="config-qualtrics-sync-btn"
                          >
                            {qualtricsSyncing ? (
                              <><Loader2 className="w-4 h-4 animate-spin" /> Sincronizando...</>
                            ) : (
                              <><RefreshCw className="w-4 h-4" /> Sincronizar NPS</>
                            )}
                          </Button>
                        </div>
                        
                        {qualtricsSyncing && qualtricsSyncStatus && (
                          <div className="mt-3 p-4 bg-purple-100 border border-purple-300 rounded-lg">
                            <div className="flex items-center gap-3 mb-3">
                              <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
                              <p className="text-purple-800 font-medium">{qualtricsSyncStatus.current_step || 'Procesando...'}</p>
                            </div>
                            <div className="w-full bg-purple-200 rounded-full h-2">
                              <div className="bg-purple-600 h-2 rounded-full transition-all duration-500" style={{ width: `${qualtricsSyncStatus.progress || 0}%` }} />
                            </div>
                          </div>
                        )}
                        
                        {qualtricsSyncResult && !qualtricsSyncing && (
                          <div className="mt-3 p-3 bg-green-100 border border-green-200 rounded-lg text-sm">
                            <p className="text-green-800 font-medium flex items-center gap-2">
                              <CheckCircle className="w-4 h-4" /> {qualtricsSyncResult.message}
                            </p>
                          </div>
                        )}
                        
                        {qualtricsSyncError && !qualtricsSyncing && (
                          <div className="mt-3 p-3 bg-red-100 border border-red-200 rounded-lg text-sm">
                            <p className="text-red-800 font-medium flex items-center gap-2">
                              <AlertTriangle className="w-4 h-4" /> Error: {qualtricsSyncError}
                            </p>
                          </div>
                        )}
                      </div>
                      
                      {/* HUBSPOT TICKETS SYNC */}
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-amber-100 rounded-lg">
                              <MessageSquare className="w-6 h-6 text-amber-600" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-amber-900">HubSpot Tickets</h4>
                              <p className="text-sm text-amber-700">Sincroniza tickets de soporte desde HubSpot CRM</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              onClick={async () => {
                                setHubspotSyncing(true);
                                setHubspotSyncError(null);
                                setHubspotSyncResult(null);
                                const authToken = getAuthToken();
                                try {
                                  const response = await fetch(`${API_URL}/api/hubspot/sync`, {
                                    method: 'POST',
                                    headers: { 'Authorization': `Bearer ${authToken}` }
                                  });
                                  const data = await response.json();
                                  if (!response.ok) throw new Error(data.error || 'Error iniciando sincronización');
                                  
                                  const pollInterval = setInterval(async () => {
                                    try {
                                      const statusRes = await fetch(`${API_URL}/api/hubspot/sync/status`, {
                                        headers: { 'Authorization': `Bearer ${authToken}` }
                                      });
                                      const statusData = await statusRes.json();
                                      setHubspotSyncStatus(statusData);
                                      
                                      if (statusData.status === 'completed') {
                                        clearInterval(pollInterval);
                                        setHubspotSyncing(false);
                                        setHubspotSyncResult({ message: statusData.message, details: statusData.details });
                                      } else if (statusData.status === 'error' || statusData.status === 'stalled') {
                                        clearInterval(pollInterval);
                                        setHubspotSyncing(false);
                                        setHubspotSyncError(statusData.message);
                                      }
                                    } catch (pollError) {
                                      console.error('Error polling HubSpot status:', pollError);
                                    }
                                  }, 3000);
                                  
                                } catch (error) {
                                  setHubspotSyncing(false);
                                  setHubspotSyncError(error.message);
                                }
                              }}
                              disabled={hubspotSyncing}
                              className="bg-amber-600 hover:bg-amber-700 text-white gap-2"
                              data-testid="config-hubspot-sync-btn"
                            >
                              {hubspotSyncing ? (
                                <><Loader2 className="w-4 h-4 animate-spin" /> Sincronizando...</>
                              ) : (
                                <><RefreshCw className="w-4 h-4" /> Sincronizar Tickets</>
                              )}
                            </Button>
                            
                            {/* Botón Full Sync - SOLO SUPERADMIN */}
                            {authUser?.role === 'superadmin' && (
                              <Button
                                onClick={async () => {
                                  const confirmed = window.confirm(
                                    '⚠️ SINCRONIZACIÓN COMPLETA\n\n' +
                                    'Esto re-sincroniza ~27,000 tickets de los últimos 12 meses.\n' +
                                    'Puede tardar varios minutos (10-20 min).\n\n' +
                                    '¿Continuar?'
                                  );
                                  if (!confirmed) return;
                                  
                                  setHubspotSyncing(true);
                                  setHubspotSyncError(null);
                                  setHubspotSyncResult(null);
                                  const authToken = getAuthToken();
                                  try {
                                    const response = await fetch(`${API_URL}/api/hubspot/sync?force_full=true`, {
                                      method: 'POST',
                                      headers: { 'Authorization': `Bearer ${authToken}` }
                                    });
                                    const data = await response.json();
                                    if (!response.ok) throw new Error(data.error || 'Error iniciando sincronización completa');
                                    
                                    const pollInterval = setInterval(async () => {
                                      try {
                                        const statusRes = await fetch(`${API_URL}/api/hubspot/sync/status`, {
                                          headers: { 'Authorization': `Bearer ${authToken}` }
                                        });
                                        const statusData = await statusRes.json();
                                        setHubspotSyncStatus(statusData);
                                        
                                        if (statusData.status === 'completed') {
                                          clearInterval(pollInterval);
                                          setHubspotSyncing(false);
                                          setHubspotSyncResult({
                                            message: '✅ Sincronización COMPLETA finalizada - ' + (statusData.message || ''),
                                            details: statusData.details
                                          });
                                        } else if (statusData.status === 'error' || statusData.status === 'stalled') {
                                          clearInterval(pollInterval);
                                          setHubspotSyncing(false);
                                          setHubspotSyncError(statusData.message);
                                        }
                                      } catch (pollError) {
                                        console.error('Error polling HubSpot status:', pollError);
                                      }
                                    }, 3000);
                                    
                                  } catch (error) {
                                    setHubspotSyncing(false);
                                    setHubspotSyncError(error.message);
                                  }
                                }}
                                disabled={hubspotSyncing}
                                variant="outline"
                                className="border-red-300 text-red-700 hover:bg-red-50 hover:border-red-400 gap-2"
                                data-testid="config-hubspot-full-sync-btn"
                              >
                                <RefreshCw className="w-4 h-4" /> Full Sync (12 meses)
                              </Button>
                            )}
                          </div>
                        </div>
                        
                        {hubspotSyncing && hubspotSyncStatus && (
                          <div className="mt-3 p-4 bg-amber-100 border border-amber-300 rounded-lg">
                            <div className="flex items-center gap-3 mb-3">
                              <Loader2 className="w-5 h-5 animate-spin text-amber-600" />
                              <p className="text-amber-800 font-medium">{hubspotSyncStatus.message || 'Procesando...'}</p>
                            </div>
                            <div className="w-full bg-amber-200 rounded-full h-2">
                              <div className="bg-amber-600 h-2 rounded-full transition-all duration-500" style={{ width: `${hubspotSyncStatus.progress || 0}%` }} />
                            </div>
                          </div>
                        )}
                        
                        {hubspotSyncResult && !hubspotSyncing && (
                          <div className="mt-3 p-3 bg-green-100 border border-green-200 rounded-lg text-sm">
                            <p className="text-green-800 font-medium flex items-center gap-2">
                              <CheckCircle className="w-4 h-4" /> {hubspotSyncResult.message}
                            </p>
                          </div>
                        )}
                        
                        {hubspotSyncError && !hubspotSyncing && (
                          <div className="mt-3 p-3 bg-red-100 border border-red-200 rounded-lg text-sm">
                            <p className="text-red-800 font-medium flex items-center gap-2">
                              <AlertTriangle className="w-4 h-4" /> Error: {hubspotSyncError}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* ══════════════════════════════════════════════════════════════════════ */}
                    {/* SECCIÓN 2: MÁSTER DE SERVICIO */}
                    {/* ══════════════════════════════════════════════════════════════════════ */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2 pb-2 border-b border-slate-200">
                        <Settings className="w-5 h-5 text-indigo-600" />
                        Máster de Servicio
                      </h3>
                      
                      <div className="p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-indigo-100 rounded-lg">
                              <Settings className="w-6 h-6 text-indigo-600" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-indigo-900">Clasificación de Incidencias</h4>
                              <p className="text-sm text-indigo-700">FCR esperado, escalamiento y área destino por tipología</p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              onClick={async () => {
                                setMasterServicioLoading(true);
                                try {
                                  const authToken = getAuthToken();
                                  const res = await fetch(`${API_URL}/api/master-servicio?solo_activos=false`, {
                                    headers: { 'Authorization': `Bearer ${authToken}` }
                                  });
                                  const data = await res.json();
                                  setMasterServicio(data.items || []);
                                  setShowMasterServicio(true);
                                } catch (err) {
                                  toast.error('Error cargando Máster de Servicio');
                                }
                                setMasterServicioLoading(false);
                              }}
                              disabled={masterServicioLoading}
                              variant="outline"
                              className="border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                              data-testid="config-master-servicio-btn"
                            >
                              {masterServicioLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                              Ver/Editar Máster
                            </Button>
                          </div>
                        </div>
                        
                        <p className="text-xs text-indigo-600 mt-3">
                          <strong>Nota:</strong> Las métricas de FCR Real vs Bruto se muestran en la pestaña <strong>Atención → Resumen</strong>.
                        </p>
                      </div>
                    </div>
                    
                    {/* ══════════════════════════════════════════════════════════════════════ */}
                    {/* SECCIÓN 3: OBJETIVOS DE ATENCIÓN (FRT/TTR/FCR/SLA) - EDITOR REAL */}
                    {/* ══════════════════════════════════════════════════════════════════════ */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2 pb-2 border-b border-slate-200">
                        <Target className="w-5 h-5 text-blue-600" />
                        Objetivos de Atención
                      </h3>
                      
                      <div className="p-4 bg-white border border-slate-200 rounded-lg shadow-sm">
                        <div className="flex items-center justify-between mb-4">
                          <p className="text-sm text-slate-600">
                            Configurá los objetivos de <strong>FRT, TTR, FCR, Reapertura, CSAT y SLA</strong> para medir el desempeño del equipo de atención.
                          </p>
                          <div className="flex gap-2">
                            {!configObjectives && !configObjectivesLoading && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={loadConfigObjectives}
                                className="border-blue-300 text-blue-700 hover:bg-blue-50"
                              >
                                <RefreshCw className="w-4 h-4 mr-1" /> Cargar
                              </Button>
                            )}
                            {configObjectives && !configObjectivesEditing && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setConfigObjectivesEditing(true)}
                                className="border-blue-300 text-blue-700 hover:bg-blue-50"
                                data-testid="config-objectives-edit-btn"
                              >
                                <Edit className="w-4 h-4 mr-1" /> Editar
                              </Button>
                            )}
                            {configObjectivesEditing && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setConfigObjectivesEditing(false);
                                    loadConfigObjectives(); // Reload to discard changes
                                  }}
                                >
                                  Cancelar
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => saveConfigObjectives(configObjectives)}
                                  disabled={configObjectivesSaving}
                                  className="bg-blue-600 hover:bg-blue-700 text-white"
                                  data-testid="config-objectives-save-btn"
                                >
                                  {configObjectivesSaving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                                  Guardar
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                        
                        {configObjectivesLoading ? (
                          <div className="flex justify-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                          </div>
                        ) : configObjectives ? (
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                            {[
                              { key: 'frt_hours', label: 'FRT (horas)', desc: 'Tiempo 1ª respuesta', suffix: 'h' },
                              { key: 'ttr_hours', label: 'TTR (horas)', desc: 'Tiempo resolución', suffix: 'h' },
                              { key: 'fcr_percent', label: 'FCR (%)', desc: 'Resolución 1er contacto', suffix: '%' },
                              { key: 'reopen_percent', label: 'Reapertura (%)', desc: 'Tasa de reapertura', suffix: '%' },
                              { key: 'csat_percent', label: 'CSAT (%)', desc: 'Satisfacción', suffix: '%' },
                              { key: 'sla_percent', label: 'SLA (%)', desc: 'Cumplimiento SLA', suffix: '%' },
                            ].map(({ key, label, desc, suffix }) => (
                              <div key={key} className="space-y-1">
                                <label className="text-xs font-medium text-slate-500">{label}</label>
                                {configObjectivesEditing ? (
                                  <input
                                    type="number"
                                    value={configObjectives[key] || ''}
                                    onChange={(e) => setConfigObjectives(prev => ({ ...prev, [key]: parseFloat(e.target.value) || 0 }))}
                                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    data-testid={`config-objective-${key}`}
                                  />
                                ) : (
                                  <div className="text-xl font-bold text-slate-800">
                                    {configObjectives[key] || 'N/A'}{configObjectives[key] ? suffix : ''}
                                  </div>
                                )}
                                <p className="text-xs text-slate-400">{desc}</p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-6 text-slate-500 bg-slate-50 rounded-lg">
                            <Target className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p>Hacé clic en "Cargar" para ver los objetivos actuales.</p>
                          </div>
                        )}
                        
                        <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                          <strong>Nota:</strong> Los tiempos (FRT, TTR) son en horas CALENDARIO, no horas hábiles. Incluyen fines de semana y feriados.
                        </div>
                      </div>
                    </div>
                    
                    {/* ══════════════════════════════════════════════════════════════════════ */}
                    {/* SECCIÓN 4: FORECAST Y COMERCIAL - EDITOR REAL */}
                    {/* ══════════════════════════════════════════════════════════════════════ */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2 pb-2 border-b border-slate-200">
                        <TrendingUp className="w-5 h-5 text-emerald-600" />
                        Forecast y Comercial (2026)
                      </h3>
                      
                      <div className="p-4 bg-white border border-slate-200 rounded-lg shadow-sm">
                        <div className="flex items-center justify-between mb-4">
                          <p className="text-sm text-slate-600">
                            Configurá el <strong>Forecast mensual</strong> y <strong>Ticket Promedio USD</strong> por instancia para las proyecciones comerciales.
                          </p>
                          {!forecastConfig && !forecastConfigLoading && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => { loadForecastConfig(); loadTicketPromedioConfig(); }}
                              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                            >
                              <RefreshCw className="w-4 h-4 mr-1" /> Cargar
                            </Button>
                          )}
                          {forecastConfig && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => { loadForecastConfig(); loadTicketPromedioConfig(); }}
                              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                            >
                              <RefreshCw className="w-4 h-4 mr-1" /> Actualizar
                            </Button>
                          )}
                        </div>
                        
                        {(forecastConfigLoading || ticketPromedioLoading) ? (
                          <div className="flex justify-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
                          </div>
                        ) : forecastConfig ? (
                          <>
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b bg-slate-50">
                                    <th className="text-left p-3 font-semibold text-slate-700">Instancia</th>
                                    <th className="text-center p-3 font-semibold text-slate-700">Forecast Anual</th>
                                    <th className="text-center p-3 font-semibold text-slate-700">Ticket USD</th>
                                    <th className="text-center p-3 font-semibold text-slate-700">Facturación Est.</th>
                                    <th className="text-center p-3 font-semibold text-slate-700">Acciones</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {Object.entries(forecastConfig).map(([slug, data]) => (
                                    <tr key={slug} className="border-b hover:bg-slate-50">
                                      <td className="p-3">
                                        <div className="font-medium text-slate-800">{data.name || slug}</div>
                                        <div className="text-xs text-slate-400">{slug}</div>
                                      </td>
                                      <td className="p-3 text-center font-semibold text-emerald-700">
                                        {data.total?.toLocaleString() || 0}
                                      </td>
                                      <td className="p-3 text-center">
                                        <span className="font-semibold">${ticketPromedioConfig?.[slug] || 0}</span>
                                      </td>
                                      <td className="p-3 text-center text-slate-600">
                                        ${((data.total || 0) * (ticketPromedioConfig?.[slug] || 0)).toLocaleString()}
                                      </td>
                                      <td className="p-3 text-center">
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => openForecastEditor(slug, data, ticketPromedioConfig?.[slug], false)}
                                          className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                          data-testid={`config-forecast-edit-${slug}`}
                                        >
                                          <Edit className="w-4 h-4" />
                                        </Button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                                <tfoot>
                                  <tr className="bg-emerald-50 font-semibold">
                                    <td className="p-3 text-emerald-800">Subtotal Implementados</td>
                                    <td className="p-3 text-center text-emerald-800">
                                      {forecastTotals.implementados.toLocaleString()}
                                    </td>
                                    <td className="p-3 text-center text-emerald-800">—</td>
                                    <td className="p-3 text-center text-emerald-800">
                                      ${Object.entries(forecastConfig).reduce((sum, [slug, d]) => sum + ((d.total || 0) * (ticketPromedioConfig?.[slug] || 0)), 0).toLocaleString()}
                                    </td>
                                    <td className="p-3"></td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                            
                            {/* Sección de Partners con REAL MANUAL (Barcelona, Conmebol, Masterchef, Claeh) */}
                            {realManualConfig && Object.keys(realManualConfig).length > 0 && (
                              <div className="mt-6">
                                <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                                  <Users className="w-4 h-4 text-blue-500" />
                                  Partners con Real Manual (entran al % de avance)
                                </h4>
                                <div className="overflow-x-auto border border-blue-200 rounded-lg">
                                  <table className="w-full text-sm">
                                    <thead>
                                      <tr className="border-b bg-blue-50">
                                        <th className="text-left p-3 font-semibold text-slate-700">Partner</th>
                                        <th className="text-center p-3 font-semibold text-blue-700">Real Anual</th>
                                        <th className="text-center p-3 font-semibold text-slate-700">Forecast Anual</th>
                                        <th className="text-center p-3 font-semibold text-slate-700">Acciones</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {Object.entries(realManualConfig).map(([slug, data]) => (
                                        <tr key={slug} className="border-b hover:bg-blue-50/50">
                                          <td className="p-3">
                                            <div className="font-medium text-slate-800">{data.name || slug}</div>
                                            <div className="text-xs text-slate-400">{slug}</div>
                                          </td>
                                          <td className="p-3 text-center font-semibold text-blue-700">
                                            {data.real_total?.toLocaleString() || 0}
                                          </td>
                                          <td className="p-3 text-center font-semibold text-slate-600">
                                            {data.forecast_total?.toLocaleString() || 0}
                                          </td>
                                          <td className="p-3 text-center">
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => openForecastEditor(slug, data, 0, false, true)}
                                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                              data-testid={`config-real-manual-edit-${slug}`}
                                            >
                                              <Edit className="w-4 h-4" />
                                            </Button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                    <tfoot>
                                      <tr className="bg-blue-100 font-semibold">
                                        <td className="p-3 text-blue-800">Subtotal Real Manual</td>
                                        <td className="p-3 text-center text-blue-800">
                                          {Object.values(realManualConfig).reduce((sum, d) => sum + (d.real_total || 0), 0).toLocaleString()}
                                        </td>
                                        <td className="p-3 text-center text-blue-800">
                                          {forecastTotals.realManual.toLocaleString()}
                                        </td>
                                        <td className="p-3"></td>
                                      </tr>
                                    </tfoot>
                                  </table>
                                </div>
                                <p className="text-xs text-blue-600 mt-2">
                                  💡 Estos partners no tienen Canvas. Su NI real se carga a mano y entra al numerador del % de avance. Su forecast entra al denominador.
                                </p>
                              </div>
                            )}
                            
                            {/* Sección de Partners NO Implementados (Placeholders) */}
                            {forecastPlaceholders && Object.keys(forecastPlaceholders).length > 0 && (
                              <div className="mt-6">
                                <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                                  <Users className="w-4 h-4 text-amber-500" />
                                  Solo Forecast (no entran al % de avance)
                                </h4>
                                <div className="overflow-x-auto border border-amber-200 rounded-lg">
                                  <table className="w-full text-sm">
                                    <thead>
                                      <tr className="border-b bg-amber-50">
                                        <th className="text-left p-3 font-semibold text-slate-700">Partner</th>
                                        <th className="text-center p-3 font-semibold text-slate-700">Forecast Anual</th>
                                        <th className="text-center p-3 font-semibold text-slate-700">Acciones</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {Object.entries(forecastPlaceholders).map(([slug, data]) => (
                                        <tr key={slug} className="border-b hover:bg-amber-50/50">
                                          <td className="p-3">
                                            <div className="font-medium text-slate-800">{data.name || slug}</div>
                                            <div className="text-xs text-slate-400">{slug}</div>
                                          </td>
                                          <td className="p-3 text-center font-semibold text-amber-700">
                                            {data.total?.toLocaleString() || 0}
                                          </td>
                                          <td className="p-3 text-center">
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => openForecastEditor(slug, data, 0, true, false)}
                                              className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                                              data-testid={`config-placeholder-edit-${slug}`}
                                            >
                                              <Edit className="w-4 h-4" />
                                            </Button>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                    <tfoot>
                                      <tr className="bg-amber-100 font-semibold">
                                        <td className="p-3 text-amber-800">Subtotal Solo Forecast</td>
                                        <td className="p-3 text-center text-amber-800">
                                          {forecastTotals.placeholders.toLocaleString()}
                                        </td>
                                        <td className="p-3"></td>
                                      </tr>
                                    </tfoot>
                                  </table>
                                </div>
                                <p className="text-xs text-amber-600 mt-2">
                                  ⚠️ Estos partners solo tienen forecast. No tienen NI real cargado, así que NO entran al % de avance.
                                </p>
                              </div>
                            )}
                            
                            {/* Total Empresa */}
                            <div className="mt-4 p-4 bg-gradient-to-r from-emerald-50 via-blue-50 to-amber-50 rounded-lg border border-emerald-200">
                              <div className="flex justify-between items-center">
                                <span className="font-semibold text-slate-700">FORECAST TOTAL EMPRESA 2026</span>
                                <span className="text-2xl font-bold text-emerald-700">
                                  {forecastTotals.empresa.toLocaleString()} NI
                                </span>
                              </div>
                              <p className="text-xs text-slate-500 mt-1">
                                = {forecastTotals.implementados.toLocaleString()} (Canvas/HubSpot) + {forecastTotals.realManual.toLocaleString()} (real manual) + {forecastTotals.placeholders.toLocaleString()} (solo forecast)
                              </p>
                              <p className="text-xs text-emerald-600 mt-1">
                                📊 % Avance usa: ({forecastTotals.implementados.toLocaleString()} + {forecastTotals.realManual.toLocaleString()}) = {(forecastTotals.implementados + forecastTotals.realManual).toLocaleString()} como denominador
                              </p>
                            </div>
                            
                            {/* Modal de edición de forecast */}
                            {editingForecastSlug && (
                              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                                <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-auto">
                                  <div className="p-6 border-b flex items-center justify-between">
                                    <h3 className="text-lg font-semibold flex items-center gap-2">
                                      <TrendingUp className={`w-5 h-5 ${editingForecastIsRealManual ? 'text-blue-600' : editingForecastIsPlaceholder ? 'text-amber-600' : 'text-emerald-600'}`} />
                                      Editar: {editingForecastValues.name || editingForecastSlug}
                                      {editingForecastIsRealManual && (
                                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Real Manual</span>
                                      )}
                                      {editingForecastIsPlaceholder && (
                                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Solo Forecast</span>
                                      )}
                                    </h3>
                                    <Button variant="ghost" size="sm" onClick={() => setEditingForecastSlug(null)}>
                                      <X className="w-4 h-4" />
                                    </Button>
                                  </div>
                                  <div className="p-6 space-y-6">
                                    {/* Nombre (para placeholders y real_manual) */}
                                    {(editingForecastIsPlaceholder || editingForecastIsRealManual) && (
                                      <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">
                                          Nombre del Partner
                                        </label>
                                        <input
                                          type="text"
                                          value={editingForecastValues.name || ''}
                                          onChange={(e) => setEditingForecastValues(prev => ({ ...prev, name: e.target.value }))}
                                          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                          data-testid="config-name-input"
                                        />
                                      </div>
                                    )}
                                    
                                    {/* NI Real Mensual (solo para real_manual) */}
                                    {editingForecastIsRealManual && (
                                      <div>
                                        <label className="block text-sm font-medium text-blue-700 mb-2">
                                          NI Real Mensual 2026 (cargado a mano)
                                        </label>
                                        <div className="grid grid-cols-4 md:grid-cols-6 gap-3">
                                          {['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'].map((mes, i) => (
                                            <div key={mes}>
                                              <label className="text-xs text-blue-500">{mes}</label>
                                              <input
                                                type="number"
                                                min="0"
                                                value={editingForecastValues.real_monthly?.[i] || 0}
                                                onChange={(e) => {
                                                  const newReal = [...(editingForecastValues.real_monthly || Array(12).fill(0))];
                                                  newReal[i] = parseInt(e.target.value) || 0;
                                                  setEditingForecastValues(prev => ({ ...prev, real_monthly: newReal }));
                                                }}
                                                className="w-full px-2 py-1 border border-blue-300 rounded text-sm text-center focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-blue-50"
                                                data-testid={`config-real-month-${i}`}
                                              />
                                            </div>
                                          ))}
                                        </div>
                                        <p className="text-xs text-blue-600 mt-2">
                                          <strong>Total Real:</strong> {(editingForecastValues.real_monthly || []).reduce((a, b) => a + b, 0).toLocaleString()} inscripciones
                                        </p>
                                      </div>
                                    )}
                                    
                                    {/* Ticket Promedio (solo para implementados Canvas/HubSpot) */}
                                    {!editingForecastIsPlaceholder && !editingForecastIsRealManual && (
                                      <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-2">
                                          Ticket Promedio USD
                                        </label>
                                        <input
                                          type="number"
                                          min="0"
                                          max="500"
                                          value={editingForecastValues.ticket}
                                          onChange={(e) => setEditingForecastValues(prev => ({ ...prev, ticket: parseInt(e.target.value) || 0 }))}
                                          className="w-32 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                          data-testid="config-forecast-ticket-input"
                                        />
                                        <p className="text-xs text-slate-500 mt-1">Valor en USD para calcular facturación estimada</p>
                                      </div>
                                    )}
                                    
                                    {/* Forecast Mensual */}
                                    <div>
                                      <label className="block text-sm font-medium text-slate-700 mb-2">
                                        Forecast Mensual 2026 (Nuevas Inscripciones)
                                      </label>
                                      <div className="grid grid-cols-4 md:grid-cols-6 gap-3">
                                        {['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'].map((mes, i) => (
                                          <div key={mes}>
                                            <label className="text-xs text-slate-500">{mes}</label>
                                            <input
                                              type="number"
                                              min="0"
                                              value={editingForecastValues.monthly[i] || 0}
                                              onChange={(e) => {
                                                const newMonthly = [...editingForecastValues.monthly];
                                                newMonthly[i] = parseInt(e.target.value) || 0;
                                                setEditingForecastValues(prev => ({ ...prev, monthly: newMonthly }));
                                              }}
                                              className={`w-full px-2 py-1 border border-slate-300 rounded text-sm text-center focus:ring-2 ${editingForecastIsRealManual ? 'focus:ring-blue-500 focus:border-blue-500' : editingForecastIsPlaceholder ? 'focus:ring-amber-500 focus:border-amber-500' : 'focus:ring-emerald-500 focus:border-emerald-500'}`}
                                              data-testid={`config-forecast-month-${i}`}
                                            />
                                          </div>
                                        ))}
                                      </div>
                                      <p className="text-xs text-slate-500 mt-2">
                                        <strong>Total Forecast:</strong> {editingForecastValues.monthly.reduce((a, b) => a + b, 0).toLocaleString()} inscripciones
                                      </p>
                                    </div>
                                  </div>
                                  <div className="p-6 border-t bg-slate-50 flex justify-end gap-3">
                                    <Button variant="ghost" onClick={() => setEditingForecastSlug(null)}>
                                      Cancelar
                                    </Button>
                                    <Button
                                      onClick={saveForecastAndTicket}
                                      disabled={savingForecast}
                                      className={editingForecastIsRealManual ? "bg-blue-600 hover:bg-blue-700 text-white" : editingForecastIsPlaceholder ? "bg-amber-600 hover:bg-amber-700 text-white" : "bg-emerald-600 hover:bg-emerald-700 text-white"}
                                      data-testid="config-forecast-save-btn"
                                    >
                                      {savingForecast ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                                      Guardar
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="text-center py-6 text-slate-500 bg-slate-50 rounded-lg">
                            <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-50" />
                            <p>Hacé clic en "Cargar" para ver la configuración actual.</p>
                          </div>
                        )}
                        
                        <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-700">
                          <strong>Nota:</strong> Estas configuraciones afectan las proyecciones en la pestaña Enrolados. El forecast considera 12 meses (Ene→Dic 2026).
                        </div>
                      </div>
                    </div>
                    
                    {/* ══════════════════════════════════════════════════════════════════════ */}
                    {/* SECCIÓN 5: CARGAS COMPLEMENTARIAS */}
                    {/* ══════════════════════════════════════════════════════════════════════ */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2 pb-2 border-b border-slate-200">
                        <FileSpreadsheet className="w-5 h-5 text-teal-600" />
                        Cargas Complementarias
                      </h3>
                      
                      {/* Slot 7: Base de Enrolados */}
                      <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="p-2 bg-teal-100 rounded-lg">
                            <Users className="w-5 h-5 text-teal-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-teal-900">Base de Enrolados (Areandina)</h4>
                            <p className="text-sm text-teal-700">CSV con fechas oficiales de inicio y programa inscripto</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <FileDropzone
                            label="Base de Enrolados.csv"
                            accept=".csv,.xlsx"
                            file={uploadFiles.baseEnrolados}
                            onFileSelect={(f) => setUploadFiles(prev => ({ ...prev, baseEnrolados: f }))}
                          />
                          <Button
                            onClick={async () => {
                              if (!uploadFiles.baseEnrolados) {
                                toast.error('Primero selecciona un archivo CSV');
                                return;
                              }
                              setBaseEnroladosSyncing(true);
                              setBaseEnroladosError(null);
                              setBaseEnroladosResult(null);
                              const authToken = getAuthToken();
                              
                              try {
                                const formData = new FormData();
                                formData.append('file', uploadFiles.baseEnrolados);
                                
                                const response = await fetch(`${API_URL}/api/partners/${selectedPartnerId}/update-base-enrolados`, {
                                  method: 'POST',
                                  headers: { 'Authorization': `Bearer ${authToken}` },
                                  body: formData
                                });
                                const data = await response.json();
                                if (!response.ok) throw new Error(data.error || 'Error iniciando actualización');
                                
                                const processId = data.process_id;
                                const pollInterval = setInterval(async () => {
                                  try {
                                    const statusRes = await fetch(`${API_URL}/api/partners/${selectedPartnerId}/update-base-enrolados/status?process_id=${processId}`, {
                                      headers: { 'Authorization': `Bearer ${authToken}` }
                                    });
                                    const statusData = await statusRes.json();
                                    setBaseEnroladosStatus(statusData);
                                    
                                    if (statusData.status === 'completed') {
                                      clearInterval(pollInterval);
                                      setBaseEnroladosSyncing(false);
                                      setBaseEnroladosResult({ message: statusData.message, details: statusData.details });
                                      toast.success('Base de enrolados actualizada');
                                    } else if (statusData.status === 'error') {
                                      clearInterval(pollInterval);
                                      setBaseEnroladosSyncing(false);
                                      setBaseEnroladosError(statusData.message);
                                    }
                                  } catch (pollError) {
                                    console.error('Error polling status:', pollError);
                                  }
                                }, 2000);
                              } catch (error) {
                                setBaseEnroladosSyncing(false);
                                setBaseEnroladosError(error.message);
                              }
                            }}
                            disabled={baseEnroladosSyncing || !uploadFiles.baseEnrolados || !selectedPartnerId}
                            className="bg-teal-600 hover:bg-teal-700 text-white gap-2"
                            data-testid="config-upload-enrolados-btn"
                          >
                            {baseEnroladosSyncing ? (
                              <><Loader2 className="w-4 h-4 animate-spin" /> Procesando...</>
                            ) : (
                              <><Upload className="w-4 h-4" /> Actualizar Enrolados</>
                            )}
                          </Button>
                        </div>
                        
                        {baseEnroladosSyncing && baseEnroladosStatus && (
                          <div className="mt-3 p-3 bg-teal-100 border border-teal-300 rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                              <Loader2 className="w-4 h-4 animate-spin text-teal-600" />
                              <span className="text-teal-800 text-sm">{baseEnroladosStatus.message || 'Procesando...'}</span>
                            </div>
                            <div className="w-full bg-teal-200 rounded-full h-2">
                              <div className="bg-teal-600 h-2 rounded-full transition-all" style={{ width: `${baseEnroladosStatus.progress || 0}%` }} />
                            </div>
                          </div>
                        )}
                        
                        {baseEnroladosResult && !baseEnroladosSyncing && (
                          <div className="mt-3 p-3 bg-green-100 border border-green-200 rounded-lg text-sm">
                            <p className="text-green-800 font-medium flex items-center gap-2">
                              <CheckCircle className="w-4 h-4" /> {baseEnroladosResult.message}
                            </p>
                          </div>
                        )}
                        
                        {baseEnroladosError && !baseEnroladosSyncing && (
                          <div className="mt-3 p-3 bg-red-100 border border-red-200 rounded-lg text-sm">
                            <p className="text-red-800 font-medium flex items-center gap-2">
                              <AlertTriangle className="w-4 h-4" /> {baseEnroladosError}
                            </p>
                          </div>
                        )}
                        
                        <p className="text-xs text-teal-600 mt-3">
                          <strong>Columnas requeridas:</strong> Email, Mes de inicio (ej: "16 de marzo 2020"), Programa Inscripto (opcional)
                        </p>
                      </div>
                      
                      {/* Slot 8: Geolocalización CCF */}
                      {selectedPartner?.slug === 'cordoba-crea-futuro' && (
                        <div className="p-4 bg-cyan-50 border border-cyan-200 rounded-lg">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="p-2 bg-cyan-100 rounded-lg">
                              <MapPin className="w-5 h-5 text-cyan-600" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-cyan-900">Geolocalización (Solo CCF)</h4>
                              <p className="text-sm text-cyan-700">Excel con correo y localidad para la pestaña Geografía</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-4">
                            <FileDropzone
                              label="Geolocalización.xlsx"
                              accept=".xlsx"
                              file={uploadFiles.geolocalizacion}
                              onFileSelect={(f) => setUploadFiles(prev => ({ ...prev, geolocalizacion: f }))}
                            />
                            <Button
                              onClick={handleUploadGeo}
                              disabled={processingGeo || !uploadFiles.geolocalizacion}
                              className="bg-cyan-600 hover:bg-cyan-700 text-white gap-2"
                              data-testid="config-upload-geo-btn"
                            >
                              {processingGeo ? (
                                <><Loader2 className="w-4 h-4 animate-spin" /> Procesando...</>
                              ) : (
                                <><Upload className="w-4 h-4" /> Subir Geolocalización</>
                              )}
                            </Button>
                          </div>
                          
                          {geoUploadResult && (
                            <p className="text-sm text-cyan-700 mt-3">
                              ✓ {geoUploadResult.matches_encontrados} alumnos actualizados de {geoUploadResult.emails_validos} en el archivo
                            </p>
                          )}
                          
                          <p className="text-xs text-cyan-600 mt-3">
                            <strong>Columnas requeridas:</strong> Correo, Localidad
                          </p>
                        </div>
                      )}
                    </div>
                    
                    {/* ══════════════════════════════════════════════════════════════════════ */}
                    {/* SECCIÓN 6: HISTORIAL DE IMPORTACIONES */}
                    {/* ══════════════════════════════════════════════════════════════════════ */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2 pb-2 border-b border-slate-200">
                        <History className="w-5 h-5 text-slate-600" />
                        Historial de Importaciones
                      </h3>
                      
                      <div className="flex items-center justify-end mb-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={loadBatches}
                          disabled={loadingBatches}
                        >
                          <RefreshCw className={`w-4 h-4 mr-1 ${loadingBatches ? 'animate-spin' : ''}`} />
                          Actualizar
                        </Button>
                      </div>
                      
                      {loadingBatches ? (
                        <div className="flex justify-center py-8">
                          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                        </div>
                      ) : batches.length === 0 ? (
                        <div className="text-center py-6 text-slate-500 bg-slate-50 rounded-lg">
                          <History className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p>No hay importaciones registradas para este partner.</p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b bg-slate-50">
                                <th className="text-left p-3 font-semibold text-slate-700">Fecha y Hora</th>
                                <th className="text-center p-3 font-semibold text-slate-700">Alumnos</th>
                                <th className="text-center p-3 font-semibold text-slate-700">Semáforo</th>
                                <th className="text-center p-3 font-semibold text-slate-700">Retención</th>
                                <th className="text-center p-3 font-semibold text-slate-700">Acción</th>
                              </tr>
                            </thead>
                            <tbody>
                              {batches.map((batch) => (
                                <tr key={batch.id} className="border-b hover:bg-slate-50">
                                  <td className="p-3">
                                    <div className="font-medium">{new Date(batch.timestamp).toLocaleDateString('es-ES', { timeZone: 'UTC' })}</div>
                                    <div className="text-slate-500 text-xs">{new Date(batch.timestamp).toLocaleTimeString('es-ES')}</div>
                                  </td>
                                  <td className="p-3 text-center font-semibold">{batch.student_count}</td>
                                  <td className="p-3">
                                    {batch.distribution ? (
                                      <div className="flex items-center justify-center gap-1 text-xs">
                                        {batch.distribution.black > 0 && <span className="px-1.5 py-0.5 bg-gray-800 text-white rounded">⚫{batch.distribution.black}</span>}
                                        {batch.distribution.red > 0 && <span className="px-1.5 py-0.5 bg-red-100 text-red-700 rounded">🔴{batch.distribution.red}</span>}
                                        {batch.distribution.orange > 0 && <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded">🟠{batch.distribution.orange}</span>}
                                        {batch.distribution.blue > 0 && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">🔵{batch.distribution.blue}</span>}
                                        {batch.distribution.green > 0 && <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded">🟢{batch.distribution.green}</span>}
                                      </div>
                                    ) : <span className="text-slate-400 text-xs">—</span>}
                                  </td>
                                  <td className="p-3 text-center">
                                    {batch.kpis?.completion_rate !== undefined ? (
                                      <span className="font-semibold text-emerald-600">{batch.kpis.completion_rate?.toFixed(1)}%</span>
                                    ) : <span className="text-slate-400">—</span>}
                                  </td>
                                  <td className="p-3 text-center">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDeleteBatch(batch.id)}
                                      disabled={deletingBatchId === batch.id}
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    >
                                      {deletingBatchId === batch.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      
                      {/* Historial NPS */}
                      {npsBatches.length > 0 && (
                        <div className="mt-4 pt-4 border-t">
                          <h4 className="font-semibold text-slate-700 flex items-center gap-2 mb-3">
                            <MessageSquare className="w-4 h-4 text-purple-600" />
                            Historial NPS
                          </h4>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b bg-purple-50">
                                  <th className="text-left p-2">Fecha</th>
                                  <th className="text-left p-2">Archivo</th>
                                  <th className="text-center p-2">Respuestas</th>
                                  <th className="text-center p-2">Vinculadas</th>
                                  <th className="text-center p-2">Acciones</th>
                                </tr>
                              </thead>
                              <tbody>
                                {npsBatches.map((batch) => (
                                  <tr key={batch.id} className="border-b hover:bg-purple-50/50">
                                    <td className="p-2 font-mono text-xs">{new Date(batch.timestamp).toLocaleString("es-ES")}</td>
                                    <td className="p-2 text-slate-600 max-w-[150px] truncate">{batch.filename || "—"}</td>
                                    <td className="p-2 text-center"><Badge variant="outline">{batch.responses_count || 0}</Badge></td>
                                    <td className="p-2 text-center"><Badge variant="outline" className="bg-green-50 text-green-700">{batch.responses_linked}</Badge></td>
                                    <td className="p-2 text-center">
                                      <Button variant="ghost" size="sm" onClick={() => handleDeleteNpsBatch(batch.id)} className="text-red-500 hover:text-red-700">
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* ══════════════════════════════════════════════════════════════════════ */}
                    {/* SECCIÓN 7: INFO DEL SISTEMA (solo lectura) */}
                    {/* ══════════════════════════════════════════════════════════════════════ */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2 pb-2 border-b border-slate-200">
                        <Info className="w-5 h-5 text-slate-600" />
                        Información del Sistema
                      </h3>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                          <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Usuario Activo</p>
                          <p className="font-medium text-slate-800">{authUser?.email || 'N/A'}</p>
                          <Badge className="mt-1" variant="secondary">{authUser?.role || 'N/A'}</Badge>
                        </div>
                        <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                          <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Partner Seleccionado</p>
                          <p className="font-medium text-slate-800">{selectedPartner?.name || 'Ninguno'}</p>
                          <p className="text-xs text-slate-500 mt-1">{selectedPartner?.slug || ''}</p>
                        </div>
                      </div>
                      
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-xs text-blue-500 uppercase tracking-wider mb-1">Versión del Sistema</p>
                        <p className="font-mono text-sm text-blue-800">{BUILD_VERSION}</p>
                      </div>
                    </div>
                    
                    {/* ══════════════════════════════════════════════════════════════════════ */}
                    {/* SECCIÓN: BACKUP DE SEGURIDAD - SOLO SUPERADMIN */}
                    {/* ══════════════════════════════════════════════════════════════════════ */}
                    {authUser?.role === 'superadmin' && (
                      <div className="space-y-4 mt-6">
                        <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2 pb-2 border-b border-slate-200">
                          <Database className="w-5 h-5 text-amber-600" />
                          Backup de Seguridad
                        </h3>
                        
                        <div className={`p-4 border rounded-lg ${
                          backupStatus?.status === 'green' 
                            ? 'bg-emerald-50 border-emerald-200' 
                            : 'bg-amber-50 border-amber-200'
                        }`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-lg ${
                                backupStatus?.status === 'green' 
                                  ? 'bg-emerald-100' 
                                  : 'bg-amber-100'
                              }`}>
                                {backupStatus?.status === 'green' 
                                  ? <CheckCircle className="w-6 h-6 text-emerald-600" />
                                  : <AlertTriangle className="w-6 h-6 text-amber-600" />
                                }
                              </div>
                              <div>
                                <h4 className={`font-semibold ${
                                  backupStatus?.status === 'green' 
                                    ? 'text-emerald-900' 
                                    : 'text-amber-900'
                                }`}>
                                  {backupStatus?.message || 'Cargando estado del backup...'}
                                </h4>
                                <p className={`text-sm ${
                                  backupStatus?.status === 'green' 
                                    ? 'text-emerald-700' 
                                    : 'text-amber-700'
                                }`}>
                                  {backupStatus?.downloaded_by 
                                    ? `Descargado por: ${backupStatus.downloaded_by}`
                                    : 'Descargue un backup para proteger los datos del sistema'
                                  }
                                </p>
                              </div>
                            </div>
                            
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={async () => {
                                  setBackupLoading(true);
                                  try {
                                    const authToken = getAuthToken();
                                    const res = await fetch(`${API_URL}/api/admin/backup/status`, {
                                      headers: { 'Authorization': `Bearer ${authToken}` }
                                    });
                                    const data = await res.json();
                                    setBackupStatus(data);
                                  } catch (err) {
                                    toast.error('Error verificando estado del backup');
                                  }
                                  setBackupLoading(false);
                                }}
                                disabled={backupLoading}
                                className="border-slate-300"
                                data-testid="config-backup-status-btn"
                              >
                                {backupLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                              </Button>
                              
                              <Button
                                onClick={async () => {
                                  setBackupDownloading(true);
                                  try {
                                    const url = `${API_URL}/api/admin/backup`;
                                    await secureExportDownload({
                                      url,
                                      method: 'POST',
                                      filename: `persistencia360_backup_${new Date().toISOString().slice(0,10)}.zip`,
                                      disclaimerText: 'Estás por descargar la BASE DE DATOS COMPLETA (todos los alumnos y todos los partners) - es el dato más sensible del sistema. Uso ULTRA restringido, solo superadmin. Mantenela siempre cifrada, no la compartas por canales no autorizados y borrala en cuanto termines de usarla. Esta descarga queda registrada (usuario, fecha, tamaño).',
                                    });
                                    toast.success('Backup descargado correctamente');
                                    
                                    // Actualizar estado
                                    const statusRes = await fetch(`${API_URL}/api/admin/backup/status`, {
                                      headers: { 'Authorization': `Bearer ${getAuthToken()}` }
                                    });
                                    const statusData = await statusRes.json();
                                    setBackupStatus(statusData);
                                    
                                  } catch (err) {
                                    if (err.message !== 'cancelado') {
                                      toast.error(err.message || 'Error descargando backup');
                                    }
                                  }
                                  setBackupDownloading(false);
                                }}
                                disabled={backupDownloading}
                                className="bg-amber-600 hover:bg-amber-700 text-white gap-2"
                                data-testid="config-backup-download-btn"
                              >
                                {backupDownloading 
                                  ? <Loader2 className="w-4 h-4 animate-spin" /> 
                                  : <Download className="w-4 h-4" />
                                }
                                Descargar Backup
                              </Button>
                            </div>
                          </div>
                          
                          <p className="text-xs text-slate-600 mt-3">
                            <strong>Importante:</strong> El backup contiene datos personales de alumnos. 
                            Almacénelo de forma segura y elimínelo cuando ya no lo necesite.
                          </p>
                        </div>
                      </div>
                    )}
                    
                  </CardContent>
                </Card>
              </TabsContent>
            )}
            
            {/* TAB 2: DASHBOARD */}
            <TabsContent value="dashboard">
              <div className="space-y-6">
                
                {/* Botón para ver Preview del PDF (temporal para mockup) - OCULTO TEMPORALMENTE
                <div className="flex justify-end">
                  <Button
                    onClick={() => setShowPDFPreview(true)}
                    className="bg-green-600 hover:bg-green-700 gap-2"
                  >
                    <FileDown className="w-4 h-4" />
                    Ver Informe PDF (Preview)
                  </Button>
                </div>
                */}
                
                {/* ══════════════════════════════════════════════════════════════════════ */}
                {/* VISTA COMERCIAL: NUEVOS ENROLADOS POR PERÍODO */}
                {/* ══════════════════════════════════════════════════════════════════════ */}
                <Card className="border-2 border-teal-300 bg-gradient-to-br from-teal-50/50 to-cyan-50/30" data-testid="commercial-card">
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-2 mb-4">
                      <TrendingUp className="w-5 h-5 text-green-600" />
                      <h3 className="font-semibold text-slate-800 text-lg">Vista Comercial</h3>
                      <Badge className="bg-green-100 text-green-700 text-xs">
                        Nuevos Enrolados
                      </Badge>
                    </div>
                    
                    {/* Gráfico de Barras - Nuevos Enrolados por Mes */}
                    {commercialLoading ? (
                      <div className="flex justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-green-500" />
                      </div>
                    ) : commercialData?.por_mes && commercialData.por_mes.length > 0 ? (
                      (() => {
                        const currentSlug = partners.find(p => p.id === selectedPartnerId)?.slug;
                        return currentSlug === 'areandina';
                      })() ? (
                        // Gráfico específico para Areandina - datos limpios del backend
                        (() => {
                          const areandina_chartData = (commercialData?.por_mes || [])
                            .sort((a, b) => a.mes.localeCompare(b.mes))
                            .map(item => ({
                              name: item.mes_display,
                              value: item.alumnos_unicos
                            }));
                          return (
                            <ResponsiveContainer width="100%" height={220}>
                              <BarChart 
                                data={areandina_chartData}
                                margin={{ top: 10, right: 20, left: 10, bottom: 20 }}
                              >
                                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                <XAxis 
                                  dataKey="name" 
                                  tick={{ fontSize: 11, fill: '#64748b' }}
                                />
                                <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                                <RechartsTooltip 
                                  contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                                  formatter={(value) => [value, 'Nuevos Enrolados']}
                                />
                                <Bar dataKey="value" fill="#16a34a" radius={[4, 4, 0, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          );
                        })()
                      ) : (
                        // Gráfico para otros partners
                        <>
                        <ResponsiveContainer width="100%" height={220}>
                          <ComposedChart 
                            data={(() => {
                              const currentSlug = partners.find(p => p.id === selectedPartnerId)?.slug;
                              const isCCF = currentSlug === 'cordoba-crea-futuro';
                              // Filtrar y ordenar datos
                              let datos = commercialData.por_mes
                                .filter(item => {
                                  // Filtrar meses futuros - EXCEPTO CCF, que desde Etapa 2
                                  // tiene cohortes futuras reales ya enroladas (ej 2027-01),
                                  // no son un error de datos.
                                  if (isCCF) return true;
                                  const [year, month] = item.mes.split('-').map(Number);
                                  const itemDate = new Date(year, month - 1, 1);
                                  return itemDate <= new Date();
                                })
                                .sort((a, b) => a.mes.localeCompare(b.mes));  // Ordenar cronológicamente
                              
                              // Formatear labels
                              return datos.map(item => ({
                                ...item,
                                mesLabel: (() => {
                                  const [year, month] = item.mes.split('-');
                                  const months = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
                                  return `${months[parseInt(month)-1]} ${year.slice(2)}`;
                                })()
                              }));
                            })()}
                            margin={{ top: 10, right: 20, left: 10, bottom: 20 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis 
                              dataKey="mesLabel" 
                              tick={{ fontSize: 11, fill: '#64748b' }}
                            />
                            <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                            <RechartsTooltip 
                              contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px' }}
                              formatter={(value) => [value, 'Nuevos Enrolados']}
                            />
                            <Bar dataKey="alumnos_unicos" fill="#16a34a" radius={[4, 4, 0, 0]} />
                          </ComposedChart>
                        </ResponsiveContainer>
                        {commercialData.historico_sin_periodo > 0 && (
                          <div className="mt-4 pt-4 border-t border-teal-200" data-testid="ccf-sin-periodo-block">
                            <div className="flex items-baseline justify-between mb-1">
                              <h4 className="text-sm font-semibold text-slate-700">
                                Por sección (cohortes sin fecha)
                              </h4>
                              <span className="text-xs text-slate-500" data-testid="ccf-sin-periodo-total">
                                {commercialData.historico_sin_periodo.toLocaleString('es-AR')} alumnos únicos
                              </span>
                            </div>
                            <p className="text-xs text-slate-500 mb-3">
                              Etapa 1 - secciones sin token de fecha en el nombre (ej. COR001). No tienen mes
                              de cohorte real, así que se muestran por sección en vez de inventar una fecha.
                            </p>
                            {commercialSinPeriodoSections.length > 0 ? (
                              <>
                              <div className="space-y-1.5">
                                {(() => {
                                  const maxCount = Math.max(...commercialSinPeriodoSections.map(s => s.student_count), 1);
                                  return commercialSinPeriodoSections.map((s) => (
                                    <div key={s.name} className="flex items-center gap-2" data-testid={`ccf-seccion-bar-${s.name}`}>
                                      <span className="text-xs text-slate-600 w-28 shrink-0 truncate" title={s.name}>{s.name}</span>
                                      <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
                                        <div
                                          className="h-full bg-teal-500/80 rounded-full"
                                          style={{ width: `${Math.max(1.5, (s.student_count / maxCount) * 100)}%` }}
                                        />
                                      </div>
                                      <span className="text-xs font-medium text-slate-700 w-14 text-right shrink-0">
                                        {s.student_count.toLocaleString('es-AR')}
                                      </span>
                                    </div>
                                  ));
                                })()}
                              </div>
                              {(() => {
                                const sumaBarras = commercialSinPeriodoSections.reduce((acc, s) => acc + s.student_count, 0);
                                const diff = sumaBarras - commercialData.historico_sin_periodo;
                                return diff > 0 ? (
                                  <p className="text-xs text-amber-600 mt-2 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="ccf-solapamiento-nota">
                                    ⚠ Las barras suman {sumaBarras.toLocaleString('es-AR')} alumnos, {diff.toLocaleString('es-AR')} más
                                    que el total único ({commercialData.historico_sin_periodo.toLocaleString('es-AR')}) porque {diff.toLocaleString('es-AR')} casos
                                    corresponden a alumnos anotados en más de una sección sin fecha a la vez (se cuentan en cada
                                    sección donde están, pero una sola vez en el total). El total único es el que suma con los meses.
                                  </p>
                                ) : null;
                              })()}
                              </>
                            ) : (
                              <p className="text-xs text-slate-400">
                                + {commercialData.historico_sin_periodo.toLocaleString('es-AR')} alumnos (detalle por sección no disponible)
                              </p>
                            )}
                            <p className="text-xs text-slate-400 mt-2" data-testid="ccf-total-reconciliacion">
                              Meses ({(commercialData.por_mes || []).reduce((acc, m) => acc + m.alumnos_unicos, 0).toLocaleString('es-AR')})
                              {' + '}secciones, total único ({commercialData.historico_sin_periodo.toLocaleString('es-AR')})
                              {' = '}
                              {((commercialData.por_mes || []).reduce((acc, m) => acc + m.alumnos_unicos, 0) + commercialData.historico_sin_periodo).toLocaleString('es-AR')} alumnos
                            </p>
                          </div>
                        )}
                        </>
                      )
                    ) : (
                      <div className="p-6 bg-amber-50 border border-amber-200 rounded-lg text-center">
                        <p className="text-sm text-amber-700">
                          <strong>Sin datos de cohorte:</strong> Para ver métricas de nuevos enrolados, 
                          sube el archivo <code className="bg-amber-100 px-1 rounded">Provisioning.zip</code> en "Carga de Datos".
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
                
                {/* NPS Score removido de Academia — usar pestaña NPS 360 (integración Qualtrics) */}
                
                {/* ══════════════════════════════════════════════════════════════════════ */}
                {/* SECCIÓN 2B: LICENCIAS LMS */}
                {/* ══════════════════════════════════════════════════════════════════════ */}
                <Card className="bg-white border border-slate-200 rounded-xl">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-4">
                      <div className="w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold bg-indigo-100 text-indigo-700">
                        {formatNumber(dashboard?.licencias_lms || 0)}
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-slate-800">Licencias LMS</h3>
                        <p className="text-sm text-slate-500">Usuarios únicos desde 01/01/{new Date().getFullYear()}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {/* ══════════════════════════════════════════════════════════════════════ */}
                {/* ACADEMIA POR PERÍODO: Volumen + Semáforo por mes de inscripción */}
                {/* ══════════════════════════════════════════════════════════════════════ */}
                {!showGlobalDashboard && selectedPartnerId && (
                  <AcademiaPorPeriodo partnerId={selectedPartnerId} token={getAuthToken()} />
                )}
                
                {/* ══════════════════════════════════════════════════════════════════════ */}
                {/* SECCIÓN 3: POBLACIÓN CÓRDOBA CREA FUTURO - Solo CCF */}
                {/* ══════════════════════════════════════════════════════════════════════ */}
                {dashboard?.is_ccf && dashboard?.metricas_admin && (
                  <Card className="bg-white border border-slate-200 rounded-xl">
                    <CardContent className="p-5">
                      <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                        <BarChart2 className="w-5 h-5 text-purple-600" />
                        Población Córdoba Crea Futuro
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div className="text-center p-4 bg-purple-50 rounded-lg border border-purple-100">
                          <div className="text-2xl font-bold text-purple-700">{formatNumber(dashboard.metricas_admin.total_becas)}</div>
                          <div className="text-xs text-purple-600 mt-1">Total de Becas</div>
                        </div>
                        <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-100">
                          <div className="text-2xl font-bold text-blue-700">{formatNumber(dashboard.metricas_admin.cursando_hoy)}</div>
                          <div className="text-xs text-blue-600 mt-1">Alumnos Enrolados</div>
                        </div>
                        <div className="text-center p-4 bg-indigo-50 rounded-lg border border-indigo-100">
                          <div className="text-2xl font-bold text-indigo-700">{dashboard.metricas_admin.becas_por_alumno}</div>
                          <div className="text-xs text-indigo-600 mt-1">Becas por Alumno</div>
                        </div>
                        <div className="text-center p-4 bg-emerald-50 rounded-lg border border-emerald-100">
                          <div className="text-2xl font-bold text-emerald-700">{formatNumber(dashboard.metricas_admin.alumnos_activos)}</div>
                          <div className="text-xs text-emerald-600 mt-1">Alumnos Activos</div>
                        </div>
                        <div className="text-center p-4 bg-amber-50 rounded-lg border border-amber-100">
                          <div className="text-2xl font-bold text-amber-700">{dashboard.metricas_admin.pct_activos_enrolados}%</div>
                          <div className="text-xs text-amber-600 mt-1">Activos s/Enrolados</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* ══════════════════════════════════════════════════════════════════════ */}
                {/* KPI: ALUMNOS CURSANDO ESTE MES - Solo para partners que no son CCF */}
                {/* ══════════════════════════════════════════════════════════════════════ */}
                {dashboard?.cursando_hoy !== undefined && !dashboard?.is_ccf && (
                  <Card className="bg-white border border-slate-200 rounded-xl">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                          <BookOpen className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <div className="text-3xl font-bold text-blue-700">{formatNumber(dashboard.cursando_hoy)}</div>
                          <div className="text-sm text-slate-600">Alumnos cursando este mes</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* ══════════════════════════════════════════════════════════════════════ */}
                {/* SECCIÓN 4: GESTIÓN ACADÉMICA - Semáforo 8 estados con barras */}
                {/* ══════════════════════════════════════════════════════════════════════ */}
                <Card className="bg-white border border-slate-200 rounded-xl">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-4">
                        <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                          <Activity className="w-5 h-5 text-blue-600" />
                          Gestión Académica
                          <span className="text-sm font-normal text-slate-500">
                            ({formatNumber(semaforoView === "mes" ? (dashboard?.semaforo_mes?.total || 0) : (dashboard?.semaforo?.total || 0))} alumnos)
                          </span>
                        </h3>
                        <div className="flex bg-slate-100 rounded-lg p-0.5">
                          <button
                            onClick={() => setSemaforoView("mes")}
                            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                              semaforoView === "mes" ? "bg-white text-blue-600 shadow-sm" : "text-slate-600 hover:text-slate-800"
                            }`}
                          >
                            Cursando
                          </button>
                          <button
                            onClick={() => setSemaforoView("todos")}
                            className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                              semaforoView === "todos" ? "bg-white text-blue-600 shadow-sm" : "text-slate-600 hover:text-slate-800"
                            }`}
                          >
                            Todos
                          </button>
                        </div>
                        {/* Filtro por Sección/Cohorte - Muestra inicio futuro */}
                        {sections && sections.length > 0 && (
                          <Select value={sectionFilter || "all"} onValueChange={(val) => setSectionFilter(val)}>
                            <SelectTrigger className="w-[240px] h-8 text-xs" data-testid="section-filter-academia">
                              <SelectValue placeholder="Todas las cohortes" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todas las cohortes</SelectItem>
                              <SelectItem value="current_only">🟢 Solo cohortes iniciadas</SelectItem>
                              <SelectItem value="future_only">⏳ Solo cohortes futuras</SelectItem>
                              {sections.map(sec => (
                                <SelectItem key={sec.name || sec} value={sec.name || sec}>
                                  {sec.is_future ? "⏳ " : ""}
                                  {sec.name || sec}
                                  {sec.student_count ? ` (${sec.student_count})` : ""}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        {sectionFilter && sectionFilter !== "all" && (
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            sectionFilter === "future_only" ? "text-amber-600 bg-amber-50" :
                            sectionFilter === "current_only" ? "text-green-600 bg-green-50" :
                            sections.find(s => (s.name || s) === sectionFilter)?.is_future ? "text-amber-600 bg-amber-50" : "text-blue-600 bg-blue-50"
                          }`}>
                            {sectionFilter === "future_only" ? "⏳ Inicio futuro" : 
                             sectionFilter === "current_only" ? "🟢 Ya iniciadas" :
                             sections.find(s => (s.name || s) === sectionFilter)?.is_future ? "⏳ Inicio futuro" : 
                             `Cohorte: ${sectionFilter.slice(0, 20)}${sectionFilter.length > 20 ? '...' : ''}`}
                          </span>
                        )}
                        {/* Filtro por período de inicio - Solo CCF */}
                        {selectedPartner?.slug === 'cordoba-crea-futuro' && periodosCCF.length > 0 && (
                          <Select value={periodoCCF || "all"} onValueChange={(val) => setPeriodoCCF(val === "all" ? "" : val)}>
                            <SelectTrigger className="w-[180px] h-8 text-xs" data-testid="periodo-ccf-filter">
                              <SelectValue placeholder="Todos los períodos" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todos los períodos</SelectItem>
                              {periodosCCF.map(p => (
                                <SelectItem key={p.mes} value={p.mes}>
                                  {p.mes_display} ({p.alumnos})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        {periodoCCF && (
                          <span className="text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded-full">
                            Período: {periodosCCF.find(p => p.mes === periodoCCF)?.mes_display || periodoCCF}
                          </span>
                        )}
                        {/* Filtro Etapa 1/Etapa 2 - Solo CCF (2026-09-18) */}
                        {selectedPartner?.slug === 'cordoba-crea-futuro' && (
                          <Select value={etapaCCF} onValueChange={setEtapaCCF}>
                            <SelectTrigger className="w-[160px] h-8 text-xs" data-testid="etapa-ccf-filter">
                              <SelectValue placeholder="Todas las etapas" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="todas">Todas las etapas</SelectItem>
                              <SelectItem value="etapa_1">Etapa 1</SelectItem>
                              <SelectItem value="etapa_2">Etapa 2</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                        {etapaCCF !== "todas" && (
                          <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full" data-testid="etapa-ccf-badge">
                            {dashboard?.filters?.etapa_labels?.[etapaCCF] || (etapaCCF === "etapa_1" ? "Etapa 1" : "Etapa 2")}
                          </span>
                        )}
                      </div>
                      <RiskCriteriaLegend partnerSlug={selectedPartner?.slug} />
                    </div>
                    <div className="space-y-3">
                      {[
                        { key: "purple", label: "Finalizado", color: "#8b5cf6" },
                        { key: "green", label: "Activo con hitos", color: "#22c55e" },
                        { key: "blue", label: "Acompañar", color: "#3b82f6" },
                        { key: "yellow", label: "Activo sin hitos", color: "#eab308" },
                        { key: "orange", label: "Sin actividad", color: "#f97316" },
                        { key: "red", label: "En riesgo", color: "#ef4444" },
                        { key: "dark_gray", label: "Inactivo", color: "#6b7280" },
                        { key: "black", label: "No iniciado", color: "#1f2937" }
                      ].map(item => {
                        const activeSemaforo = semaforoView === "mes" ? dashboard?.semaforo_mes : dashboard?.semaforo;
                        const total = activeSemaforo?.total || 0;
                        const value = activeSemaforo?.[item.key] || 0;
                        const pct = total > 0 ? ((value / total) * 100) : 0;
                        return (
                          <div 
                            key={item.key}
                            data-testid={`semaforo-row-${item.key}`}
                            className="flex items-center gap-3 cursor-pointer hover:bg-slate-50 p-2 rounded-lg transition-colors"
                            onClick={() => { setRiskFilter(item.key); setActiveTab("explorer"); }}
                          >
                            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }}></div>
                            <div className="w-32 text-sm text-slate-700 font-medium">{item.label}</div>
                            <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
                              <div 
                                className="h-full rounded-full transition-all duration-500"
                                style={{ width: `${pct}%`, backgroundColor: item.color }}
                              ></div>
                            </div>
                            <div data-testid={`semaforo-value-${item.key}`} className="w-16 text-right text-sm font-semibold text-slate-700">
                              {formatNumber(value)}
                            </div>
                            <div className="w-14 text-right text-sm text-slate-500">
                              {pct.toFixed(1)}%
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* KPI Activos vs En Riesgo/Inactivos */}
                    {(() => {
                      const activeSemaforo = semaforoView === "mes" ? dashboard?.semaforo_mes : dashboard?.semaforo;
                      const total = activeSemaforo?.total || 0;
                      const activos = (activeSemaforo?.green || 0) + (activeSemaforo?.blue || 0) + (activeSemaforo?.yellow || 0);
                      const enRiesgo = (activeSemaforo?.orange || 0) + (activeSemaforo?.red || 0) + (activeSemaforo?.dark_gray || 0) + (activeSemaforo?.black || 0);
                      const activosPct = total > 0 ? ((activos / total) * 100).toFixed(1) : 0;
                      const enRiesgoPct = total > 0 ? ((enRiesgo / total) * 100).toFixed(1) : 0;
                      
                      return (
                        <div className="mt-4 pt-4 border-t border-slate-200">
                          <div className="grid grid-cols-2 gap-4">
                            <div 
                              className="p-3 bg-emerald-50 rounded-lg border border-emerald-200 cursor-pointer hover:bg-emerald-100 transition-colors"
                              onClick={() => { setRiskFilter("green,blue,yellow"); setActiveTab("explorer"); }}
                              data-testid="kpi-activos"
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-emerald-700">Activos</span>
                                <span className="text-xs text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">{activosPct}%</span>
                              </div>
                              <div className="text-2xl font-bold text-emerald-800 mt-1">{formatNumber(activos)}</div>
                              <div className="text-xs text-emerald-600 mt-0.5">Con hitos + Acompañar + Sin hitos</div>
                            </div>
                            <div 
                              className="p-3 bg-red-50 rounded-lg border border-red-200 cursor-pointer hover:bg-red-100 transition-colors"
                              onClick={() => { setRiskFilter("orange,red,dark_gray,black"); setActiveTab("explorer"); }}
                              data-testid="kpi-en-riesgo"
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-red-700">En Riesgo / Inactivos</span>
                                <span className="text-xs text-red-600 bg-red-100 px-2 py-0.5 rounded-full">{enRiesgoPct}%</span>
                              </div>
                              <div className="text-2xl font-bold text-red-800 mt-1">{formatNumber(enRiesgo)}</div>
                              <div className="text-xs text-red-600 mt-0.5">Sin actividad + En riesgo + Inactivo + No iniciado</div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>
                {/* ══════════════════════════════════════════════════════════════════════ */}
                <Card className="bg-white border border-slate-200 rounded-xl">
                  <CardContent className="p-5">
                    <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                      <Clock className="w-5 h-5 text-cyan-600" />
                      Acceso a Plataforma
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div className="p-4 bg-white rounded-lg border border-slate-200">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: "#22c55e" }}></div>
                          <div className="text-2xl font-semibold text-slate-800">{formatNumber(semaforoView === "mes" ? (dashboard?.actividad_mes?.activos_7d || 0) : (dashboard?.actividad?.activos_7d || 0))}</div>
                        </div>
                        <div className="text-[13px] text-slate-500 mt-1">Accedió esta semana</div>
                        <div className="text-xs text-slate-400 mt-0.5">{semaforoView === "mes" ? (dashboard?.actividad_mes?.activos_7d_pct || 0) : (dashboard?.actividad?.activos_7d_pct || 0)}%</div>
                      </div>
                      <div className="p-4 bg-white rounded-lg border border-slate-200">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: "#eab308" }}></div>
                          <div className="text-2xl font-semibold text-slate-800">{formatNumber(semaforoView === "mes" ? (dashboard?.actividad_mes?.recientes_15d || 0) : (dashboard?.actividad?.recientes_15d || 0))}</div>
                        </div>
                        <div className="text-[13px] text-slate-500 mt-1">Hace 7-15 días</div>
                        <div className="text-xs text-slate-400 mt-0.5">{semaforoView === "mes" ? (dashboard?.actividad_mes?.recientes_15d_pct || 0) : (dashboard?.actividad?.recientes_15d_pct || 0)}%</div>
                      </div>
                      <div className="p-4 bg-white rounded-lg border border-slate-200">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: "#f97316" }}></div>
                          <div className="text-2xl font-semibold text-slate-800">{formatNumber(semaforoView === "mes" ? (dashboard?.actividad_mes?.alejandose_30d || 0) : (dashboard?.actividad?.alejandose_30d || 0))}</div>
                        </div>
                        <div className="text-[13px] text-slate-500 mt-1">Hace 15-30 días</div>
                        <div className="text-xs text-slate-400 mt-0.5">{semaforoView === "mes" ? (dashboard?.actividad_mes?.alejandose_30d_pct || 0) : (dashboard?.actividad?.alejandose_30d_pct || 0)}%</div>
                      </div>
                      <div className="p-4 bg-white rounded-lg border border-slate-200">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: "#ef4444" }}></div>
                          <div className="text-2xl font-semibold text-slate-800">{formatNumber(semaforoView === "mes" ? (dashboard?.actividad_mes?.inactivos_30d || 0) : (dashboard?.actividad?.inactivos_30d || 0))}</div>
                        </div>
                        <div className="text-[13px] text-slate-500 mt-1">Hace más de 30 días</div>
                        <div className="text-xs text-slate-400 mt-0.5">{semaforoView === "mes" ? (dashboard?.actividad_mes?.inactivos_30d_pct || 0) : (dashboard?.actividad?.inactivos_30d_pct || 0)}%</div>
                      </div>
                      <div className="p-4 bg-white rounded-lg border border-slate-200">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: "#1e293b" }}></div>
                          <div className="text-2xl font-semibold text-slate-800">{formatNumber(semaforoView === "mes" ? (dashboard?.actividad_mes?.sin_acceso || 0) : (dashboard?.actividad?.sin_acceso || 0))}</div>
                        </div>
                        <div className="text-[13px] text-slate-500 mt-1">Sin acceso registrado</div>
                        <div className="text-xs text-slate-400 mt-0.5">{semaforoView === "mes" ? (dashboard?.actividad_mes?.sin_acceso_pct || 0) : (dashboard?.actividad?.sin_acceso_pct || 0)}%</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {/* ══════════════════════════════════════════════════════════════════════ */}
                {/* BLOQUE ACTIVIDAD POR BECA - SOLO CCF */}
                {/* TEMPORALMENTE OCULTO - Descomentar para reactivar */}
                {/* ══════════════════════════════════════════════════════════════════════ */}
                {/* {selectedPartner?.slug === 'cordoba-crea-futuro' && actividadBecas && (
                  <Card className="bg-white border border-slate-200 rounded-xl">
                    <CardContent className="p-5">
                      <h3 className="font-semibold text-slate-800 mb-1 flex items-center gap-2">
                        <Award className="w-5 h-5 text-purple-600" />
                        Actividad por Beca
                      </h3>
                      <p className="text-xs text-slate-500 mb-4">
                        Nivel de actividad sobre total de becas entregadas ({formatNumber(actividadBecas.total_enrollments)} becas)
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div className="p-4 bg-white rounded-lg border border-slate-200">
                          <div className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: "#22c55e" }}></div>
                            <div className="text-2xl font-semibold text-slate-800">{formatNumber(actividadBecas.activa_esta_semana?.n || 0)}</div>
                          </div>
                          <div className="text-[13px] text-slate-500 mt-1">Activa esta semana</div>
                          <div className="text-xs text-slate-400 mt-0.5">{actividadBecas.activa_esta_semana?.pct || 0}%</div>
                        </div>
                        <div className="p-4 bg-white rounded-lg border border-slate-200">
                          <div className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: "#eab308" }}></div>
                            <div className="text-2xl font-semibold text-slate-800">{formatNumber(actividadBecas.hace_7_15_dias?.n || 0)}</div>
                          </div>
                          <div className="text-[13px] text-slate-500 mt-1">Hace 7-15 días</div>
                          <div className="text-xs text-slate-400 mt-0.5">{actividadBecas.hace_7_15_dias?.pct || 0}%</div>
                        </div>
                        <div className="p-4 bg-white rounded-lg border border-slate-200">
                          <div className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: "#f97316" }}></div>
                            <div className="text-2xl font-semibold text-slate-800">{formatNumber(actividadBecas.hace_15_30_dias?.n || 0)}</div>
                          </div>
                          <div className="text-[13px] text-slate-500 mt-1">Hace 15-30 días</div>
                          <div className="text-xs text-slate-400 mt-0.5">{actividadBecas.hace_15_30_dias?.pct || 0}%</div>
                        </div>
                        <div className="p-4 bg-white rounded-lg border border-slate-200">
                          <div className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: "#ef4444" }}></div>
                            <div className="text-2xl font-semibold text-slate-800">{formatNumber(actividadBecas.hace_mas_30_dias?.n || 0)}</div>
                          </div>
                          <div className="text-[13px] text-slate-500 mt-1">Hace más de 30 días</div>
                          <div className="text-xs text-slate-400 mt-0.5">{actividadBecas.hace_mas_30_dias?.pct || 0}%</div>
                        </div>
                        <div className="p-4 bg-white rounded-lg border border-slate-200">
                          <div className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: "#1e293b" }}></div>
                            <div className="text-2xl font-semibold text-slate-800">{formatNumber(actividadBecas.sin_acceso?.n || 0)}</div>
                          </div>
                          <div className="text-[13px] text-slate-500 mt-1">Sin acceso registrado</div>
                          <div className="text-xs text-slate-400 mt-0.5">{actividadBecas.sin_acceso?.pct || 0}%</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )} */}
                
                {/* ══════════════════════════════════════════════════════════════════════ */}
                {/* SECCIÓN 6: RENDIMIENTO ACADÉMICO */}
                {/* ══════════════════════════════════════════════════════════════════════ */}
                <Card className="bg-white border border-slate-200 rounded-xl">
                  <CardContent className="p-5">
                    <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-emerald-600" />
                      Rendimiento Académico
                    </h3>
                    <div className={`grid gap-4 ${selectedPartner?.slug === 'cordoba-crea-futuro' ? 'grid-cols-5' : 'grid-cols-3'}`}>
                      <div className="p-4 bg-white rounded-lg border border-slate-200 group relative">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: "#22c55e" }}></div>
                          <div className="text-2xl font-semibold text-slate-800">{formatNumber(semaforoView === "mes" && dashboard?.rendimiento_mes ? (dashboard?.rendimiento_mes?.aprobaron || 0) : (dashboard?.rendimiento?.aprobaron || 0))}</div>
                        </div>
                        <div className="text-[13px] text-slate-500 mt-1">Aprobaron (≥60%)</div>
                        <div className="text-xs text-slate-400 mt-0.5">{semaforoView === "mes" && dashboard?.rendimiento_mes ? Math.round((dashboard?.rendimiento_mes?.aprobaron || 0) / (dashboard?.semaforo_mes?.total || 1) * 100) : (dashboard?.rendimiento?.aprobaron_pct || 0)}%</div>
                        <button
                          onClick={async () => {
                            try {
                              const cursando = semaforoView === "mes" ? "&cursando=true" : "";
                              const url = `${API_URL}/api/partners/${selectedPartner?.id}/export-rendimiento?categoria=aprobaron${cursando}`;
                              await secureExportDownload({ url, filename: `aprobaron_${selectedPartner?.slug || 'export'}_${new Date().toISOString().split('T')[0]}.xlsx` });
                            } catch (err) {
                              if (err.message !== 'cancelado') {
                                console.error("Export Aprobaron error:", err);
                                toast.error("Error al descargar: " + (err.message || "Error desconocido"));
                              }
                            }
                          }}
                          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 bg-emerald-100 hover:bg-emerald-200 rounded text-emerald-700"
                          title="Exportar Aprobaron"
                          data-testid="export-aprobaron"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="p-4 bg-white rounded-lg border border-slate-200 group relative">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: "#eab308" }}></div>
                          <div className="text-2xl font-semibold text-slate-800">{formatNumber(semaforoView === "mes" && dashboard?.rendimiento_mes ? (dashboard?.rendimiento_mes?.sin_aprobar || 0) : (dashboard?.rendimiento?.sin_aprobar || 0))}</div>
                        </div>
                        <div className="text-[13px] text-slate-500 mt-1">Sin aprobar (1-59%)</div>
                        <div className="text-xs text-slate-400 mt-0.5">{semaforoView === "mes" && dashboard?.rendimiento_mes ? Math.round((dashboard?.rendimiento_mes?.sin_aprobar || 0) / (dashboard?.semaforo_mes?.total || 1) * 100) : (dashboard?.rendimiento?.sin_aprobar_pct || 0)}%</div>
                        <button
                          onClick={async () => {
                            try {
                              const cursando = semaforoView === "mes" ? "&cursando=true" : "";
                              const url = `${API_URL}/api/partners/${selectedPartner?.id}/export-rendimiento?categoria=sin_aprobar${cursando}`;
                              await secureExportDownload({ url, filename: `sin_aprobar_${selectedPartner?.slug || 'export'}_${new Date().toISOString().split('T')[0]}.xlsx` });
                            } catch (err) {
                              if (err.message !== 'cancelado') {
                                console.error("Export Sin Aprobar error:", err);
                                toast.error("Error al descargar: " + (err.message || "Error desconocido"));
                              }
                            }
                          }}
                          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 bg-yellow-100 hover:bg-yellow-200 rounded text-yellow-700"
                          title="Exportar Sin aprobar"
                          data-testid="export-sin-aprobar"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="p-4 bg-white rounded-lg border border-slate-200 group relative">
                        <div className="flex items-center gap-3">
                          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: "#6b7280" }}></div>
                          <div className="text-2xl font-semibold text-slate-800">{formatNumber(semaforoView === "mes" && dashboard?.rendimiento_mes ? (dashboard?.rendimiento_mes?.sin_rendir || 0) : (dashboard?.rendimiento?.sin_rendir || 0))}</div>
                        </div>
                        <div className="text-[13px] text-slate-500 mt-1">Sin rendir aún</div>
                        <div className="text-xs text-slate-400 mt-0.5">{semaforoView === "mes" && dashboard?.rendimiento_mes ? Math.round((dashboard?.rendimiento_mes?.sin_rendir || 0) / (dashboard?.semaforo_mes?.total || 1) * 100) : (dashboard?.rendimiento?.sin_rendir_pct || 0)}%</div>
                        <button
                          onClick={async () => {
                            try {
                              const cursando = semaforoView === "mes" ? "&cursando=true" : "";
                              const url = `${API_URL}/api/partners/${selectedPartner?.id}/export-rendimiento?categoria=sin_rendir${cursando}`;
                              await secureExportDownload({ url, filename: `sin_rendir_${selectedPartner?.slug || 'export'}_${new Date().toISOString().split('T')[0]}.xlsx` });
                            } catch (err) {
                              if (err.message !== 'cancelado') {
                                console.error("Export Sin Rendir error:", err);
                                toast.error("Error al descargar: " + (err.message || "Error desconocido"));
                              }
                            }
                          }}
                          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 bg-slate-100 hover:bg-slate-200 rounded text-slate-700"
                          title="Exportar Sin rendir"
                          data-testid="export-sin-rendir"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                      </div>
                      
                      {/* CARD 4: Alumnos a Certificar (SOLO CCF) */}
                      {selectedPartner?.slug === 'cordoba-crea-futuro' && (
                        <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl p-4 group relative border border-amber-200" data-testid="card-alumnos-certificar">
                          <div className="flex items-start gap-3">
                            <div className="p-2 bg-amber-100 rounded-lg">
                              <Trophy className="w-5 h-5 text-amber-600" />
                            </div>
                            <div>
                              <div className="text-2xl font-semibold text-slate-800">{formatNumber(dashboard?.rendimiento?.alumnos_a_certificar || 0)}</div>
                            </div>
                          </div>
                          <div className="text-[13px] text-slate-500 mt-1">Alumnos a certificar</div>
                          <div className="text-xs text-slate-400 mt-0.5">Aprobaron 2+ cursos (excluye cursos sello)</div>
                          <button
                            onClick={async () => {
                              try {
                                const url = `${API_URL}/api/partners/${selectedPartner?.id}/export-alumnos-certificar`;
                                await secureExportDownload({ url, filename: `CCF_Alumnos_a_Certificar_${new Date().toISOString().split('T')[0]}.xlsx` });
                              } catch (err) {
                                if (err.message !== 'cancelado') {
                                  console.error("Export Alumnos Certificar error:", err);
                                  toast.error("Error al descargar: " + (err.message || "Error desconocido"));
                                }
                              }
                            }}
                            className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 bg-amber-100 hover:bg-amber-200 rounded text-amber-700"
                            title="Exportar alumnos a certificar"
                            data-testid="export-alumnos-certificar"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                      
                      {/* CARD 5: Certificados Emitidos (SOLO CCF) - por (alumno x programa/beca), distinto de "alumnos a certificar" */}
                      {selectedPartner?.slug === 'cordoba-crea-futuro' && (
                        <div className="bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl p-4 border border-violet-200" data-testid="card-certificados-emitidos">
                          <div className="flex items-start gap-3">
                            <div className="p-2 bg-violet-100 rounded-lg">
                              <Award className="w-5 h-5 text-violet-600" />
                            </div>
                            <div>
                              <div className="text-2xl font-semibold text-slate-800">{formatNumber(dashboard?.rendimiento?.certificados_emitidos || 0)}</div>
                            </div>
                          </div>
                          <div className="text-[13px] text-slate-500 mt-1">Certificados emitidos</div>
                          <div className="text-xs text-slate-400 mt-0.5">Por programa/beca (un alumno con 2 certificados cuenta 2)</div>
                          <div className="flex gap-3 mt-2 text-xs text-violet-600">
                            <span data-testid="certificados-etapa1">Etapa 1: {formatNumber(dashboard?.rendimiento?.certificados_emitidos_etapa1 || 0)}</span>
                            <span data-testid="certificados-etapa2">Etapa 2: {formatNumber(dashboard?.rendimiento?.certificados_emitidos_etapa2 || 0)}</span>
                          </div>
                        </div>
                      )}

                      {/* CARD 6: CAP (Certificado de Aprobación) - FIX 2026-10-26 (Cande/CTO,
                          Fix #3+4). Todos los partners (no solo CCF). 3 buckets: en situación
                          (sección CAP abierta, sin nota) / aprobado (≥60) / desaprobado (<60).
                          "aprobado"/"desaprobado" son PROVISORIOS (proxy con current_score,
                          sin los campos "unposted" que pidió Cande - no existen en el sistema)
                          hasta que ella confirme que el criterio le sirve. */}
                      {dashboard?.rendimiento?.cap && (
                        (dashboard.rendimiento.cap.en_situacion + dashboard.rendimiento.cap.aprobado + dashboard.rendimiento.cap.desaprobado) > 0
                      ) && (
                        <div className={`bg-gradient-to-br from-sky-50 to-cyan-50 rounded-xl p-4 border border-sky-200 ${selectedPartner?.slug === 'cordoba-crea-futuro' ? 'col-span-2' : ''}`} data-testid="card-cap">
                          <div className="flex items-start gap-3">
                            <div className="p-2 bg-sky-100 rounded-lg">
                              <GraduationCap className="w-5 h-5 text-sky-600" />
                            </div>
                            <div>
                              <div className="text-[13px] text-slate-600 font-medium">CAP (Certificado de Aprobación)</div>
                              <div className="text-xs text-slate-400">Exclusión del semáforo: pendiente (Fase 2){selectedPartner?.slug === 'cordoba-crea-futuro' ? ' - CCF no se excluirá' : ''}</div>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-2 mt-3">
                            <div data-testid="cap-en-situacion">
                              <div className="text-xl font-semibold text-slate-800">{formatNumber(dashboard.rendimiento.cap.en_situacion)}</div>
                              <div className="text-[11px] text-slate-500">En situación</div>
                              <div className="text-[10px] text-slate-400">Sección abierta, sin nota</div>
                            </div>
                            <div data-testid="cap-aprobado">
                              <div className="text-xl font-semibold text-emerald-600">{formatNumber(dashboard.rendimiento.cap.aprobado)}</div>
                              <div className="text-[11px] text-slate-500">Aprobado</div>
                              <div className="text-[10px] text-amber-500" title="Provisorio: usa current_score como proxy de la nota, sin los campos 'unposted' - a confirmar con Cande">Provisorio ⓘ</div>
                            </div>
                            <div data-testid="cap-desaprobado">
                              <div className="text-xl font-semibold text-rose-600">{formatNumber(dashboard.rendimiento.cap.desaprobado)}</div>
                              <div className="text-[11px] text-slate-500">Desaprobado</div>
                              <div className="text-[10px] text-amber-500" title="Provisorio: usa current_score como proxy de la nota, sin los campos 'unposted' - a confirmar con Cande">Provisorio ⓘ</div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
                
                {/* ══════════════════════════════════════════════════════════════════════ */}
                {/* SECCIÓN: EXPORTABLES ADICIONALES */}
                {/* ══════════════════════════════════════════════════════════════════════ */}
                <Card className="bg-white border border-slate-200 rounded-xl">
                  <CardContent className="p-5">
                    <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                      <FileDown className="w-5 h-5 text-blue-600" />
                      Exportables
                      <span className="text-sm font-normal text-slate-500">(Respetan el filtro de instancia activo)</span>
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {/* Rendimiento Académico */}
                      <button
                        onClick={async () => {
                          try {
                            const url = `${API_URL}/api/partners/${selectedPartner?.id}/export/rendimiento-academico`;
                            await secureExportDownload({ url, filename: `Rendimiento_Academico_${selectedPartner?.slug || 'export'}_${new Date().toISOString().split('T')[0]}.xlsx` });
                          } catch (err) {
                            if (err.message !== 'cancelado') {
                              console.error("Export error:", err);
                              toast.error("Error al descargar");
                            }
                          }
                        }}
                        className="flex flex-col items-center gap-2 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200 hover:border-blue-400 hover:shadow-md transition-all text-center"
                        data-testid="export-rendimiento-academico"
                      >
                        <BarChart3 className="w-6 h-6 text-blue-600" />
                        <span className="text-sm font-medium text-blue-700">Rendimiento Académico</span>
                        <span className="text-xs text-blue-500">Nota + Semáforo por alumno</span>
                      </button>
                      
                      {/* Alumnos que Terminaron */}
                      <button
                        onClick={async () => {
                          try {
                            const url = `${API_URL}/api/partners/${selectedPartner?.id}/export/alumnos-terminaron`;
                            await secureExportDownload({ url, filename: `Alumnos_Terminaron_${selectedPartner?.slug || 'export'}_${new Date().toISOString().split('T')[0]}.xlsx` });
                          } catch (err) {
                            if (err.message !== 'cancelado') {
                              console.error("Export error:", err);
                              toast.error("Error al descargar");
                            }
                          }
                        }}
                        className="flex flex-col items-center gap-2 p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg border border-amber-200 hover:border-amber-400 hover:shadow-md transition-all text-center"
                        data-testid="export-alumnos-terminaron"
                      >
                        <GraduationCap className="w-6 h-6 text-amber-600" />
                        <span className="text-sm font-medium text-amber-700">Alumnos Terminaron</span>
                        <span className="text-xs text-amber-500">Nota final + Estado</span>
                      </button>
                      
                      {/* Calificaciones por Curso */}
                      <button
                        onClick={async () => {
                          try {
                            const url = `${API_URL}/api/partners/${selectedPartner?.id}/export/calificaciones-por-curso`;
                            await secureExportDownload({ url, filename: `Calificaciones_Por_Curso_${selectedPartner?.slug || 'export'}_${new Date().toISOString().split('T')[0]}.xlsx` });
                          } catch (err) {
                            if (err.message !== 'cancelado') {
                              console.error("Export error:", err);
                              toast.error("Error al descargar");
                            }
                          }
                        }}
                        className="flex flex-col items-center gap-2 p-4 bg-gradient-to-br from-emerald-50 to-green-50 rounded-lg border border-emerald-200 hover:border-emerald-400 hover:shadow-md transition-all text-center"
                        data-testid="export-calificaciones-curso"
                      >
                        <Award className="w-6 h-6 text-emerald-600" />
                        <span className="text-sm font-medium text-emerald-700">Calificaciones</span>
                        <span className="text-xs text-emerald-500">Por alumno × curso</span>
                      </button>
                      
                      {/* Engagement - con selector de mes */}
                      <div className="flex flex-col gap-2">
                        <select
                          id="engagement-mes-selector"
                          className="text-xs bg-white border border-purple-200 rounded px-2 py-1 text-purple-700 cursor-pointer hover:bg-purple-50"
                          defaultValue=""
                          data-testid="engagement-mes-selector"
                        >
                          <option value="">Todos los meses</option>
                          <option value="2026-01">Enero 2026</option>
                          <option value="2026-02">Febrero 2026</option>
                          <option value="2026-03">Marzo 2026</option>
                          <option value="2026-04">Abril 2026</option>
                          <option value="2026-05">Mayo 2026</option>
                          <option value="2026-06">Junio 2026</option>
                          <option value="2026-07">Julio 2026</option>
                          <option value="2026-08">Agosto 2026</option>
                          <option value="2026-09">Septiembre 2026</option>
                          <option value="2026-10">Octubre 2026</option>
                          <option value="2026-11">Noviembre 2026</option>
                          <option value="2026-12">Diciembre 2026</option>
                        </select>
                        <button
                          onClick={async () => {
                            try {
                              const mesSelector = document.getElementById('engagement-mes-selector');
                              const mesParam = mesSelector?.value ? `&mes=${mesSelector.value}` : '';
                              const mesLabel = mesSelector?.value || 'todos';
                              const url = `${API_URL}/api/partners/${selectedPartner?.id}/export/engagement${mesParam ? `?${mesParam.slice(1)}` : ''}`;
                              await secureExportDownload({ url, filename: `Engagement_${selectedPartner?.slug || 'export'}_${mesLabel}_${new Date().toISOString().split('T')[0]}.xlsx` });
                            } catch (err) {
                              if (err.message !== 'cancelado') {
                                console.error("Export error:", err);
                                toast.error("Error al descargar");
                              }
                            }
                          }}
                          className="flex flex-col items-center gap-2 p-4 bg-gradient-to-br from-purple-50 to-violet-50 rounded-lg border border-purple-200 hover:border-purple-400 hover:shadow-md transition-all text-center flex-1"
                          data-testid="export-engagement"
                        >
                          <Activity className="w-6 h-6 text-purple-600" />
                          <span className="text-sm font-medium text-purple-700">Engagement</span>
                          <span className="text-xs text-purple-500">Views + Participaciones</span>
                        </button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {/* ══════════════════════════════════════════════════════════════════════ */}
                {/* SECCIÓN: HITOS DE ENGAGEMENT (solo si hay datos y semaforoView === "mes") */}
                {/* ══════════════════════════════════════════════════════════════════════ */}
                {semaforoView === "mes" && dashboard?.rendimiento_mes && (dashboard?.rendimiento_mes?.total_views > 0 || dashboard?.rendimiento_mes?.total_participaciones > 0) && (
                  <Card className="bg-white border border-slate-200 rounded-xl">
                    <CardContent className="p-5">
                      <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                        <Activity className="w-5 h-5 text-indigo-600" />
                        Hitos de Engagement
                        <span className="text-sm font-normal text-slate-500">(Alumnos cursando)</span>
                      </h3>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                          <div className="flex items-center gap-3">
                            <Eye className="w-5 h-5 text-indigo-600" />
                            <div className="text-2xl font-semibold text-indigo-800">{formatNumber(dashboard.rendimiento_mes.total_views)}</div>
                          </div>
                          <div className="text-[13px] text-indigo-600 mt-1">Total Views</div>
                        </div>
                        <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                          <div className="flex items-center gap-3">
                            <Zap className="w-5 h-5 text-purple-600" />
                            <div className="text-2xl font-semibold text-purple-800">{formatNumber(dashboard.rendimiento_mes.total_participaciones)}</div>
                          </div>
                          <div className="text-[13px] text-purple-600 mt-1">Participaciones</div>
                        </div>
                        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                          <div className="flex items-center gap-3">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            <div className="text-2xl font-semibold text-green-800">{formatNumber(dashboard.rendimiento_mes.sin_faltantes)}</div>
                          </div>
                          <div className="text-[13px] text-green-600 mt-1">Sin faltantes</div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* ══════════════════════════════════════════════════════════════════════ */}
                {/* SECCIÓN 7: ALUMNOS QUE TERMINARON */}
                {/* ══════════════════════════════════════════════════════════════════════ */}
                {dashboard?.egresados && (
                  <Card className="border-2 border-amber-300 bg-gradient-to-br from-amber-50/80 to-orange-50/50">
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center gap-2 mb-4">
                        <GraduationCap className="w-5 h-5 text-amber-600" />
                        <h3 className="font-semibold text-amber-800 text-lg">Alumnos que Terminaron</h3>
                        <Badge className="bg-amber-100 text-amber-700 text-xs ml-auto">
                          {dashboard.egresados.total} alumnos
                        </Badge>
                      </div>
                      
                      {/* Filtros de período para egresados */}
                      <div className="mb-4 flex flex-wrap gap-2">
                        {/* Filtro por actividad reciente */}
                        <select 
                          value={egresadosPeriod}
                          onChange={(e) => setEgresadosPeriod(e.target.value)}
                          className="text-xs bg-white border border-amber-300 rounded px-2 py-1.5 text-amber-700 cursor-pointer hover:bg-amber-50"
                          data-testid="egresados-period-filter"
                        >
                          <option value="all">Todo el tiempo</option>
                          <option value="30d">Últimos 30 días</option>
                          <option value="60d">Últimos 60 días</option>
                          <option value="90d">Últimos 90 días</option>
                          <option value="365d">Último año</option>
                        </select>
                        
                        {/* Filtro por mes de finalización (solo Areandina) */}
                        {dashboard.egresados.finalizaciones_por_mes?.length > 0 && (
                          <select 
                            value={mesFinalizacion}
                            onChange={(e) => setMesFinalizacion(e.target.value)}
                            className="text-xs bg-white border border-amber-300 rounded px-2 py-1.5 text-amber-700 cursor-pointer hover:bg-amber-50"
                            data-testid="mes-finalizacion-filter"
                          >
                            <option value="all">Todos los meses (fin)</option>
                            {dashboard.egresados.finalizaciones_por_mes.map((m) => (
                              <option key={m.mes} value={m.mes}>
                                {m.mes_display} ({m.finalizaron})
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                      
                      {dashboard.egresados.total > 0 ? (
                        <>
                          {/* Métricas de egresados - filtradas por mes si aplica */}
                          {(() => {
                            const datosMes = mesFinalizacion !== "all" && dashboard.egresados.finalizaciones_por_mes
                              ? dashboard.egresados.finalizaciones_por_mes.find(m => m.mes === mesFinalizacion)
                              : null;
                            
                            const total = datosMes ? datosMes.finalizaron : dashboard.egresados.total;
                            const aprobados = datosMes ? datosMes.aprobados : dashboard.egresados.aprobados;
                            const desaprobados = datosMes ? datosMes.desaprobados : dashboard.egresados.desaprobados;
                            const sinNota = datosMes ? datosMes.sin_nota : dashboard.egresados.sin_nota;
                            const exitoPercent = total > 0 ? Math.round((aprobados / total) * 100 * 10) / 10 : 0;
                            
                            return (
                              <>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                  <div className="text-center p-3 bg-white/70 rounded-lg border border-amber-200">
                                    <div data-testid="egresados-total" className="text-2xl font-bold text-amber-700">
                                      {total}
                                    </div>
                                    <div className="text-xs text-amber-600">Total Terminaron</div>
                                  </div>
                                  
                                  <div className="text-center p-3 bg-white/70 rounded-lg border border-emerald-200 relative group">
                                    <div className="flex items-center justify-center gap-1">
                                      <CheckCircle className="w-4 h-4 text-emerald-500" />
                                      <span data-testid="egresados-aprobados" className="text-2xl font-bold text-emerald-600">
                                        {aprobados}
                                      </span>
                                    </div>
                                    <div className="text-xs text-emerald-600">Aprobados (≥60%)</div>
                                    {/* Botón descarga Excel - Solo CCF */}
                                    {selectedPartner?.slug === 'cordoba-crea-futuro' && (
                                      <button
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          try {
                                            const url = `${API_URL}/api/partners/${selectedPartner?.id}/export/aprobados-ccf`;
                                            await secureExportDownload({ url, filename: `CCF_Aprobados_Certificacion_${new Date().toISOString().split('T')[0]}.xlsx` });
                                          } catch (err) {
                                            if (err.message !== 'cancelado') {
                                              console.error("Export Aprobados CCF error:", err);
                                              toast.error("Error al descargar: " + (err.message || "Error desconocido"));
                                            }
                                          }
                                        }}
                                        className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity p-1 bg-emerald-100 hover:bg-emerald-200 rounded text-emerald-700"
                                        title="Descargar Excel de aprobados"
                                        data-testid="export-aprobados-ccf"
                                      >
                                        <Download className="w-3 h-3" />
                                      </button>
                                    )}
                                  </div>
                                  
                                  <div className="text-center p-3 bg-white/70 rounded-lg border border-orange-200 relative group">
                                    <div className="flex items-center justify-center gap-1">
                                      <AlertTriangle className="w-4 h-4 text-orange-500" />
                                      <span data-testid="egresados-desaprobados" className="text-2xl font-bold text-orange-600">
                                        {desaprobados}
                                      </span>
                                    </div>
                                    <div className="text-xs text-orange-600">Desaprobados (&lt;60%)</div>
                                    {/* Botón descarga Excel Desaprobados - Solo CCF */}
                                    {selectedPartner?.slug === 'cordoba-crea-futuro' && (
                                      <button
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          try {
                                            const url = `${API_URL}/api/partners/${selectedPartner?.id}/export/desaprobados-ccf`;
                                            await secureExportDownload({ url, filename: `CCF_Desaprobados_${new Date().toISOString().split('T')[0]}.xlsx` });
                                          } catch (err) {
                                            if (err.message !== 'cancelado') {
                                              console.error("Export Desaprobados CCF error:", err);
                                              toast.error("Error al descargar: " + (err.message || "Error desconocido"));
                                            }
                                          }
                                        }}
                                        className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity p-1 bg-orange-100 hover:bg-orange-200 rounded text-orange-700"
                                        title="Descargar Excel de desaprobados"
                                        data-testid="export-desaprobados-ccf"
                                      >
                                        <Download className="w-3 h-3" />
                                      </button>
                                    )}
                                  </div>
                                  
                                  <div className="text-center p-3 bg-white/70 rounded-lg border border-slate-200 relative group">
                                    <div className="flex items-center justify-center gap-1">
                                      <XCircle className="w-4 h-4 text-slate-400" />
                                      <span data-testid="egresados-sin-nota" className="text-2xl font-bold text-slate-500">
                                        {sinNota}
                                      </span>
                                    </div>
                                    <div className="text-xs text-slate-500">
                                      {selectedPartner?.slug === 'cordoba-crea-futuro' ? 'No terminaron' : 'Abandonaron'}
                                    </div>
                                    {/* Botón descarga Excel No Terminaron - Solo CCF */}
                                    {selectedPartner?.slug === 'cordoba-crea-futuro' && (
                                      <button
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          try {
                                            const url = `${API_URL}/api/partners/${selectedPartner?.id}/export/no-terminaron-ccf`;
                                            await secureExportDownload({ url, filename: `CCF_No_Terminaron_${new Date().toISOString().split('T')[0]}.xlsx` });
                                          } catch (err) {
                                            if (err.message !== 'cancelado') {
                                              console.error("Export No Terminaron CCF error:", err);
                                              toast.error("Error al descargar: " + (err.message || "Error desconocido"));
                                            }
                                          }
                                        }}
                                        className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity p-1 bg-slate-100 hover:bg-slate-200 rounded text-slate-700"
                                        title="Descargar Excel de no terminaron"
                                        data-testid="export-no-terminaron-ccf"
                                      >
                                        <Download className="w-3 h-3" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Tasa de éxito calculada */}
                                {datosMes && (
                                  <div className="mb-4 p-2 bg-emerald-50 rounded-lg border border-emerald-200">
                                    <div className="flex items-center justify-between">
                                      <span className="text-sm text-emerald-700">Tasa de Éxito ({datosMes.mes_display || mesFinalizacion})</span>
                                      <span className="text-lg font-bold text-emerald-600">{exitoPercent}%</span>
                                    </div>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                          
                          {/* 3 TASAS con barras de progreso diferenciadas - solo si no hay mes filtrado */}
                          {mesFinalizacion === "all" && (
                          <div className="pt-4 border-t border-amber-200 space-y-4">
                            <h4 className="text-sm font-medium text-amber-700 mb-3">Indicadores de Resultado</h4>
                            
                            {/* 🟢 Tasa de Éxito */}
                            <div className="flex items-center gap-4">
                              <div className="w-40">
                                <div className="flex items-center gap-1.5">
                                  <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                                  <span className="text-sm font-medium text-slate-700">Tasa de Éxito</span>
                                </div>
                                <p className="text-xs text-slate-500 ml-4.5">Aprobados / Total</p>
                              </div>
                              <div className="flex-1 flex items-center gap-3">
                                <div className="flex-1 h-3 bg-slate-200 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                                    style={{ width: `${dashboard.egresados.exito_percent || 0}%` }}
                                  />
                                </div>
                                <span className="text-sm font-bold text-emerald-600 w-14 text-right">
                                  {dashboard.egresados.exito_percent || 0}%
                                </span>
                              </div>
                            </div>
                            
                            {/* 🔵 Tasa de Finalización */}
                            <div className="flex items-center gap-4">
                              <div className="w-40">
                                <div className="flex items-center gap-1.5">
                                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                                  <span className="text-sm font-medium text-slate-700">Tasa de Finalización</span>
                                </div>
                                <p className="text-xs text-slate-500 ml-4.5">Con nota / Total</p>
                              </div>
                              <div className="flex-1 flex items-center gap-3">
                                <div className="flex-1 h-3 bg-slate-200 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-blue-500 rounded-full transition-all duration-500"
                                    style={{ width: `${dashboard.egresados.finalizacion_percent || 0}%` }}
                                  />
                                </div>
                                <span className="text-sm font-bold text-blue-600 w-14 text-right">
                                  {dashboard.egresados.finalizacion_percent || 0}%
                                </span>
                              </div>
                            </div>
                            
                            {/* ⚫ Tasa de Abandono */}
                            <div className="flex items-center gap-4">
                              <div className="w-40">
                                <div className="flex items-center gap-1.5">
                                  <div className="w-3 h-3 rounded-full bg-slate-500"></div>
                                  <span className="text-sm font-medium text-slate-700">Tasa de Abandono</span>
                                </div>
                                <p className="text-xs text-slate-500 ml-4.5">Sin nota / Total</p>
                              </div>
                              <div className="flex-1 flex items-center gap-3">
                                <div className="flex-1 h-3 bg-slate-200 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-slate-500 rounded-full transition-all duration-500"
                                    style={{ width: `${dashboard.egresados.abandono_percent || 0}%` }}
                                  />
                                </div>
                                <span className="text-sm font-bold text-slate-600 w-14 text-right">
                                  {dashboard.egresados.abandono_percent || 0}%
                                </span>
                              </div>
                            </div>
                          </div>
                          )}
                        </>
                      ) : (
                        <div className="text-center py-6 text-amber-600">
                          <GraduationCap className="w-12 h-12 mx-auto mb-2 text-amber-300" />
                          <p className="text-sm font-medium">
                            Sin alumnos terminados aún
                          </p>
                          <p className="text-xs text-amber-500 mt-1">
                            Los alumnos aparecerán aquí cuando completen todos sus cursos según Canvas
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
                
                {/* TODO: Filtro por programa - pendiente de implementar correctamente
                {programStats.length > 0 && programFilter === "all" && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Comparativa por Programa</CardTitle>
                      <CardDescription>Rendimiento de cada programa del partner (con NPS)</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead>Programa</TableHead>
                            <TableHead className="text-right">Total</TableHead>
                            <TableHead className="text-right">🟢 Activos</TableHead>
                            <TableHead className="text-right">🟡 En Riesgo</TableHead>
                            <TableHead className="text-right">🔴 Críticos</TableHead>
                            <TableHead className="text-center">NPS Prom.</TableHead>
                            <TableHead className="text-center">Detractores</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {programStats.map((prog) => (
                            <TableRow 
                              key={prog.program_name} 
                              className="hover:bg-slate-50 cursor-pointer"
                              onClick={() => setProgramFilter(prog.program_name)}
                            >
                              <TableCell className="font-medium">{prog.program_name}</TableCell>
                              <TableCell className="text-right font-mono">{prog.total_students}</TableCell>
                              <TableCell className="text-right">
                                <span className="font-mono text-green-600">{prog.green_percent}%</span>
                                <span className="text-slate-400 text-xs ml-1">({prog.green_count})</span>
                              </TableCell>
                              <TableCell className="text-right">
                                <span className="font-mono text-amber-600">{prog.yellow_percent}%</span>
                                <span className="text-slate-400 text-xs ml-1">({prog.yellow_count})</span>
                              </TableCell>
                              <TableCell className="text-right">
                                <span className={`font-mono ${prog.red_percent > 30 ? "text-red-600 font-bold" : "text-red-500"}`}>
                                  {prog.red_percent}%
                                </span>
                                <span className="text-slate-400 text-xs ml-1">({prog.red_count})</span>
                              </TableCell>
                              <TableCell className="text-center">
                                {prog.nps_avg !== null ? (
                                  <Badge variant="outline" className={`text-xs ${
                                    prog.nps_avg >= 9 ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                                    prog.nps_avg >= 7 ? "bg-amber-50 text-amber-700 border-amber-200" :
                                    "bg-red-50 text-red-700 border-red-200"
                                  }`}>
                                    {prog.nps_avg}
                                  </Badge>
                                ) : (
                                  <span className="text-slate-400 text-xs">—</span>
                                )}
                              </TableCell>
                              <TableCell className="text-center">
                                {prog.nps_detractors > 0 ? (
                                  <Badge variant="outline" className="text-xs bg-red-100 text-red-700 border-red-300">
                                    {prog.nps_detractors}
                                  </Badge>
                                ) : (
                                  <span className="text-slate-400 text-xs">0</span>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      <p className="text-xs text-slate-400 mt-3 text-center">
                        💡 Haz clic en un programa para filtrar el dashboard
                      </p>
                    </CardContent>
                  </Card>
                )}
                */}
              </div>
            </TabsContent>
            
            {/* TAB: CERTIFICADOS POR PROGRAMA - Solo CCF */}
            {selectedPartner?.slug === 'cordoba-crea-futuro' && (
            <TabsContent value="programs">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <GraduationCap className="w-5 h-5" />
                    Vista por Certificado - {selectedPartner?.name}
                  </CardTitle>
                  <CardDescription>
                    Distribución de alumnos por programa de certificación. Semáforo de 8 estados.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {programsCCFLoading ? (
                    <div className="flex justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                    </div>
                  ) : programsCCF && programsCCF.programs?.length > 0 ? (
                    <div className="space-y-4">
                      {/* Resumen Global */}
                      <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200">
                        <div className="flex flex-wrap items-center justify-between gap-4">
                          <div className="flex items-center gap-2">
                            <GraduationCap className="w-5 h-5 text-amber-600" />
                            <span className="font-semibold text-slate-800">
                              {programsCCF.programs?.length} Certificados
                            </span>
                          </div>
                          <div className="flex items-center gap-6 text-sm">
                            <span><strong className="text-blue-600">{programsCCF.totals?.activos?.toLocaleString('es-AR')}</strong> Alumnos Activos</span>
                            <span><strong className="text-emerald-600">{programsCCF.totals?.terminados?.toLocaleString('es-AR')}</strong> Terminados</span>
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              programsCCF.totals?.tasa_riesgo > 50 ? 'bg-red-100 text-red-700' : 
                              programsCCF.totals?.tasa_riesgo > 20 ? 'bg-orange-100 text-orange-700' : 
                              'bg-green-100 text-green-700'
                            }`}>
                              {programsCCF.totals?.tasa_riesgo}% Riesgo
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Tabla de Certificados */}
                      <div className="border border-slate-200 rounded-lg overflow-x-auto">
                        <table className="w-full text-sm min-w-[1000px]">
                          <thead className="bg-slate-100">
                            <tr>
                              <th className="px-4 py-3 text-left font-semibold text-slate-700">Certificado</th>
                              <th 
                                className="px-3 py-3 text-center font-semibold text-slate-700 whitespace-nowrap cursor-pointer hover:bg-slate-200 select-none"
                                onClick={() => {
                                  if (coursesSortBy === "activos") {
                                    setCoursesSortOrder(coursesSortOrder === "desc" ? "asc" : "desc");
                                  } else {
                                    setCoursesSortBy("activos");
                                    setCoursesSortOrder("desc");
                                  }
                                }}
                              >
                                Activos {coursesSortBy === "activos" && (coursesSortOrder === "desc" ? "↓" : "↑")}
                              </th>
                              <th 
                                className="px-3 py-3 text-center font-semibold text-slate-700 cursor-pointer hover:bg-slate-200 select-none"
                                onClick={() => {
                                  if (coursesSortBy === "terminados") {
                                    setCoursesSortOrder(coursesSortOrder === "desc" ? "asc" : "desc");
                                  } else {
                                    setCoursesSortBy("terminados");
                                    setCoursesSortOrder("desc");
                                  }
                                }}
                              >
                                Term. {coursesSortBy === "terminados" && (coursesSortOrder === "desc" ? "↓" : "↑")}
                              </th>
                              <th className="px-3 py-3 text-center font-semibold text-slate-700">Riesgo</th>
                              <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#22c55e'}}></span> <span className="text-xs" style={{color:'#22c55e'}}>Sal</span></th>
                              <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#3b82f6'}}></span> <span className="text-xs" style={{color:'#3b82f6'}}>Aco</span></th>
                              <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#a855f7'}}></span> <span className="text-xs" style={{color:'#a855f7'}}>Com</span></th>
                              <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#f97316'}}></span> <span className="text-xs" style={{color:'#f97316'}}>Baj</span></th>
                              <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#eab308'}}></span> <span className="text-xs" style={{color:'#eab308'}}>SinA</span></th>
                              <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#ef4444'}}></span> <span className="text-xs" style={{color:'#ef4444'}}>Rie</span></th>
                              <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#6b7280'}}></span> <span className="text-xs" style={{color:'#6b7280'}}>Ina</span></th>
                              <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#1f2937'}}></span> <span className="text-xs" style={{color:'#1f2937'}}>NoI</span></th>
                            </tr>
                          </thead>
                          <tbody>
                            {[...programsCCF.programs].sort((a, b) => {
                              if (!coursesSortBy) return 0;
                              const aVal = coursesSortBy === "activos" ? (a.activos || 0) : (a.terminados || 0);
                              const bVal = coursesSortBy === "activos" ? (b.activos || 0) : (b.terminados || 0);
                              return coursesSortOrder === "desc" ? bVal - aVal : aVal - bVal;
                            }).map((prog, idx) => (
                              <tr 
                                key={idx}
                                className="bg-white hover:bg-amber-50 border-t border-slate-100 transition-colors"
                              >
                                <td className="px-4 py-3">
                                  <span className="font-medium text-slate-800">{prog.name}</span>
                                </td>
                                <td className="px-3 py-3 text-center font-semibold text-slate-800">{prog.activos?.toLocaleString('es-AR')}</td>
                                <td className="px-3 py-3 text-center font-semibold text-emerald-600">{prog.terminados?.toLocaleString('es-AR')}</td>
                                <td className="px-3 py-3 text-center">
                                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                    prog.tasa_riesgo > 50 ? 'bg-red-100 text-red-700' : 
                                    prog.tasa_riesgo > 20 ? 'bg-orange-100 text-orange-700' : 
                                    'bg-green-100 text-green-700'
                                  }`}>
                                    {prog.tasa_riesgo}%
                                  </span>
                                </td>
                                <td className="px-2 py-3 text-center font-medium" style={{color:'#22c55e'}}>{prog.semaforo?.green || 0}</td>
                                <td className="px-2 py-3 text-center font-medium" style={{color:'#3b82f6'}}>{prog.semaforo?.blue || 0}</td>
                                <td className="px-2 py-3 text-center font-medium" style={{color:'#a855f7'}}>{prog.semaforo?.purple || 0}</td>
                                <td className="px-2 py-3 text-center font-medium" style={{color:'#f97316'}}>{prog.semaforo?.orange || 0}</td>
                                <td className="px-2 py-3 text-center font-medium" style={{color:'#eab308'}}>{prog.semaforo?.yellow || 0}</td>
                                <td className="px-2 py-3 text-center font-medium" style={{color:'#ef4444'}}>{prog.semaforo?.red || 0}</td>
                                <td className="px-2 py-3 text-center font-medium" style={{color:'#6b7280'}}>{prog.semaforo?.dark_gray || 0}</td>
                                <td className="px-2 py-3 text-center font-medium" style={{color:'#1f2937'}}>{prog.semaforo?.black || 0}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      
                      {/* Leyenda 8 estados */}
                      <div className="flex flex-wrap items-center justify-end gap-3 text-xs text-slate-500 pt-2">
                        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#22c55e'}}></span> Con hitos</span>
                        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#3b82f6'}}></span> Acompañar</span>
                        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#eab308'}}></span> Sin hitos</span>
                        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#f97316'}}></span> Sin act.</span>
                        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#ef4444'}}></span> Riesgo</span>
                        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#6b7280'}}></span> Inactivo</span>
                        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#1f2937'}}></span> NoInició</span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-slate-500">
                      <GraduationCap className="w-12 h-12 mx-auto mb-4 opacity-30" />
                      <p>No hay datos de certificados disponibles.</p>
                      <p className="text-sm mt-2">Sube y procesa los archivos en la pestaña "Carga de Datos".</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            )}
            
            {/* TAB: CURSOS - Vista Ejecutiva (Tabla Plana) */}
            <TabsContent value="certificates">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="w-5 h-5" />
                    Vista Ejecutiva: {selectedPartner?.slug === 'cordoba-crea-futuro' ? 'Módulos' : 'Cursos'} - {selectedPartner?.name}
                  </CardTitle>
                  <CardDescription>
                    Resumen por {selectedPartner?.slug === 'cordoba-crea-futuro' ? 'módulo' : 'curso'} (course_name). {(selectedPartner?.slug === 'areandina' || selectedPartner?.slug === 'cordoba-crea-futuro') ? 'Semáforo basado en hitos calculado en tiempo real.' : 'Semáforo de 8 estados calculado en tiempo real.'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {certificatesLoading ? (
                    <div className="flex justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                    </div>
                  ) : certificates && certificates.certificates?.length > 0 ? (
                    (() => {
                      // Filtrar módulos por certificado (solo CCF)
                      const filteredCerts = selectedPartner?.slug === 'cordoba-crea-futuro' && modulosCertFilter !== "all"
                        ? certificates.certificates.filter(cert => {
                            const courseId = parseInt(cert.course_id);
                            const allowedIds = CERTIFICADOS_CCF[modulosCertFilter] || [];
                            return allowedIds.includes(courseId);
                          })
                        : certificates.certificates;
                      
                      // Recalcular totales para módulos filtrados
                      const filteredTotals = filteredCerts.reduce((acc, cert) => {
                        acc.activos += cert.activos || 0;
                        acc.terminados += cert.terminados || 0;
                        acc.green += cert.semaforo?.green || 0;
                        acc.blue += cert.semaforo?.blue || 0;
                        acc.purple += cert.semaforo?.purple || 0;
                        acc.orange += cert.semaforo?.orange || 0;
                        acc.yellow += cert.semaforo?.yellow || 0;
                        acc.red += cert.semaforo?.red || 0;
                        acc.dark_gray += cert.semaforo?.dark_gray || 0;
                        acc.black += cert.semaforo?.black || 0;
                        return acc;
                      }, { activos: 0, terminados: 0, green: 0, blue: 0, purple: 0, orange: 0, yellow: 0, red: 0, dark_gray: 0, black: 0 });
                      
                      const riesgoCount = filteredTotals.yellow + filteredTotals.red + filteredTotals.dark_gray + filteredTotals.black;
                      const tasaRiesgoFiltrada = filteredTotals.activos > 0 
                        ? Math.round((riesgoCount / filteredTotals.activos) * 1000) / 10 
                        : 0;
                      
                      return (
                    <div className="space-y-4">
                      {/* Toggle Cursando/Todos */}
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-600">Vista:</span>
                        <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                          <button
                            onClick={() => setCursosView("mes")}
                            className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                              cursosView === "mes" 
                                ? "bg-blue-600 text-white" 
                                : "bg-white text-slate-600 hover:bg-slate-50"
                            }`}
                            data-testid="cursos-view-mes"
                          >
                            Cursando este mes
                          </button>
                          <button
                            onClick={() => setCursosView("todos")}
                            className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                              cursosView === "todos" 
                                ? "bg-blue-600 text-white" 
                                : "bg-white text-slate-600 hover:bg-slate-50"
                            }`}
                            data-testid="cursos-view-todos"
                          >
                            Todos
                          </button>
                        </div>
                        {cursosView === "mes" && (
                          <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                            Filtro activo: solo alumnos cursando
                          </span>
                        )}
                      </div>
                      
                      {/* Filtro por Sección */}
                      <div className="flex items-center gap-3">
                        <span className="text-sm text-slate-600 font-medium">Filtrar por sección:</span>
                        <Select value={cursosSectionFilter} onValueChange={setCursosSectionFilter}>
                          <SelectTrigger className="w-[280px]" data-testid="cursos-section-filter">
                            <SelectValue placeholder="Todas las secciones" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todas las secciones</SelectItem>
                            {sections.map(sec => (
                              <SelectItem key={sec} value={sec}>{sec}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {cursosSectionFilter !== "all" && (
                          <Button variant="ghost" size="sm" onClick={() => setCursosSectionFilter("all")} className="text-slate-500">
                            <X className="w-4 h-4 mr-1" /> Limpiar
                          </Button>
                        )}
                      </div>
                      
                      {/* Filtro por Certificado (solo CCF) */}
                      {selectedPartner?.slug === 'cordoba-crea-futuro' && (
                        <div className="flex items-center gap-3">
                          <span className="text-sm text-slate-600 font-medium">Filtrar por certificado:</span>
                          <Select value={modulosCertFilter} onValueChange={setModulosCertFilter}>
                            <SelectTrigger className="w-[320px]" data-testid="modulos-cert-filter">
                              <SelectValue placeholder="Todos los módulos" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">Todos los módulos</SelectItem>
                              {Object.keys(CERTIFICADOS_CCF).map(certName => (
                                <SelectItem key={certName} value={certName}>{certName}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {modulosCertFilter !== "all" && (
                            <Button variant="ghost" size="sm" onClick={() => setModulosCertFilter("all")} className="text-slate-500">
                              <X className="w-4 h-4 mr-1" /> Limpiar filtro
                            </Button>
                          )}
                        </div>
                      )}
                      
                      {/* Resumen Global Compacto */}
                      <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl p-4 border border-slate-200">
                        <div className="flex flex-wrap items-center justify-between gap-4">
                          <div className="flex items-center gap-2">
                            <BarChart3 className="w-5 h-5 text-blue-600" />
                            <span className="font-semibold text-slate-800">
                              {filteredCerts.length} {selectedPartner?.slug === 'cordoba-crea-futuro' ? 'Módulos' : 'Cursos'}
                            </span>
                            {modulosCertFilter !== "all" && (
                              <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
                                Filtrado: {modulosCertFilter}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-6 text-sm">
                            <span><strong className="text-blue-600">{certificates.totals?.activos?.toLocaleString('es-AR')}</strong> Activos (únicos)</span>
                            <span><strong className="text-emerald-600">{certificates.totals?.terminados?.toLocaleString('es-AR')}</strong> Terminados</span>
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              (certificates.totals?.tasa_riesgo || 0) > 50 ? 'bg-red-100 text-red-700' : 
                              (certificates.totals?.tasa_riesgo || 0) > 20 ? 'bg-orange-100 text-orange-700' : 
                              'bg-green-100 text-green-700'
                            }`}>
                              {certificates.totals?.tasa_riesgo || 0}% Riesgo
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* KPIs Cards para Siglo-21 - 5 columnas */}
                      {selectedPartner?.slug === 'siglo-21' && (() => {
                        const totalCursos = filteredCerts.length;
                        const cursosConActivos = filteredCerts.filter(c => (c.activos || 0) > 0).length;
                        const cursosSinActivos = filteredCerts.filter(c => (c.activos || 0) === 0).length;
                        const cursosConTerminados = filteredCerts.filter(c => (c.terminados || 0) > 0).length;
                        const cursosSinTerminados = filteredCerts.filter(c => (c.terminados || 0) === 0).length;
                        return (
                          <div className="grid grid-cols-5 gap-3 mb-4">
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                              <p className="text-2xl font-bold text-blue-700">{totalCursos}</p>
                              <p className="text-xs text-blue-600">Total Cursos</p>
                              <p className="text-[10px] text-blue-400">100%</p>
                            </div>
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                              <p className="text-2xl font-bold text-green-700">{cursosConActivos}</p>
                              <p className="text-xs text-green-600">Con Activos</p>
                              <p className="text-[10px] text-green-400">{totalCursos > 0 ? (cursosConActivos / totalCursos * 100).toFixed(1) : 0}%</p>
                            </div>
                            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
                              <p className="text-2xl font-bold text-slate-700">{cursosSinActivos}</p>
                              <p className="text-xs text-slate-600">Sin Activos</p>
                              <p className="text-[10px] text-slate-400">{totalCursos > 0 ? (cursosSinActivos / totalCursos * 100).toFixed(1) : 0}%</p>
                            </div>
                            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
                              <p className="text-2xl font-bold text-emerald-700">{cursosConTerminados}</p>
                              <p className="text-xs text-emerald-600">Con Terminados</p>
                              <p className="text-[10px] text-emerald-400">{totalCursos > 0 ? (cursosConTerminados / totalCursos * 100).toFixed(1) : 0}%</p>
                            </div>
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                              <p className="text-2xl font-bold text-amber-700">{cursosSinTerminados}</p>
                              <p className="text-xs text-amber-600">Sin Terminados</p>
                              <p className="text-[10px] text-amber-400">{totalCursos > 0 ? (cursosSinTerminados / totalCursos * 100).toFixed(1) : 0}%</p>
                            </div>
                          </div>
                        );
                      })()}
                      
                      {/* Tabla Plana de Cursos con 8 estados */}
                      <div className="border border-slate-200 rounded-lg overflow-x-auto">
                        <table className="w-full text-sm min-w-[1000px]">
                          <thead className="bg-slate-100">
                            <tr>
                              <th className="px-4 py-3 text-left font-semibold text-slate-700">{selectedPartner?.slug === 'cordoba-crea-futuro' ? 'Módulo' : 'Curso'}</th>
                              <th 
                                className="px-3 py-3 text-center font-semibold text-slate-700 whitespace-nowrap cursor-pointer hover:bg-slate-200 select-none"
                                onClick={() => {
                                  if (coursesSortBy === "activos") {
                                    setCoursesSortOrder(coursesSortOrder === "desc" ? "asc" : "desc");
                                  } else {
                                    setCoursesSortBy("activos");
                                    setCoursesSortOrder("desc");
                                  }
                                }}
                              >
                                Activos {coursesSortBy === "activos" && (coursesSortOrder === "desc" ? "↓" : "↑")}
                              </th>
                              <th 
                                className="px-3 py-3 text-center font-semibold text-slate-700 cursor-pointer hover:bg-slate-200 select-none"
                                onClick={() => {
                                  if (coursesSortBy === "terminados") {
                                    setCoursesSortOrder(coursesSortOrder === "desc" ? "asc" : "desc");
                                  } else {
                                    setCoursesSortBy("terminados");
                                    setCoursesSortOrder("desc");
                                  }
                                }}
                              >
                                Term. {coursesSortBy === "terminados" && (coursesSortOrder === "desc" ? "↓" : "↑")}
                              </th>
                              <th className="px-3 py-3 text-center font-semibold text-slate-700">Riesgo</th>
                              {(selectedPartner?.slug === 'areandina' || selectedPartner?.slug === 'cordoba-crea-futuro') ? (
                                <>
                                  <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#22c55e'}}></span> <span className="text-xs" style={{color:'#22c55e'}}>CHi</span></th>
                                  <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#3b82f6'}}></span> <span className="text-xs" style={{color:'#3b82f6'}}>Aco</span></th>
                                  <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#eab308'}}></span> <span className="text-xs" style={{color:'#eab308'}}>SiH</span></th>
                                  <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#f97316'}}></span> <span className="text-xs" style={{color:'#f97316'}}>SiA</span></th>
                                  <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#ef4444'}}></span> <span className="text-xs" style={{color:'#ef4444'}}>Rie</span></th>
                                  <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#6b7280'}}></span> <span className="text-xs" style={{color:'#6b7280'}}>Ina</span></th>
                                  <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#1f2937'}}></span> <span className="text-xs" style={{color:'#1f2937'}}>NoI</span></th>
                                </>
                              ) : (
                                <>
                                  <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#22c55e'}}></span> <span className="text-xs" style={{color:'#22c55e'}}>Sal</span></th>
                                  <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#3b82f6'}}></span> <span className="text-xs" style={{color:'#3b82f6'}}>Aco</span></th>
                                  <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#a855f7'}}></span> <span className="text-xs" style={{color:'#a855f7'}}>Com</span></th>
                                  <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#f97316'}}></span> <span className="text-xs" style={{color:'#f97316'}}>Baj</span></th>
                                  <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#eab308'}}></span> <span className="text-xs" style={{color:'#eab308'}}>SinA</span></th>
                                  <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#ef4444'}}></span> <span className="text-xs" style={{color:'#ef4444'}}>Rie</span></th>
                                  <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#6b7280'}}></span> <span className="text-xs" style={{color:'#6b7280'}}>Ina</span></th>
                                  <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#1f2937'}}></span> <span className="text-xs" style={{color:'#1f2937'}}>NoI</span></th>
                                </>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              // Ordenar primero
                              const sortedCerts = [...filteredCerts].sort((a, b) => {
                                if (!coursesSortBy) return 0;
                                const aVal = coursesSortBy === "activos" ? (a.activos || 0) : (a.terminados || 0);
                                const bVal = coursesSortBy === "activos" ? (b.activos || 0) : (b.terminados || 0);
                                return coursesSortOrder === "desc" ? bVal - aVal : aVal - bVal;
                              });
                              
                              // Paginar solo para Siglo-21
                              const paginatedCerts = selectedPartner?.slug === 'siglo-21'
                                ? sortedCerts.slice((coursesPage - 1) * SIGLO21_PAGE_SIZE, coursesPage * SIGLO21_PAGE_SIZE)
                                : sortedCerts;
                              
                              return paginatedCerts.map((cert, idx) => (
                              <tr 
                                key={idx}
                                className="bg-white hover:bg-blue-50 border-t border-slate-100 transition-colors"
                              >
                                <td className="px-4 py-3">
                                  <span className="font-medium text-slate-800">{cert.course_name}</span>
                                  <span className="text-xs text-slate-400 ml-2">({cert.sections?.length || 0} sec)</span>
                                </td>
                                <td className="px-3 py-3 text-center font-semibold text-slate-800">{cert.activos?.toLocaleString('es-AR')}</td>
                                <td className="px-3 py-3 text-center font-semibold text-emerald-600">{cert.terminados?.toLocaleString('es-AR')}</td>
                                <td className="px-3 py-3 text-center">
                                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                    cert.tasa_riesgo > 50 ? 'bg-red-100 text-red-700' : 
                                    cert.tasa_riesgo > 20 ? 'bg-orange-100 text-orange-700' : 
                                    'bg-green-100 text-green-700'
                                  }`}>
                                    {cert.tasa_riesgo}%
                                  </span>
                                </td>
                                <td className="px-2 py-3 text-center font-medium" style={{color:'#22c55e'}}>{cert.semaforo?.green || 0}</td>
                                <td className="px-2 py-3 text-center font-medium" style={{color:'#3b82f6'}}>{cert.semaforo?.blue || 0}</td>
                                <td className="px-2 py-3 text-center font-medium" style={{color:'#a855f7'}}>{cert.semaforo?.purple || 0}</td>
                                <td className="px-2 py-3 text-center font-medium" style={{color:'#f97316'}}>{cert.semaforo?.orange || 0}</td>
                                <td className="px-2 py-3 text-center font-medium" style={{color:'#eab308'}}>{cert.semaforo?.yellow || 0}</td>
                                <td className="px-2 py-3 text-center font-medium" style={{color:'#ef4444'}}>{cert.semaforo?.red || 0}</td>
                                <td className="px-2 py-3 text-center font-medium" style={{color:'#6b7280'}}>{cert.semaforo?.dark_gray || 0}</td>
                                <td className="px-2 py-3 text-center font-medium" style={{color:'#1f2937'}}>{cert.semaforo?.black || 0}</td>
                              </tr>
                              ));
                            })()}
                          </tbody>
                        </table>
                      </div>
                      
                      {/* Paginación solo para Siglo-21 */}
                      {selectedPartner?.slug === 'siglo-21' && (
                        <>
                          {/* Paginador */}
                          {filteredCerts.length > SIGLO21_PAGE_SIZE && (
                            <div className="flex items-center justify-center gap-4 mt-4">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCoursesPage(p => Math.max(1, p - 1))}
                                disabled={coursesPage === 1}
                              >
                                ← Anterior
                              </Button>
                              <span className="text-sm text-slate-600">
                                Página {coursesPage} de {Math.ceil(filteredCerts.length / SIGLO21_PAGE_SIZE)}
                              </span>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCoursesPage(p => Math.min(Math.ceil(filteredCerts.length / SIGLO21_PAGE_SIZE), p + 1))}
                                disabled={coursesPage >= Math.ceil(filteredCerts.length / SIGLO21_PAGE_SIZE)}
                              >
                                Siguiente →
                              </Button>
                            </div>
                          )}
                        </>
                      )}
                      
                      {/* Leyenda 8 estados */}
                      <div className="flex flex-wrap items-center justify-end gap-3 text-xs text-slate-500 pt-2">
                        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#22c55e'}}></span> Con hitos</span>
                        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#3b82f6'}}></span> Acompañar</span>
                        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#eab308'}}></span> Sin hitos</span>
                        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#f97316'}}></span> Sin act.</span>
                        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#ef4444'}}></span> Riesgo</span>
                        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#6b7280'}}></span> Inactivo</span>
                        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#1f2937'}}></span> NoInició</span>
                      </div>
                    </div>
                      );
                    })()
                  ) : (
                    <div className="text-center py-12 text-slate-500">
                      <Award className="w-12 h-12 mx-auto mb-4 opacity-30" />
                      <p>No hay datos de {selectedPartner?.slug === 'cordoba-crea-futuro' ? 'módulos' : 'cursos'} disponibles.</p>
                      <p className="text-sm mt-2">Sube y procesa los archivos en la pestaña "Carga de Datos".</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* TAB: SECCIONES - Vista Operativa (Tabla Plana con 8 estados) */}
            <TabsContent value="sections">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Layers className="w-5 h-5" />
                    Vista Operativa: Secciones - {selectedPartner?.name}
                  </CardTitle>
                  <CardDescription>
                    Detalle por sección (section_name). {(selectedPartner?.slug === 'areandina' || selectedPartner?.slug === 'cordoba-crea-futuro') ? 'Semáforo basado en hitos calculado en tiempo real.' : 'Semáforo de 8 estados calculado en tiempo real.'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {certificatesLoading ? (
                    <div className="flex justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                    </div>
                  ) : certificates && certificates.all_sections?.length > 0 ? (
                    <div className="space-y-4">
                      {/* Toggle Cursando/Todos */}
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-600">Vista:</span>
                        <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                          <button
                            onClick={() => setCursosView("mes")}
                            className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                              cursosView === "mes" 
                                ? "bg-purple-600 text-white" 
                                : "bg-white text-slate-600 hover:bg-slate-50"
                            }`}
                            data-testid="secciones-view-mes"
                          >
                            Cursando este mes
                          </button>
                          <button
                            onClick={() => setCursosView("todos")}
                            className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                              cursosView === "todos" 
                                ? "bg-purple-600 text-white" 
                                : "bg-white text-slate-600 hover:bg-slate-50"
                            }`}
                            data-testid="secciones-view-todos"
                          >
                            Todos
                          </button>
                        </div>
                        {cursosView === "mes" && (
                          <span className="text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded-full">
                            Filtro activo: solo alumnos cursando
                          </span>
                        )}
                      </div>
                      
                      {/* Resumen de Secciones */}
                      <div className="bg-gradient-to-r from-slate-50 to-purple-50 rounded-xl p-4 border border-slate-200">
                        <div className="flex flex-wrap items-center justify-between gap-4">
                          <div className="flex items-center gap-2">
                            <Layers className="w-5 h-5 text-purple-600" />
                            <span className="font-semibold text-slate-800">
                              {certificates.total_sections} Secciones Únicas
                            </span>
                          </div>
                          <div className="flex items-center gap-6 text-sm">
                            <span><strong className="text-blue-600">{certificates.totals?.activos?.toLocaleString('es-AR')}</strong> Activos (únicos)</span>
                            <span><strong className="text-emerald-600">{certificates.totals?.terminados?.toLocaleString('es-AR')}</strong> Terminados</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* KPIs Cards para Siglo-21 - 5 columnas */}
                      {selectedPartner?.slug === 'siglo-21' && (() => {
                        const allSections = certificates.all_sections || [];
                        const totalSecciones = allSections.length;
                        const seccionesConActivos = allSections.filter(s => (s.activos || 0) > 0).length;
                        const seccionesSinActivos = allSections.filter(s => (s.activos || 0) === 0).length;
                        const seccionesConTerminados = allSections.filter(s => (s.terminados || 0) > 0).length;
                        const seccionesSinTerminados = allSections.filter(s => (s.terminados || 0) === 0).length;
                        return (
                          <div className="grid grid-cols-5 gap-3 mb-4">
                            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center">
                              <p className="text-2xl font-bold text-purple-700">{totalSecciones}</p>
                              <p className="text-xs text-purple-600">Total Secciones</p>
                              <p className="text-[10px] text-purple-400">100%</p>
                            </div>
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                              <p className="text-2xl font-bold text-green-700">{seccionesConActivos}</p>
                              <p className="text-xs text-green-600">Con Activos</p>
                              <p className="text-[10px] text-green-400">{totalSecciones > 0 ? (seccionesConActivos / totalSecciones * 100).toFixed(1) : 0}%</p>
                            </div>
                            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
                              <p className="text-2xl font-bold text-slate-700">{seccionesSinActivos}</p>
                              <p className="text-xs text-slate-600">Sin Activos</p>
                              <p className="text-[10px] text-slate-400">{totalSecciones > 0 ? (seccionesSinActivos / totalSecciones * 100).toFixed(1) : 0}%</p>
                            </div>
                            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
                              <p className="text-2xl font-bold text-emerald-700">{seccionesConTerminados}</p>
                              <p className="text-xs text-emerald-600">Con Terminados</p>
                              <p className="text-[10px] text-emerald-400">{totalSecciones > 0 ? (seccionesConTerminados / totalSecciones * 100).toFixed(1) : 0}%</p>
                            </div>
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                              <p className="text-2xl font-bold text-amber-700">{seccionesSinTerminados}</p>
                              <p className="text-xs text-amber-600">Sin Terminados</p>
                              <p className="text-[10px] text-amber-400">{totalSecciones > 0 ? (seccionesSinTerminados / totalSecciones * 100).toFixed(1) : 0}%</p>
                            </div>
                          </div>
                        );
                      })()}
                      
                      {/* Tabla Plana de Secciones con 8 estados */}
                      <div className="border border-slate-200 rounded-lg overflow-x-auto">
                        <table className="w-full text-sm min-w-[1000px]">
                          <thead className="bg-slate-100">
                            <tr>
                              <th className="px-4 py-3 text-left font-semibold text-slate-700">Sección</th>
                              <th 
                                className="px-3 py-3 text-center font-semibold text-slate-700 cursor-pointer hover:bg-slate-200 select-none"
                                onClick={() => {
                                  if (sectionsSortBy === "activos") {
                                    setSectionsSortOrder(sectionsSortOrder === "desc" ? "asc" : "desc");
                                  } else {
                                    setSectionsSortBy("activos");
                                    setSectionsSortOrder("desc");
                                  }
                                }}
                              >
                                Activos {sectionsSortBy === "activos" && (sectionsSortOrder === "desc" ? "↓" : "↑")}
                              </th>
                              <th 
                                className="px-3 py-3 text-center font-semibold text-slate-700 cursor-pointer hover:bg-slate-200 select-none"
                                onClick={() => {
                                  if (sectionsSortBy === "terminados") {
                                    setSectionsSortOrder(sectionsSortOrder === "desc" ? "asc" : "desc");
                                  } else {
                                    setSectionsSortBy("terminados");
                                    setSectionsSortOrder("desc");
                                  }
                                }}
                              >
                                Term. {sectionsSortBy === "terminados" && (sectionsSortOrder === "desc" ? "↓" : "↑")}
                              </th>
                              <th className="px-3 py-3 text-center font-semibold text-slate-700">Riesgo</th>
                              {(selectedPartner?.slug === 'areandina' || selectedPartner?.slug === 'cordoba-crea-futuro') ? (
                                <>
                                  <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#22c55e'}}></span> <span className="text-xs" style={{color:'#22c55e'}}>CHi</span></th>
                                  <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#3b82f6'}}></span> <span className="text-xs" style={{color:'#3b82f6'}}>Aco</span></th>
                                  <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#eab308'}}></span> <span className="text-xs" style={{color:'#eab308'}}>SiH</span></th>
                                  <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#f97316'}}></span> <span className="text-xs" style={{color:'#f97316'}}>SiA</span></th>
                                  <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#ef4444'}}></span> <span className="text-xs" style={{color:'#ef4444'}}>Rie</span></th>
                                  <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#6b7280'}}></span> <span className="text-xs" style={{color:'#6b7280'}}>Ina</span></th>
                                  <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#1f2937'}}></span> <span className="text-xs" style={{color:'#1f2937'}}>NoI</span></th>
                                </>
                              ) : (
                                <>
                                  <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#22c55e'}}></span> <span className="text-xs" style={{color:'#22c55e'}}>Sal</span></th>
                                  <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#3b82f6'}}></span> <span className="text-xs" style={{color:'#3b82f6'}}>Aco</span></th>
                                  <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#a855f7'}}></span> <span className="text-xs" style={{color:'#a855f7'}}>Com</span></th>
                                  <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#f97316'}}></span> <span className="text-xs" style={{color:'#f97316'}}>Baj</span></th>
                                  <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#eab308'}}></span> <span className="text-xs" style={{color:'#eab308'}}>SinA</span></th>
                                  <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#ef4444'}}></span> <span className="text-xs" style={{color:'#ef4444'}}>Rie</span></th>
                                  <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#6b7280'}}></span> <span className="text-xs" style={{color:'#6b7280'}}>Ina</span></th>
                                  <th className="px-2 py-3 text-center whitespace-nowrap"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{backgroundColor:'#1f2937'}}></span> <span className="text-xs" style={{color:'#1f2937'}}>NoI</span></th>
                                </>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              // Ordenar primero
                              const sortedSections = [...certificates.all_sections].sort((a, b) => {
                                if (!sectionsSortBy) return 0;
                                const aVal = sectionsSortBy === "activos" ? (a.activos || 0) : (a.terminados || 0);
                                const bVal = sectionsSortBy === "activos" ? (b.activos || 0) : (b.terminados || 0);
                                return sectionsSortOrder === "desc" ? bVal - aVal : aVal - bVal;
                              });
                              
                              // Paginar solo para Siglo-21
                              const paginatedSections = selectedPartner?.slug === 'siglo-21'
                                ? sortedSections.slice((sectionsPage - 1) * SIGLO21_PAGE_SIZE, sectionsPage * SIGLO21_PAGE_SIZE)
                                : sortedSections;
                              
                              return paginatedSections.map((section, idx) => (
                              <tr 
                                key={idx}
                                className="bg-white hover:bg-blue-50 border-t border-slate-100 transition-colors"
                              >
                                <td className="px-4 py-3">
                                  <span className="font-medium text-slate-800">{section.section_name}</span>
                                </td>
                                <td className="px-3 py-3 text-center font-semibold text-slate-800">{section.activos?.toLocaleString('es-AR')}</td>
                                <td className="px-3 py-3 text-center font-semibold text-emerald-600">{section.terminados?.toLocaleString('es-AR')}</td>
                                <td className="px-3 py-3 text-center">
                                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                                    section.tasa_riesgo > 50 ? 'bg-red-100 text-red-700' : 
                                    section.tasa_riesgo > 20 ? 'bg-orange-100 text-orange-700' : 
                                    'bg-green-100 text-green-700'
                                  }`}>
                                    {section.tasa_riesgo}%
                                  </span>
                                </td>
                                <td className="px-2 py-3 text-center font-medium" style={{color:'#22c55e'}}>{section.semaforo?.green || 0}</td>
                                <td className="px-2 py-3 text-center font-medium" style={{color:'#3b82f6'}}>{section.semaforo?.blue || 0}</td>
                                <td className="px-2 py-3 text-center font-medium" style={{color:'#a855f7'}}>{section.semaforo?.purple || 0}</td>
                                <td className="px-2 py-3 text-center font-medium" style={{color:'#f97316'}}>{section.semaforo?.orange || 0}</td>
                                <td className="px-2 py-3 text-center font-medium" style={{color:'#eab308'}}>{section.semaforo?.yellow || 0}</td>
                                <td className="px-2 py-3 text-center font-medium" style={{color:'#ef4444'}}>{section.semaforo?.red || 0}</td>
                                <td className="px-2 py-3 text-center font-medium" style={{color:'#6b7280'}}>{section.semaforo?.dark_gray || 0}</td>
                                <td className="px-2 py-3 text-center font-medium" style={{color:'#1f2937'}}>{section.semaforo?.black || 0}</td>
                              </tr>
                              ));
                            })()}
                          </tbody>
                        </table>
                      </div>
                      
                      {/* Paginación solo para Siglo-21 */}
                      {selectedPartner?.slug === 'siglo-21' ? (
                        <>
                          {/* Paginador */}
                          {(certificates.all_sections?.length || 0) > SIGLO21_PAGE_SIZE && (
                            <div className="flex items-center justify-center gap-4 mt-4">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setSectionsPage(p => Math.max(1, p - 1))}
                                disabled={sectionsPage === 1}
                              >
                                ← Anterior
                              </Button>
                              <span className="text-sm text-slate-600">
                                Página {sectionsPage} de {Math.ceil((certificates.all_sections?.length || 0) / SIGLO21_PAGE_SIZE)}
                              </span>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setSectionsPage(p => Math.min(Math.ceil((certificates.all_sections?.length || 0) / SIGLO21_PAGE_SIZE), p + 1))}
                                disabled={sectionsPage >= Math.ceil((certificates.all_sections?.length || 0) / SIGLO21_PAGE_SIZE)}
                              >
                                Siguiente →
                              </Button>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-sm text-slate-500">
                          Mostrando {certificates.all_sections?.length || 0} secciones únicas
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-slate-500">
                      <Layers className="w-12 h-12 mx-auto mb-4 opacity-30" />
                      <p>No hay datos de secciones disponibles.</p>
                      <p className="text-sm mt-2">Sube y procesa los archivos en la pestaña "Carga de Datos".</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* TAB 3: EVOLUTION - SISTEMA 8 ESTADOS */}
            <TabsContent value="evolution">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Evolución - Sistema de 8 Estados
                    {evolution.length > 0 && evolution[evolution.length - 1]?.is_live && (
                      <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full border border-blue-200 animate-pulse">
                        ● En vivo
                      </span>
                    )}
                  </CardTitle>
                  <CardDescription>
                    Seguimiento histórico del semáforo académico
                    {evolution.length > 0 && evolution[evolution.length - 1]?.is_live && (
                      <span className="ml-1 text-blue-600">
                        — El último punto es el estado actual calculado en tiempo real (no guardado)
                      </span>
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {evolution.length > 0 ? (
                    <div className="space-y-6">
                      {/* Summary Cards - 8 estados en 2 filas de 4 */}
                      <div className="grid grid-cols-4 gap-3">
                        <div className="rounded-lg p-3 border" style={{backgroundColor:'#f0fdf4', borderColor:'#bbf7d0'}}>
                          <p className="text-sm font-medium" style={{color:'#22c55e'}}>
                            Activo con hitos
                          </p>
                          <p className="text-xl font-bold" style={{color:'#16a34a'}}>
                            {evolution[evolution.length - 1]?.green || 0}
                          </p>
                        </div>
                        <div className="rounded-lg p-3 border" style={{backgroundColor:'#eff6ff', borderColor:'#bfdbfe'}}>
                          <p className="text-sm font-medium" style={{color:'#3b82f6'}}>Acompañar</p>
                          <p className="text-xl font-bold" style={{color:'#2563eb'}}>
                            {evolution[evolution.length - 1]?.blue || 0}
                          </p>
                        </div>
                        <div className="rounded-lg p-3 border" style={{backgroundColor:'#fefce8', borderColor:'#fef08a'}}>
                          <p className="text-sm font-medium" style={{color:'#eab308'}}>
                            Activo sin hitos
                          </p>
                          <p className="text-xl font-bold" style={{color:'#ca8a04'}}>
                            {evolution[evolution.length - 1]?.yellow || 0}
                          </p>
                        </div>
                        <div className="rounded-lg p-3 border" style={{backgroundColor:'#fff7ed', borderColor:'#fed7aa'}}>
                          <p className="text-sm font-medium" style={{color:'#f97316'}}>
                            Sin actividad
                          </p>
                          <p className="text-xl font-bold" style={{color:'#ea580c'}}>
                            {evolution[evolution.length - 1]?.orange || 0}
                          </p>
                        </div>
                        <div className="rounded-lg p-3 border" style={{backgroundColor:'#fef2f2', borderColor:'#fecaca'}}>
                          <p className="text-sm font-medium" style={{color:'#ef4444'}}>En riesgo</p>
                          <p className="text-xl font-bold" style={{color:'#dc2626'}}>
                            {evolution[evolution.length - 1]?.red || 0}
                          </p>
                        </div>
                        <div className="rounded-lg p-3 border" style={{backgroundColor:'#f9fafb', borderColor:'#d1d5db'}}>
                          <p className="text-sm font-medium" style={{color:'#6b7280'}}>Inactivo</p>
                          <p className="text-xl font-bold" style={{color:'#4b5563'}}>
                            {evolution[evolution.length - 1]?.dark_gray || 0}
                          </p>
                        </div>
                        <div className="rounded-lg p-3 border" style={{backgroundColor:'#f3f4f6', borderColor:'#9ca3af'}}>
                          <p className="text-sm font-medium" style={{color:'#1f2937'}}>No iniciado</p>
                          <p className="text-xl font-bold" style={{color:'#111827'}}>
                            {evolution[evolution.length - 1]?.black || 0}
                          </p>
                        </div>
                      </div>
                      
                      {/* Line Chart - 8 líneas */}
                      <div className="h-[400px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={evolution} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis 
                              dataKey="date" 
                              tick={{ fontSize: 11, fill: "#64748b" }}
                              tickFormatter={(val) => {
                                const parts = val.split("-");
                                return `${parts[2]}/${parts[1]}`;
                              }}
                              axisLine={{ stroke: "#cbd5e1" }}
                            />
                            <YAxis 
                              tick={{ fontSize: 11, fill: "#64748b" }}
                              axisLine={{ stroke: "#cbd5e1" }}
                              label={{ value: "Cantidad de Alumnos", angle: -90, position: "insideLeft", fontSize: 12, fill: "#64748b" }}
                            />
                            <RechartsTooltip
                              contentStyle={{ 
                                backgroundColor: "white", 
                                border: "1px solid #e2e8f0",
                                borderRadius: "8px",
                                boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)"
                              }}
                              labelFormatter={(label, payload) => {
                                const isLive = payload?.[0]?.payload?.is_live;
                                return isLive 
                                  ? `📍 ${label} (En vivo - tiempo real)`
                                  : `Fecha: ${label}`;
                              }}
                              formatter={(value, name) => {
                                const labels = {
                                    green: 'Activo con hitos', blue: 'Acompañar', purple: 'Finalizado',
                                    orange: 'Sin actividad', yellow: 'Activo sin hitos', red: 'En riesgo',
                                    dark_gray: 'Inactivo', black: 'No iniciado'
                                  };
                                return [formatNumber(value), labels[name] || name];
                              }}
                            />
                            <Legend 
                              verticalAlign="top"
                              height={36}
                              formatter={(value) => {
                                const labels = {
                                    green: 'ConHitos', blue: 'Acompañar', purple: 'Finaliz',
                                    orange: 'SinAct', yellow: 'SinHitos', red: 'Riesgo',
                                    dark_gray: 'Inactivo', black: 'NoInició'
                                  };
                                return labels[value] || value;
                              }}
                            />
                            {/* 8 líneas con colores exactos - puntos live son huecos */}
                            <Line 
                              type="monotone" 
                              dataKey="green" 
                              name="green" 
                              stroke="#22c55e" 
                              strokeWidth={3} 
                              dot={(props) => {
                                const { cx, cy, payload } = props;
                                if (payload?.is_live) {
                                  return <circle cx={cx} cy={cy} r={6} fill="white" stroke="#22c55e" strokeWidth={3} />;
                                }
                                return <circle cx={cx} cy={cy} r={4} fill="#22c55e" />;
                              }}
                            />
                            <Line 
                              type="monotone" 
                              dataKey="blue" 
                              name="blue" 
                              stroke="#3b82f6" 
                              strokeWidth={2} 
                              dot={(props) => {
                                const { cx, cy, payload } = props;
                                if (payload?.is_live) {
                                  return <circle cx={cx} cy={cy} r={5} fill="white" stroke="#3b82f6" strokeWidth={2} />;
                                }
                                return <circle cx={cx} cy={cy} r={3} fill="#3b82f6" />;
                              }}
                            />
                            <Line 
                              type="monotone" 
                              dataKey="purple" 
                              name="purple" 
                              stroke="#a855f7" 
                              strokeWidth={2} 
                              dot={(props) => {
                                const { cx, cy, payload } = props;
                                if (payload?.is_live) {
                                  return <circle cx={cx} cy={cy} r={5} fill="white" stroke="#a855f7" strokeWidth={2} />;
                                }
                                return <circle cx={cx} cy={cy} r={3} fill="#a855f7" />;
                              }}
                            />
                            <Line 
                              type="monotone" 
                              dataKey="orange" 
                              name="orange" 
                              stroke="#f97316" 
                              strokeWidth={2} 
                              dot={(props) => {
                                const { cx, cy, payload } = props;
                                if (payload?.is_live) {
                                  return <circle cx={cx} cy={cy} r={5} fill="white" stroke="#f97316" strokeWidth={2} />;
                                }
                                return <circle cx={cx} cy={cy} r={3} fill="#f97316" />;
                              }}
                            />
                            <Line 
                              type="monotone" 
                              dataKey="yellow" 
                              name="yellow" 
                              stroke="#eab308" 
                              strokeWidth={2} 
                              dot={(props) => {
                                const { cx, cy, payload } = props;
                                if (payload?.is_live) {
                                  return <circle cx={cx} cy={cy} r={5} fill="white" stroke="#eab308" strokeWidth={2} />;
                                }
                                return <circle cx={cx} cy={cy} r={3} fill="#eab308" />;
                              }}
                            />
                            <Line 
                              type="monotone" 
                              dataKey="red" 
                              name="red" 
                              stroke="#ef4444" 
                              strokeWidth={3} 
                              dot={(props) => {
                                const { cx, cy, payload } = props;
                                if (payload?.is_live) {
                                  return <circle cx={cx} cy={cy} r={6} fill="white" stroke="#ef4444" strokeWidth={3} />;
                                }
                                return <circle cx={cx} cy={cy} r={4} fill="#ef4444" />;
                              }}
                            />
                            <Line 
                              type="monotone" 
                              dataKey="dark_gray" 
                              name="dark_gray" 
                              stroke="#6b7280" 
                              strokeWidth={2} 
                              dot={(props) => {
                                const { cx, cy, payload } = props;
                                if (payload?.is_live) {
                                  return <circle cx={cx} cy={cy} r={5} fill="white" stroke="#6b7280" strokeWidth={2} />;
                                }
                                return <circle cx={cx} cy={cy} r={3} fill="#6b7280" />;
                              }}
                            />
                            <Line 
                              type="monotone" 
                              dataKey="black" 
                              name="black" 
                              stroke="#1f2937" 
                              strokeWidth={3} 
                              dot={(props) => {
                                const { cx, cy, payload } = props;
                                if (payload?.is_live) {
                                  return <circle cx={cx} cy={cy} r={6} fill="white" stroke="#1f2937" strokeWidth={3} />;
                                }
                                return <circle cx={cx} cy={cy} r={4} fill="#1f2937" />;
                              }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                      
                      {/* Instructions */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-sm text-blue-800">
                          <><strong>Objetivo:</strong> Las curvas verde (Activo con hitos) y azul (Acompañar) deben subir. Las curvas roja (En riesgo), gris (Inactivo) y negra (No iniciado) deben bajar.</>
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="h-[400px] flex items-center justify-center text-slate-400">
                      <div className="text-center">
                        <TrendingUp className="w-16 h-16 mx-auto mb-4 opacity-30" />
                        <p>No hay datos históricos aún.</p>
                        <p className="text-sm">Procesa archivos para comenzar a trackear la evolución.</p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* TAB 4: EXPLORER */}
            <TabsContent value="explorer">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Users className="w-5 h-5" />
                        Explorador 360 de Alumnos
                      </CardTitle>
                      <CardDescription>{formatNumber(studentsTotal)} alumnos registrados</CardDescription>
                    </div>
                    
                    {/* Filters and Export */}
                    <div className="flex items-center gap-3 flex-wrap">
                      {/* FIX 2026-10-26 (Punto 1, Cande/CTO): autoComplete="off" (v1) no
                          alcanza - Chrome lo ignora a propósito para su autofill de
                          direcciones/emails guardados. Técnica combinada: (1) decoy oculto
                          tipo email ANTES del campo real, para que Chrome autocomplete AHÍ
                          y no en el buscador real; (2) buscador real sin name/id/type
                          reconocibles, autoComplete="new-password" (Chrome sí lo respeta);
                          (3) readOnly hasta el primer focus (frena el autofill de carga de
                          página); (4) failsafe: si igual se cuela un valor antes de que el
                          usuario toque el campo, se limpia solo. No puede validarse 100%
                          sin un Chrome real con el email de Cande guardado - confirmar en
                          producción. */}
                      <input
                        type="email"
                        name="email"
                        autoComplete="email"
                        tabIndex={-1}
                        aria-hidden="true"
                        style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
                        data-testid="search-autofill-decoy"
                      />
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          ref={searchInputRef}
                          placeholder="Buscar alumno (nombre)..."
                          title="Busca por nombre o email del alumno"
                          value={searchTerm}
                          onChange={(e) => { setSearchTerm(e.target.value); setStudentsPage(1); }}
                          onFocus={() => setSearchFieldLocked(false)}
                          readOnly={searchFieldLocked}
                          className="pl-9 w-[220px]"
                          name="qx-flt-7a3"
                          id="qx-flt-7a3"
                          autoComplete="new-password"
                          data-testid="search-input"
                        />
                      </div>
                      {(searchTerm || (riskFilter && riskFilter !== "all") || (npsFilter && npsFilter !== "all") ||
                        (periodFilter && periodFilter !== "all") ||
                        (selectedPartner?.slug === 'cordoba-crea-futuro' && etapaCCF !== "todas")) && (
                        <Badge
                          variant="outline"
                          className="bg-amber-50 border-amber-300 text-amber-700 gap-1.5"
                          data-testid="filtro-activo-chip"
                        >
                          Filtro activo{searchTerm ? `: "${searchTerm}"` : ""}
                          <button
                            type="button"
                            onClick={() => {
                              setSearchTerm("");
                              setRiskFilter("all");
                              setNpsFilter("all");
                              setPeriodFilter("all");
                              setEtapaCCF("todas");
                              setStudentsPage(1);
                            }}
                            className="ml-1 hover:text-amber-900 font-semibold"
                            data-testid="filtro-activo-limpiar"
                          >
                            × limpiar
                          </button>
                        </Badge>
                      )}
                      
                      <Select value={riskFilter} onValueChange={(v) => { setRiskFilter(v); setStudentsPage(1); }}>
                        <SelectTrigger className="w-[180px]" data-testid="risk-filter">
                          <Filter className="w-4 h-4 mr-2 text-slate-400" />
                          <SelectValue placeholder="Filtrar semáforo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos los estados</SelectItem>
                          <SelectItem value="purple">🟣 Finalizado</SelectItem>
                          <SelectItem value="green">🟢 Activo con hitos</SelectItem>
                          <SelectItem value="blue">🔵 Acompañar</SelectItem>
                          <SelectItem value="yellow">🟡 Activo sin hitos</SelectItem>
                          <SelectItem value="orange">🟠 Sin actividad</SelectItem>
                          <SelectItem value="red">🔴 En riesgo</SelectItem>
                          <SelectItem value="dark_gray">⚫ Inactivo</SelectItem>
                          <SelectItem value="black">⬛ No iniciado</SelectItem>
                          <SelectItem value="yellow,red,dark_gray,black">⚠️ Requieren atención</SelectItem>
                          <SelectItem value="purple,green,blue">✅ Sin riesgo</SelectItem>
                          <SelectItem value="green,blue,yellow">✅ Activos</SelectItem>
                          <SelectItem value="orange,red,dark_gray,black">⚠️ En Riesgo / Inactivos</SelectItem>
                        </SelectContent>
                      </Select>
                      
                      {/* Period Filter */}
                      {availablePeriods.length > 0 && (
                        <Select value={periodFilter} onValueChange={(v) => { setPeriodFilter(v); setStudentsPage(1); }}>
                          <SelectTrigger className="w-[160px]" data-testid="period-filter">
                            <CalendarDays className="w-4 h-4 mr-2 text-slate-400" />
                            <SelectValue placeholder="Período" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todos los períodos</SelectItem>
                            {availablePeriods.map((p) => (
                              <SelectItem key={p.mes} value={p.mes}>
                                {p.mes_display} ({p.alumnos})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      
                      {/* TODO: Filtro por programa - pendiente de implementar correctamente
                      {programs.length > 0 && (
                        <Select value={programFilter} onValueChange={(v) => { setProgramFilter(v); setStudentsPage(1); }}>
                          <SelectTrigger className="w-[200px]" data-testid="program-filter-explorer">
                            <SelectValue placeholder="Programa" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todos los Programas</SelectItem>
                            {programs.map((prog) => (
                              <SelectItem key={prog} value={prog}>{prog}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      */}
                      
                      {/* NPS Filter */}
                      <Select value={npsFilter} onValueChange={(v) => { setNpsFilter(v); setStudentsPage(1); }}>
                        <SelectTrigger className="w-[150px]" data-testid="nps-filter">
                          <MessageSquare className="w-4 h-4 mr-2 text-slate-400" />
                          <SelectValue placeholder="Filtrar NPS" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos NPS</SelectItem>
                          <SelectItem value="detractor">🔴 Detractores</SelectItem>
                          <SelectItem value="passive">🟡 Neutrales</SelectItem>
                          <SelectItem value="promoter">🟢 Promotores</SelectItem>
                          <SelectItem value="sin_nps">Sin respuesta</SelectItem>
                        </SelectContent>
                      </Select>
                      
                      {/* Filtro Etapa 1/Etapa 2 - Solo CCF (2026-09-29, export CASA) */}
                      {selectedPartner?.slug === 'cordoba-crea-futuro' && (
                        <Select value={etapaCCF} onValueChange={(v) => { setEtapaCCF(v); setStudentsPage(1); }}>
                          <SelectTrigger className="w-[160px]" data-testid="etapa-ccf-filter-explorer">
                            <SelectValue placeholder="Todas las etapas" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="todas">Todas las etapas</SelectItem>
                            <SelectItem value="etapa_1">Etapa 1</SelectItem>
                            <SelectItem value="etapa_2">Etapa 2</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                      
                      {/* Export Buttons */}
                      <div className="flex gap-2">
                        <Button
                          variant="default"
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          onClick={async () => {
                            try {
                              const params = {};
                              if (riskFilter && riskFilter !== "all") params.risk_filter = riskFilter;
                              if (npsFilter && npsFilter !== "all") params.nps_filter = npsFilter;
                              if (searchTerm) params.search = searchTerm;
                              if (programFilter && programFilter !== "all") params.program = programFilter;
                              if (periodFilter && periodFilter !== "all") params.period_filter = periodFilter;
                              const qs = new URLSearchParams(params).toString();
                              const url = `${api.getExportUrl(selectedPartnerId, {})}${qs ? `&${qs}` : ''}`;
                              await secureExportDownload({ url, filename: `alumnos_${selectedPartner?.slug || 'export'}_${new Date().toISOString().split('T')[0]}.xlsx` });
                            } catch (err) {
                              if (err.message !== 'cancelado') {
                                console.error("Export Excel error:", err);
                                toast.error("Error al descargar Excel: " + (err.message || "Error desconocido"));
                              }
                            }
                          }}
                          data-testid="export-excel-btn"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Excel cifrado
                        </Button>
                        {/* Exportar base (CASA) - RBAC: superadmin/gestor_academico/casa - P0 (2026-09-29) */}
                        {(authUser?.role === 'superadmin' || authUser?.role === 'gestor_academico' || authUser?.role === 'casa') && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                            title="Exporta la base COMPLETA del partner (ignora el buscador de arriba, respeta estado/NPS/período/etapa)"
                            onClick={async () => {
                              try {
                                const params = {};
                                if (riskFilter && riskFilter !== "all") params.risk_filter = riskFilter;
                                if (npsFilter && npsFilter !== "all") params.nps_filter = npsFilter;
                                // FIX 2026-10-24 (Bug A, Cande/CTO): "Exportar base" debe traer la
                                // base COMPLETA del partner, no el subconjunto del buscador por-alumno
                                // (search/email) - si el buscador quedaba con un valor (ej. autofill
                                // del navegador con el email del usuario logueado), el export salía
                                // con 0 filas. Los filtros legítimos (etapa/estado/nps/período) SÍ
                                // se respetan, search/email NO.
                                if (periodFilter && periodFilter !== "all") params.period_filter = periodFilter;
                                if (selectedPartner?.slug === 'cordoba-crea-futuro' && etapaCCF !== "todas") params.etapa = etapaCCF;
                                const qs = new URLSearchParams(params).toString();
                                const url = `${api.getStudentsCasaExportUrl(selectedPartnerId, {})}${qs ? `&${qs}` : ''}`;
                                const casaSlug = selectedPartner?.slug === 'cordoba-crea-futuro' ? 'ccf' : (selectedPartner?.slug || selectedPartnerId);
                                // FIX 2026-10-26 (Bug B v2): .xlsx con clave nativa de Office en vez
                                // de .zip (el "Extraer todo" de Windows no soporta AES en zip) - se
                                // abre con doble click en Excel.
                                await secureExportDownload({ url, filename: `alumnos_${casaSlug}_casa_${new Date().toISOString().split('T')[0]}.xlsx` });
                              } catch (err) {
                                if (err.message !== 'cancelado') {
                                  console.error("Export CASA error:", err);
                                  toast.error("Error al exportar base CASA: " + (err.message || "Error desconocido"));
                                }
                              }
                            }}
                            data-testid="export-casa-btn"
                          >
                            <FileDown className="w-4 h-4 mr-1" />
                            Exportar base completa (Excel cifrado)
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Quick Filter Chips - 8 Estados */}
                  <div className="flex gap-2 mt-4 flex-wrap items-center">
                    <Badge 
                      variant={riskFilter === "all" ? "default" : "outline"}
                      className="cursor-pointer hover:bg-slate-100"
                      onClick={() => { setRiskFilter("all"); setStudentsPage(1); }}
                    >
                      Todos ({studentsTotal})
                    </Badge>
                    <Badge 
                      variant={riskFilter === "green" ? "default" : "outline"}
                      className="cursor-pointer hover:bg-green-50 text-green-600 border-green-300"
                      onClick={() => { setRiskFilter("green"); setStudentsPage(1); }}
                    >
                      🟢 Activo con hitos ({studentsColorCounts.green || 0})
                    </Badge>
                    <Badge 
                      variant={riskFilter === "purple" ? "default" : "outline"}
                      className="cursor-pointer hover:bg-purple-50 text-purple-600 border-purple-300"
                      onClick={() => { setRiskFilter("purple"); setStudentsPage(1); }}
                    >
                      🟣 Finalizado ({studentsColorCounts.purple || 0})
                    </Badge>
                    <Badge 
                      variant={riskFilter === "blue" ? "default" : "outline"}
                      className="cursor-pointer hover:bg-blue-50 text-blue-600 border-blue-300"
                      onClick={() => { setRiskFilter("blue"); setStudentsPage(1); }}
                    >
                      🔵 Acompañar ({studentsColorCounts.blue || 0})
                    </Badge>
                    <Badge 
                      variant={riskFilter === "yellow" ? "default" : "outline"}
                      className="cursor-pointer hover:bg-yellow-50 text-yellow-600 border-yellow-300"
                      onClick={() => { setRiskFilter("yellow"); setStudentsPage(1); }}
                    >
                      🟡 Activo sin hitos ({studentsColorCounts.yellow || 0})
                    </Badge>
                    <Badge 
                      variant={riskFilter === "orange" ? "default" : "outline"}
                      className="cursor-pointer hover:bg-orange-50 text-orange-600 border-orange-300"
                      onClick={() => { setRiskFilter("orange"); setStudentsPage(1); }}
                    >
                      🟠 Sin actividad ({studentsColorCounts.orange || 0})
                    </Badge>
                    <Badge 
                      variant={riskFilter === "red" ? "default" : "outline"}
                      className="cursor-pointer hover:bg-red-50 text-red-600 border-red-300"
                      onClick={() => { setRiskFilter("red"); setStudentsPage(1); }}
                    >
                      🔴 En riesgo ({studentsColorCounts.red || 0})
                    </Badge>
                    <Badge 
                      variant={riskFilter === "dark_gray" ? "default" : "outline"}
                      className="cursor-pointer hover:bg-gray-100 text-gray-600 border-gray-400"
                      onClick={() => { setRiskFilter("dark_gray"); setStudentsPage(1); }}
                    >
                      ⚫ Inactivo ({studentsColorCounts.dark_gray || 0})
                    </Badge>
                    <Badge 
                      variant={riskFilter === "black" ? "default" : "outline"}
                      className="cursor-pointer hover:bg-gray-100 text-gray-800 border-gray-500"
                      onClick={() => { setRiskFilter("black"); setStudentsPage(1); }}
                    >
                      ⬛ {selectedPartner?.slug === 'areandina' || selectedPartner?.slug === 'cordoba-crea-futuro' ? 'No iniciado' : 'No Iniciado'} ({studentsColorCounts.black || 0})
                    </Badge>
                    
                    {/* Separador visual */}
                    <span className="text-slate-300">|</span>
                    
                    {/* Chip de Detractores NPS */}
                    <Badge 
                      variant={npsFilter === "detractor" ? "default" : "outline"}
                      className="cursor-pointer hover:bg-red-100 text-red-700 border-red-400 bg-red-50"
                      onClick={() => { setNpsFilter("detractor"); setStudentsPage(1); }}
                    >
                      🔴 Detractores NPS ({detractorsCount || 0})
                    </Badge>
                    
                    {/* Criteria Legend */}
                    <div className="ml-auto">
                      <RiskCriteriaLegend partnerSlug={selectedPartner?.slug} />
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[450px]">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>Nombre</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Programa</TableHead>
                          <TableHead>Secciones</TableHead>
                          <TableHead className="text-right">Días Inact.</TableHead>
                          <TableHead className="text-right">% Aprob.</TableHead>
                          <TableHead className="text-center">Actividad</TableHead>
                          <TableHead className="text-center">Semáforo</TableHead>
                          <TableHead 
                            className="text-center cursor-pointer hover:bg-slate-100 select-none"
                            onClick={() => {
                              if (sortBy === "nps") {
                                // Toggle order: asc -> desc -> null
                                if (sortOrder === "asc") {
                                  setSortOrder("desc");
                                } else {
                                  setSortBy(null);
                                  setSortOrder("asc");
                                }
                              } else {
                                setSortBy("nps");
                                setSortOrder("asc");
                              }
                              setStudentsPage(1);
                            }}
                            data-testid="nps-sort-header"
                          >
                            <div className="flex flex-col items-center justify-center">
                              <div className="flex items-center gap-1">
                                NPS
                                {sortBy === "nps" && (
                                  <span className="text-xs">
                                    {sortOrder === "asc" ? "↑" : "↓"}
                                  </span>
                                )}
                              </div>
                              {detractorsCount > 0 && (
                                <span className="text-[10px] text-red-600 font-medium">
                                  ({detractorsCount} Det.)
                                </span>
                              )}
                            </div>
                          </TableHead>
                          <TableHead className="text-center">Acciones</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {students.map((student) => (
                          <TableRow 
                            key={student.id} 
                            className={`hover:bg-slate-50/50 ${
                              student.current_risk === "red" ? "bg-red-50/30" : 
                              student.current_risk === "yellow" ? "bg-amber-50/30" : ""
                            }`}
                          >
                            <TableCell className="font-medium">{student.name || "Sin nombre"}</TableCell>
                            <TableCell className="text-sm text-slate-500 max-w-[120px] truncate" title={student.email}>
                              {student.email || "—"}
                            </TableCell>
                            <TableCell className="text-sm text-slate-600 max-w-[120px] truncate" title={student.program_name}>
                              {student.program_name || "—"}
                            </TableCell>
                            <TableCell className="text-xs">
                              {student.sections && student.sections.length > 0 ? (
                                <div className="flex flex-wrap gap-1">
                                  {student.sections.slice(0, 2).map((sec, idx) => (
                                    <Badge key={idx} variant="outline" className="text-xs bg-blue-50 border-blue-200 text-blue-700">
                                      {sec}
                                    </Badge>
                                  ))}
                                  {student.sections.length > 2 && (
                                    <Badge variant="outline" className="text-xs">+{student.sections.length - 2}</Badge>
                                  )}
                                </div>
                              ) : "—"}
                            </TableCell>
                            <TableCell className="text-right font-mono tabular-nums">
                              {student.days_inactive !== null ? (
                                <span className={student.days_inactive > 14 ? "text-red-600 font-bold" : 
                                               student.days_inactive > 7 ? "text-amber-600" : "text-green-600"}>
                                  {student.days_inactive}
                                </span>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-mono tabular-nums">
                              {student.approval_rate !== null ? (
                                <span className={student.approval_rate < 30 ? "text-red-600 font-bold" :
                                               student.approval_rate < 60 ? "text-amber-600" : "text-green-600"}>
                                  {formatPercent(student.approval_rate)}
                                </span>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              <Badge variant="outline" className={`text-xs ${
                                student.activity_status === "active" ? "bg-green-50 text-green-700 border-green-200" :
                                student.activity_status === "inactive" ? "bg-amber-50 text-amber-700 border-amber-200" :
                                "bg-gray-100 text-gray-600 border-gray-200"
                              }`}>
                                {student.activity_status === "active" ? "Activo" :
                                 student.activity_status === "inactive" ? "Inactivo" :
                                 student.activity_status === "concluded" ? "Concluido" : "—"}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              {student.current_risk && <RiskBadge color={student.current_risk} partnerSlug={selectedPartner?.slug} />}
                            </TableCell>
                            <TableCell className="text-center">
                              <NPSBadge npsData={student.nps} />
                            </TableCell>
                            <TableCell className="text-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedStudent(student);
                                  setShowProfile360(true);
                                }}
                                className="text-slate-500 hover:text-blue-600"
                                data-testid={`view-profile-${student.id}`}
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                <span className="hidden sm:inline">Ver Perfil</span>
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        {students.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={11} className="text-center py-8 text-slate-400">
                              No hay alumnos que mostrar
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                  
                  {/* Pagination and Export Info */}
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <div className="flex items-center gap-4">
                      <p className="text-sm text-slate-500">
                        Página {studentsPage} de {totalPages || 1}
                      </p>
                      {riskFilter && riskFilter !== "all" && (
                        <p className="text-sm text-blue-600">
                          Filtro activo: {(selectedPartner?.slug === 'areandina' || selectedPartner?.slug === 'cordoba-crea-futuro') 
                            ? ({green: "Activo con hitos", blue: "Acompañar", purple: "Finalizado", yellow: "Activo sin hitos", orange: "Sin actividad", red: "En riesgo", dark_gray: "Inactivo", black: "No iniciado"}[riskFilter.split(",")[0]] || riskFilter.split(",")[0])
                            : getRiskLabelStatic(riskFilter.split(",")[0])}
                          {riskFilter.includes(",") && " + más"}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setStudentsPage(p => Math.max(1, p - 1))}
                        disabled={studentsPage === 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setStudentsPage(p => Math.min(totalPages || 1, p + 1))}
                        disabled={studentsPage === (totalPages || 1)}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            {/* TAB: GEOGRAFÍA - OCULTA por solicitud del CTO */}
            {false && selectedPartner?.slug === 'cordoba-crea-futuro' && (
            <TabsContent value="geografia">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    Distribución Geográfica — CCF
                  </CardTitle>
                  <CardDescription>Semáforo de gestión académica por localidad</CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingGeo ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
                    </div>
                  ) : !geoData || geoData.localidades?.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <MapPin className="w-12 h-12 mx-auto mb-4 opacity-30" />
                      <p className="text-lg font-medium">Sin datos de localidad</p>
                      <p className="text-sm mt-2">
                        Subí un archivo de geolocalización en la pestaña "Carga" (Slot 8) para ver la distribución por localidad.
                      </p>
                      <Button 
                        onClick={loadGeoData} 
                        variant="outline" 
                        className="mt-4 gap-2"
                        data-testid="reload-geo-btn"
                      >
                        <RefreshCw className="w-4 h-4" />
                        Recargar datos
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* SECCIÓN 1: Cards resumen */}
                      <div className="grid grid-cols-3 gap-4">
                        {/* Localidad más activa */}
                        {(() => {
                          const sorted = [...(geoData.localidades || [])].sort((a, b) => {
                            const activaA = (a.semaforo.green?.pct || 0) + (a.semaforo.blue?.pct || 0) + (a.semaforo.yellow?.pct || 0);
                            const activaB = (b.semaforo.green?.pct || 0) + (b.semaforo.blue?.pct || 0) + (b.semaforo.yellow?.pct || 0);
                            return activaB - activaA;
                          });
                          const top = sorted[0];
                          const pctActiva = top ? ((top.semaforo.green?.pct || 0) + (top.semaforo.blue?.pct || 0) + (top.semaforo.yellow?.pct || 0)).toFixed(1) : 0;
                          return (
                            <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4">
                              <div className="flex items-center gap-2 text-green-700 mb-2">
                                <TrendingUp className="w-4 h-4" />
                                <span className="text-sm font-medium">Localidad más activa</span>
                              </div>
                              <p className="text-xl font-bold text-green-800">{top?.localidad || '-'}</p>
                              <p className="text-sm text-green-600">{pctActiva}% activos ({top?.total || 0} alumnos)</p>
                            </div>
                          );
                        })()}
                        
                        {/* Mayor riesgo (min 10 alumnos) */}
                        {(() => {
                          const filtered = (geoData.localidades || []).filter(l => l.total >= 10);
                          const sorted = [...filtered].sort((a, b) => {
                            const riesgoA = (a.semaforo.orange?.pct || 0) + (a.semaforo.red?.pct || 0) + (a.semaforo.dark_gray?.pct || 0) + (a.semaforo.black?.pct || 0);
                            const riesgoB = (b.semaforo.orange?.pct || 0) + (b.semaforo.red?.pct || 0) + (b.semaforo.dark_gray?.pct || 0) + (b.semaforo.black?.pct || 0);
                            return riesgoB - riesgoA;
                          });
                          const top = sorted[0];
                          const pctRiesgo = top ? ((top.semaforo.orange?.pct || 0) + (top.semaforo.red?.pct || 0) + (top.semaforo.dark_gray?.pct || 0) + (top.semaforo.black?.pct || 0)).toFixed(1) : 0;
                          return (
                            <div className="bg-gradient-to-br from-red-50 to-orange-50 border border-red-200 rounded-xl p-4">
                              <div className="flex items-center gap-2 text-red-700 mb-2">
                                <AlertTriangle className="w-4 h-4" />
                                <span className="text-sm font-medium">Mayor riesgo</span>
                              </div>
                              <p className="text-xl font-bold text-red-800">{top?.localidad || '-'}</p>
                              <p className="text-sm text-red-600">{pctRiesgo}% en riesgo ({top?.total || 0} alumnos)</p>
                            </div>
                          );
                        })()}
                        
                        {/* Sin cobertura */}
                        <div className="bg-gradient-to-br from-slate-50 to-gray-50 border border-slate-200 rounded-xl p-4">
                          <div className="flex items-center gap-2 text-slate-700 mb-2">
                            <MapPin className="w-4 h-4" />
                            <span className="text-sm font-medium">Sin cobertura</span>
                          </div>
                          <p className="text-xl font-bold text-slate-800">{geoData.sin_cobertura?.toLocaleString() || 0}</p>
                          <p className="text-sm text-slate-600">alumnos sin localidad asignada</p>
                        </div>
                      </div>
                      
                      {/* SECCIÓN 2: Tabla semáforo por localidad */}
                      <div className="border rounded-xl overflow-hidden">
                        <div className="bg-slate-50 px-4 py-3 border-b flex items-center justify-between">
                          <h3 className="font-semibold text-slate-700">Semáforo por Localidad</h3>
                          <span className="text-sm text-slate-500">{geoData.total_localidades} localidades</span>
                        </div>
                        <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-slate-100 sticky top-0">
                              <tr>
                                {[
                                  { key: 'localidad', label: 'Localidad', align: 'left' },
                                  { key: 'total', label: 'Total', align: 'right' },
                                  { key: 'activos', label: 'Activos', align: 'right' },
                                  { key: 'pct_activos', label: '% Activos', align: 'right' },
                                  { key: 'green', label: '🟢', align: 'right', color: '#16a34a' },
                                  { key: 'blue', label: '🔵', align: 'right', color: '#2563eb' },
                                  { key: 'yellow', label: '🟡', align: 'right', color: '#ca8a04' },
                                  { key: 'orange', label: '🟠', align: 'right', color: '#ea580c' },
                                  { key: 'red', label: '🔴', align: 'right', color: '#dc2626' },
                                  { key: 'dark_gray', label: '⚫', align: 'right', color: '#6b7280' },
                                  { key: 'black', label: '⬛', align: 'right', color: '#111827' }
                                ].map(col => (
                                  <th 
                                    key={col.key}
                                    onClick={() => {
                                      if (geoSortColumn === col.key) {
                                        setGeoSortDir(geoSortDir === 'asc' ? 'desc' : 'asc');
                                      } else {
                                        setGeoSortColumn(col.key);
                                        setGeoSortDir('asc');
                                      }
                                    }}
                                    className={`${col.align === 'left' ? 'text-left px-4' : 'text-right px-2'} py-2 font-medium cursor-pointer hover:bg-slate-200 select-none`}
                                    style={{ color: col.color || '#475569' }}
                                  >
                                    {col.label}
                                    {geoSortColumn === col.key && (
                                      <span className="ml-1">{geoSortDir === 'asc' ? '↑' : '↓'}</span>
                                    )}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {[...(geoData.localidades || [])].sort((a, b) => {
                                let valA, valB;
                                if (geoSortColumn === 'localidad') {
                                  valA = a.localidad || '';
                                  valB = b.localidad || '';
                                  return geoSortDir === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
                                } else if (['total', 'activos', 'pct_activos'].includes(geoSortColumn)) {
                                  valA = a[geoSortColumn] || 0;
                                  valB = b[geoSortColumn] || 0;
                                } else {
                                  // Semáforo colors
                                  valA = a.semaforo?.[geoSortColumn]?.pct || 0;
                                  valB = b.semaforo?.[geoSortColumn]?.pct || 0;
                                }
                                return geoSortDir === 'asc' ? valA - valB : valB - valA;
                              }).map((loc, i) => (
                                <tr key={i} className="hover:bg-slate-50">
                                  <td className="px-4 py-2 font-medium text-slate-800">{loc.localidad}</td>
                                  <td className="text-right px-3 py-2 font-semibold">{loc.total}</td>
                                  <td className="text-right px-3 py-2 font-semibold">{loc.activos || 0}</td>
                                  <td className="text-right px-3 py-2 font-semibold" style={{
                                    color: (loc.pct_activos || 0) >= 40 ? '#16a34a' : (loc.pct_activos || 0) >= 20 ? '#ea580c' : '#dc2626'
                                  }}>{loc.pct_activos || 0}%</td>
                                  <td className="text-right px-2 py-2 text-green-600">{loc.semaforo.green?.pct || 0}%</td>
                                  <td className="text-right px-2 py-2 text-blue-600">{loc.semaforo.blue?.pct || 0}%</td>
                                  <td className="text-right px-2 py-2 text-yellow-600">{loc.semaforo.yellow?.pct || 0}%</td>
                                  <td className="text-right px-2 py-2 text-orange-600">{loc.semaforo.orange?.pct || 0}%</td>
                                  <td className="text-right px-2 py-2 text-red-600">{loc.semaforo.red?.pct || 0}%</td>
                                  <td className="text-right px-2 py-2 text-gray-500">{loc.semaforo.dark_gray?.pct || 0}%</td>
                                  <td className="text-right px-2 py-2 text-gray-800">{loc.semaforo.black?.pct || 0}%</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      
                      {/* SECCIÓN 3: Gráfico barras apiladas (top 15) */}
                      <div className="border rounded-xl p-4">
                        <h3 className="font-semibold text-slate-700 mb-4">Top 15 Localidades por Cantidad de Alumnos</h3>
                        <div className="h-[350px]">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={(geoData.localidades || []).slice(0, 15).map(loc => ({
                                name: loc.localidad.length > 15 ? loc.localidad.substring(0, 15) + '...' : loc.localidad,
                                green: loc.semaforo.green?.n || 0,
                                blue: loc.semaforo.blue?.n || 0,
                                yellow: loc.semaforo.yellow?.n || 0,
                                orange: loc.semaforo.orange?.n || 0,
                                red: loc.semaforo.red?.n || 0,
                                dark_gray: loc.semaforo.dark_gray?.n || 0,
                                black: loc.semaforo.black?.n || 0
                              }))}
                              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                              <XAxis 
                                dataKey="name" 
                                tick={{ fontSize: 10 }} 
                                angle={-45} 
                                textAnchor="end"
                                height={80}
                              />
                              <YAxis tick={{ fontSize: 11 }} />
                              <RechartsTooltip 
                                formatter={(value, name) => {
                                  const labels = {
                                    green: 'Activo con hitos',
                                    blue: 'Acompañar',
                                    yellow: 'Activo sin hitos',
                                    orange: 'Sin actividad',
                                    red: 'En riesgo',
                                    dark_gray: 'Inactivo',
                                    black: 'No iniciado'
                                  };
                                  return [value, labels[name] || name];
                                }}
                              />
                              <Legend 
                                formatter={(value) => {
                                  const labels = {
                                    green: 'Activo con hitos',
                                    blue: 'Acompañar',
                                    yellow: 'Activo sin hitos',
                                    orange: 'Sin actividad',
                                    red: 'En riesgo',
                                    dark_gray: 'Inactivo',
                                    black: 'No iniciado'
                                  };
                                  return labels[value] || value;
                                }}
                              />
                              <Bar dataKey="green" stackId="a" fill="#16a34a" />
                              <Bar dataKey="blue" stackId="a" fill="#2563eb" />
                              <Bar dataKey="yellow" stackId="a" fill="#ca8a04" />
                              <Bar dataKey="orange" stackId="a" fill="#ea580c" />
                              <Bar dataKey="red" stackId="a" fill="#dc2626" />
                              <Bar dataKey="dark_gray" stackId="a" fill="#6b7280" />
                              <Bar dataKey="black" stackId="a" fill="#111827" />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            )}
            
            {/* TAB: KPI HISTORY - OCULTA por solicitud del CTO */}
            {false && (
            <TabsContent value="kpi-history">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Evolución de KPIs
                  </CardTitle>
                  <CardDescription>Histórico de métricas por fecha de procesamiento</CardDescription>
                </CardHeader>
                <CardContent>
                  {kpiHistoryLoading ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                  ) : !kpiHistory || kpiHistory.dates?.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      Sin datos históricos disponibles. Los snapshots se generan automáticamente con cada procesamiento de archivos.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="font-semibold sticky left-0 bg-white min-w-[180px]">KPI</TableHead>
                            {kpiHistory.dates.map((d, i) => (
                              <TableHead key={i} className="text-center min-w-[100px]">
                                                                {(() => { const [year, month, day] = d.split('-'); return new Date(year, month-1, day).toLocaleDateString('es-AR', {day: '2-digit', month: 'short'}); })()}
                              </TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          <TableRow className="bg-slate-50">
                            <TableCell className="font-medium sticky left-0 bg-slate-50">Total Alumnos</TableCell>
                            {kpiHistory.kpis.total_alumnos.map((v, i) => (
                              <TableCell key={i} className="text-center">{v.toLocaleString()}</TableCell>
                            ))}
                          </TableRow>
                          <TableRow>
                            <TableCell className="font-medium sticky left-0 bg-white">Cursando (mes)</TableCell>
                            {kpiHistory.kpis.cursando_mes.map((v, i) => (
                              <TableCell key={i} className="text-center">{v.toLocaleString()}</TableCell>
                            ))}
                          </TableRow>
                          {/* Semáforo - 7 colores */}
                          <TableRow className="bg-green-50">
                            <TableCell className="font-medium sticky left-0 bg-green-50">🟢 Activo con hitos</TableCell>
                            {kpiHistory.kpis.semaforo.green.map((v, i) => (
                              <TableCell key={i} className="text-center">{v.abs.toLocaleString()} ({v.pct}%)</TableCell>
                            ))}
                          </TableRow>
                          <TableRow className="bg-blue-50">
                            <TableCell className="font-medium sticky left-0 bg-blue-50">🔵 Acompañar</TableCell>
                            {kpiHistory.kpis.semaforo.blue.map((v, i) => (
                              <TableCell key={i} className="text-center">{v.abs.toLocaleString()} ({v.pct}%)</TableCell>
                            ))}
                          </TableRow>
                          <TableRow className="bg-yellow-50">
                            <TableCell className="font-medium sticky left-0 bg-yellow-50">🟡 Activo sin hitos</TableCell>
                            {kpiHistory.kpis.semaforo.yellow.map((v, i) => (
                              <TableCell key={i} className="text-center">{v.abs.toLocaleString()} ({v.pct}%)</TableCell>
                            ))}
                          </TableRow>
                          <TableRow className="bg-orange-50">
                            <TableCell className="font-medium sticky left-0 bg-orange-50">🟠 Sin actividad</TableCell>
                            {kpiHistory.kpis.semaforo.orange.map((v, i) => (
                              <TableCell key={i} className="text-center">{v.abs.toLocaleString()} ({v.pct}%)</TableCell>
                            ))}
                          </TableRow>
                          <TableRow className="bg-red-50">
                            <TableCell className="font-medium sticky left-0 bg-red-50">🔴 En riesgo</TableCell>
                            {kpiHistory.kpis.semaforo.red.map((v, i) => (
                              <TableCell key={i} className="text-center">{v.abs.toLocaleString()} ({v.pct}%)</TableCell>
                            ))}
                          </TableRow>
                          <TableRow className="bg-slate-100">
                            <TableCell className="font-medium sticky left-0 bg-slate-100">⚫ Inactivo</TableCell>
                            {kpiHistory.kpis.semaforo.dark_gray.map((v, i) => (
                              <TableCell key={i} className="text-center">{v.abs.toLocaleString()} ({v.pct}%)</TableCell>
                            ))}
                          </TableRow>
                          <TableRow className="bg-slate-200">
                            <TableCell className="font-medium sticky left-0 bg-slate-200">⬛ No iniciado</TableCell>
                            {kpiHistory.kpis.semaforo.black.map((v, i) => (
                              <TableCell key={i} className="text-center">{v.abs.toLocaleString()} ({v.pct}%)</TableCell>
                            ))}
                          </TableRow>
                          {/* Rendimiento */}
                          <TableRow className="bg-emerald-50">
                            <TableCell className="font-medium sticky left-0 bg-emerald-50">% Aprobaron</TableCell>
                            {kpiHistory.kpis.rendimiento.aprobaron.map((v, i) => (
                              <TableCell key={i} className="text-center">{v.abs?.toLocaleString() || 0} ({v.pct}%)</TableCell>
                            ))}
                          </TableRow>
                          <TableRow className="bg-amber-50">
                            <TableCell className="font-medium sticky left-0 bg-amber-50">% Sin Aprobar</TableCell>
                            {kpiHistory.kpis.rendimiento.sin_aprobar.map((v, i) => (
                              <TableCell key={i} className="text-center">{v.abs?.toLocaleString() || 0} ({v.pct}%)</TableCell>
                            ))}
                          </TableRow>
                          <TableRow className="bg-slate-50">
                            <TableCell className="font-medium sticky left-0 bg-slate-50">% Sin Rendir</TableCell>
                            {kpiHistory.kpis.rendimiento.sin_rendir.map((v, i) => (
                              <TableCell key={i} className="text-center">{v.abs?.toLocaleString() || 0} ({v.pct}%)</TableCell>
                            ))}
                          </TableRow>
                          {/* Tasas */}
                          <TableRow className="bg-indigo-50">
                            <TableCell className="font-medium sticky left-0 bg-indigo-50">Tasa Finalización</TableCell>
                            {kpiHistory.kpis.tasas.finalizacion.map((v, i) => (
                              <TableCell key={i} className="text-center">{v}%</TableCell>
                            ))}
                          </TableRow>
                          <TableRow className="bg-rose-50">
                            <TableCell className="font-medium sticky left-0 bg-rose-50">Tasa Abandono</TableCell>
                            {kpiHistory.kpis.tasas.abandono.map((v, i) => (
                              <TableCell key={i} className="text-center">{v}%</TableCell>
                            ))}
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            )}
            
            {/* TAB: NPS 360 - Dashboard de análisis integral */}
            <TabsContent value="nps360">
              <NPS360Dashboard 
                selectedPartnerName={selectedPartner?.name}
                globalMode={!selectedPartnerId || showGlobalDashboard}
                getAuthHeaders={getAuthHeaders}
                onRelink={() => {
                  // Recargar datos después de re-vincular
                  loadNpsOverview(npsPanelInstance);
                }}
              />
            </TabsContent>
            
            {/* TAB: ENROLADOS 360 - Nuevos Ingresos (NI) */}
            <TabsContent value="enrolados360">
              <Enrolados360Dashboard token={getAuthToken()} />
            </TabsContent>
            
            {/* TAB: ATENCIÓN - Visible para todos los partners activos */}
            <TabsContent value="atencion">
                {/* Sub-tabs para Atención */}
                <Tabs defaultValue="atencion360" className="space-y-4">
                  <TabsList className="grid w-full grid-cols-5 max-w-3xl">
                    <TabsTrigger value="atencion360" className="flex items-center gap-1.5" data-testid="subtab-atencion360">
                      <BarChart2 className="w-4 h-4" />
                      Atención 360
                    </TabsTrigger>
                    <TabsTrigger value="derivaciones" className="flex items-center gap-1.5" data-testid="subtab-derivaciones">
                      <GitBranch className="w-4 h-4" />
                      Derivaciones
                    </TabsTrigger>
                    <TabsTrigger value="fcr-incidencia" className="flex items-center gap-1.5" data-testid="subtab-fcr-incidencia">
                      <Target className="w-4 h-4" />
                      FCR Incidencia
                    </TabsTrigger>
                    <TabsTrigger value="reincidencia" className="flex items-center gap-1.5" data-testid="subtab-reincidencia">
                      <Users className="w-4 h-4" />
                      Reincidencia
                    </TabsTrigger>
                    <TabsTrigger value="tickets-legacy" className="flex items-center gap-1.5" data-testid="subtab-tickets-legacy">
                      <Headphones className="w-4 h-4" />
                      Vista Clásica
                    </TabsTrigger>
                  </TabsList>
                  
                  {/* Atención 360 Dashboard */}
                  <TabsContent value="atencion360">
                    <Atencion360Dashboard
                      selectedPartnerSlug={showGlobalDashboard ? null : selectedPartner?.slug}
                      selectedPartnerName={showGlobalDashboard ? null : selectedPartner?.name}
                      getAuthHeaders={getAuthHeaders}
                    />
                  </TabsContent>
                  
                  {/* Derivaciones por Equipo (NUEVA - reemplaza vista por subclase) */}
                  <TabsContent value="derivaciones">
                    <DerivacionesPorEquipo
                      token={getAuthToken()}
                      selectedPartner={showGlobalDashboard ? 'global' : selectedPartner?.slug}
                    />
                  </TabsContent>
                  
                  {/* FCR por Incidencia (¿Problema de PROCESO?) */}
                  <TabsContent value="fcr-incidencia">
                    <FCRPorIncidenciaView
                      token={getAuthToken()}
                      selectedPartner={showGlobalDashboard ? 'global' : selectedPartner?.slug}
                    />
                  </TabsContent>
                  
                  {/* Reincidencia (separada de Derivaciones) */}
                  <TabsContent value="reincidencia">
                    <ReincidenciaView
                      token={getAuthToken()}
                      selectedPartner={showGlobalDashboard ? 'global' : selectedPartner?.slug}
                    />
                  </TabsContent>
                  
                  {/* Vista clásica (legacy) */}
                  <TabsContent value="tickets-legacy">
                    <div className="space-y-6">
                      {/* Header */}
                      <div className="flex items-center justify-between">
                        <div>
                          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                            <Headphones className="w-6 h-6 text-orange-600" />
                            Centro de Atención (Vista Clásica)
                          </h2>
                          <p className="text-slate-500">Análisis de tickets HubSpot — Consultas vs Campañas de recupero</p>
                        </div>
                        <Button variant="outline" onClick={loadTicketsData} disabled={loadingTickets}>
                          <RefreshCw className={`w-4 h-4 mr-2 ${loadingTickets ? 'animate-spin' : ''}`} />
                          Actualizar
                        </Button>
                      </div>
                      
                      {loadingTickets ? (
                        <div className="flex justify-center py-12">
                          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
                        </div>
                      ) : !ticketsData || ticketsData.kpis?.total === 0 ? (
                        <Card className="p-12 text-center">
                          <Headphones className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                          <h3 className="text-xl font-semibold text-slate-700 mb-2">No hay datos de tickets</h3>
                          <p className="text-slate-500 mb-4">Cargá el archivo de tickets HubSpot desde la pestaña "Carga de Datos"</p>
                        </Card>
                      ) : (
                        <>
                          {/* KPI Cards */}
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                            <Card className="bg-white border-slate-200">
                              <CardContent className="p-4">
                                <p className="text-xs text-slate-500 uppercase tracking-wider">Total Tickets</p>
                                <p className="text-3xl font-bold text-slate-800">{ticketsData.kpis.total.toLocaleString()}</p>
                              </CardContent>
                            </Card>
                        <Card className="bg-amber-50 border-amber-200">
                          <CardContent className="p-4">
                            <p className="text-xs text-amber-600 uppercase tracking-wider">Abiertos</p>
                            <p className="text-3xl font-bold text-amber-700">{ticketsData.kpis.abiertos.toLocaleString()}</p>
                          </CardContent>
                        </Card>
                        <Card className="bg-emerald-50 border-emerald-200">
                          <CardContent className="p-4">
                            <p className="text-xs text-emerald-600 uppercase tracking-wider">Resueltos</p>
                            <p className="text-3xl font-bold text-emerald-700">{ticketsData.kpis.resueltos.toLocaleString()}</p>
                          </CardContent>
                        </Card>
                        <Card className="bg-green-50 border-green-200">
                          <CardContent className="p-4">
                            <p className="text-xs text-green-600 uppercase tracking-wider">Consultas Reales</p>
                            <p className="text-3xl font-bold text-green-700">{ticketsData.kpis.consultas.toLocaleString()}</p>
                          </CardContent>
                        </Card>
                        <Card className="bg-blue-50 border-blue-200">
                          <CardContent className="p-4">
                            <p className="text-xs text-blue-600 uppercase tracking-wider">Campañas</p>
                            <p className="text-3xl font-bold text-blue-700">{ticketsData.kpis.campanas.toLocaleString()}</p>
                          </CardContent>
                        </Card>
                      </div>
                      
                      {/* Card Índice de Contacto */}
                      <Card className="bg-slate-50 border-slate-200">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-lg text-slate-800">Índice de Contacto con Atención</CardTitle>
                          <CardDescription>Por alumno activo histórico (4.609)</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Consultas espontáneas */}
                            <div className="text-center p-4 bg-white rounded-lg border border-slate-200">
                              <p className="text-3xl font-semibold text-blue-500">
                                {(ticketsData.kpis.consultas / 4609).toFixed(2)}
                              </p>
                              <p className="text-sm font-medium text-slate-700 mt-1">Consultas espontáneas</p>
                              <p className="text-[11px] text-slate-400 mt-0.5">Alumnos que contactaron por iniciativa propia</p>
                            </div>
                            
                            {/* Respuesta a campañas */}
                            <div className="text-center p-4 bg-white rounded-lg border border-slate-200">
                              <p className="text-3xl font-semibold text-green-600">
                                {(ticketsData.kpis.campanas / 4609).toFixed(2)}
                              </p>
                              <p className="text-sm font-medium text-slate-700 mt-1">Respuesta a campañas</p>
                              <p className="text-[11px] text-slate-400 mt-0.5">Alumnos que respondieron a acciones del equipo</p>
                            </div>
                            
                            {/* Engagement total */}
                            <div className="text-center p-4 bg-white rounded-lg border border-slate-200">
                              <p className="text-3xl font-semibold text-slate-600">
                                {(ticketsData.kpis.total / 4609).toFixed(2)}
                              </p>
                              <p className="text-sm font-medium text-slate-700 mt-1">Engagement total</p>
                              <p className="text-[11px] text-slate-400 mt-0.5">Contactos totales por alumno activo</p>
                            </div>
                          </div>
                          <p className="text-[10px] text-slate-400 mt-4 text-center">
                            Sobre 4.609 alumnos activos históricos al 22/03/2026. Se actualizará con cada recarga de datos.
                          </p>
                        </CardContent>
                      </Card>
                      
                      {/* Charts Row */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Evolución Mensual */}
                        <Card>
                          <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-lg">
                              <BarChart3 className="w-5 h-5 text-slate-600" />
                              Evolución Mensual
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            {ticketsData.por_mes?.length > 0 ? (
                              <ResponsiveContainer width="100%" height={280}>
                                <ComposedChart data={ticketsData.por_mes} margin={{ top: 10, right: 20, left: 10, bottom: 20 }}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                  <XAxis 
                                    dataKey="mes" 
                                    tick={{ fontSize: 11 }}
                                    tickFormatter={(v) => {
                                      if (!v) return '';
                                      const [year, month] = v.split('-');
                                      const months = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
                                      return `${months[parseInt(month)-1]} ${year?.slice(2)}`;
                                    }}
                                  />
                                  <YAxis tick={{ fontSize: 11 }} />
                                  <RechartsTooltip />
                                  <Legend />
                                  <Bar dataKey="consultas" stackId="a" fill="#22c55e" name="Consultas" />
                                  <Bar dataKey="campanas" stackId="a" fill="#3b82f6" name="Campañas" />
                                </ComposedChart>
                              </ResponsiveContainer>
                            ) : (
                              <p className="text-center text-slate-400 py-8">Sin datos de evolución</p>
                            )}
                          </CardContent>
                        </Card>
                        
                        {/* Por Fuente (Dona) */}
                        <Card>
                          <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-lg">
                              <Phone className="w-5 h-5 text-slate-600" />
                              Por Fuente
                            </CardTitle>
                          </CardHeader>
                          <CardContent>
                            {ticketsData.por_fuente?.length > 0 ? (
                              <ResponsiveContainer width="100%" height={280}>
                                <PieChart>
                                  <Pie
                                    data={ticketsData.por_fuente}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={100}
                                    dataKey="count"
                                    nameKey="fuente"
                                    label={({ fuente, percent }) => `${fuente} (${(percent * 100).toFixed(0)}%)`}
                                  >
                                    {ticketsData.por_fuente.map((entry, index) => (
                                      <Cell key={`cell-${index}`} fill={['#3b82f6', '#22c55e', '#eab308', '#ef4444', '#8b5cf6', '#f97316'][index % 6]} />
                                    ))}
                                  </Pie>
                                  <RechartsTooltip />
                                </PieChart>
                              </ResponsiveContainer>
                            ) : (
                              <p className="text-center text-slate-400 py-8">Sin datos por fuente</p>
                            )}
                          </CardContent>
                        </Card>
                      </div>
                      
                      {/* Por Agente */}
                      <Card>
                        <CardHeader>
                          <CardTitle className="flex items-center gap-2 text-lg">
                            <Users className="w-5 h-5 text-slate-600" />
                            Tickets por Agente (Top 10)
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          {ticketsData.por_agente?.length > 0 ? (
                            <div className="space-y-2">
                              {ticketsData.por_agente.slice(0, 10).map((agente, idx) => (
                                <div key={idx} className="flex items-center gap-3">
                                  <span className="w-40 text-sm text-slate-700 truncate">{agente.agente}</span>
                                  <div className="flex-1 bg-slate-100 rounded-full h-6 overflow-hidden">
                                    <div 
                                      className="h-full bg-orange-500 flex items-center justify-end pr-2"
                                      style={{ width: `${Math.min((agente.count / ticketsData.por_agente[0].count) * 100, 100)}%` }}
                                    >
                                      <span className="text-xs text-white font-medium">{agente.count}</span>
                                    </div>
                                  </div>
                                  <span className="text-xs text-emerald-600 w-20 text-right">{agente.resueltos} resueltos</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-center text-slate-400 py-4">Sin datos por agente</p>
                          )}
                        </CardContent>
                      </Card>
                      
                      {/* Motivos de Contacto - Inbound vs Outbound */}
                      {ticketsData.motivos_inbound?.length > 0 || ticketsData.motivos_outbound?.length > 0 ? (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          {/* Inbound (Consultas) */}
                          <Card>
                            <CardHeader>
                              <CardTitle className="flex items-center gap-2 text-lg">
                                <MessageSquare className="w-5 h-5 text-green-600" />
                                Motivos Inbound (Consultas)
                              </CardTitle>
                              <CardDescription>Clasificación de tickets entrantes por motivo</CardDescription>
                            </CardHeader>
                            <CardContent>
                              {ticketsData.motivos_inbound?.length > 0 ? (
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="border-b border-slate-200">
                                      <th className="text-left py-2 font-semibold text-slate-700">Categoría</th>
                                      <th className="text-right py-2 font-semibold text-slate-700">Cantidad</th>
                                      <th className="text-right py-2 font-semibold text-slate-700">%</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {ticketsData.motivos_inbound.map((m, idx) => (
                                      <tr key={idx} className="border-b border-slate-100 hover:bg-green-50">
                                        <td className="py-2 text-slate-700">{m.categoria}</td>
                                        <td className="py-2 text-right font-medium text-slate-800">{m.cantidad.toLocaleString()}</td>
                                        <td className="py-2 text-right text-green-600 font-medium">{m.porcentaje}%</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              ) : (
                                <p className="text-center text-slate-400 py-4">Sin datos de consultas</p>
                              )}
                            </CardContent>
                          </Card>
                          
                          {/* Outbound (Campañas) */}
                          <Card>
                            <CardHeader>
                              <CardTitle className="flex items-center gap-2 text-lg">
                                <Send className="w-5 h-5 text-blue-600" />
                                Motivos Outbound (Campañas)
                              </CardTitle>
                              <CardDescription>Clasificación de campañas salientes por tipo</CardDescription>
                            </CardHeader>
                            <CardContent>
                              {ticketsData.motivos_outbound?.length > 0 ? (
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="border-b border-slate-200">
                                      <th className="text-left py-2 font-semibold text-slate-700">Categoría</th>
                                      <th className="text-right py-2 font-semibold text-slate-700">Cantidad</th>
                                      <th className="text-right py-2 font-semibold text-slate-700">%</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {ticketsData.motivos_outbound.map((m, idx) => (
                                      <tr key={idx} className="border-b border-slate-100 hover:bg-blue-50">
                                        <td className="py-2 text-slate-700">{m.categoria}</td>
                                        <td className="py-2 text-right font-medium text-slate-800">{m.cantidad.toLocaleString()}</td>
                                        <td className="py-2 text-right text-blue-600 font-medium">{m.porcentaje}%</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              ) : (
                                <p className="text-center text-slate-400 py-4">Sin datos de campañas</p>
                              )}
                            </CardContent>
                          </Card>
                        </div>
                      ) : null}
                      
                      {/* Tabla de Alumnos */}
                      <Card>
                        <CardHeader>
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <CardTitle className="flex items-center gap-2 text-lg">
                              <Users className="w-5 h-5 text-slate-600" />
                              Alumnos con más Tickets
                            </CardTitle>
                            <div className="flex gap-2 flex-wrap">
                              <Button 
                                variant={ticketsFilter === 'all' ? 'default' : 'outline'} 
                                size="sm"
                                onClick={() => { setTicketsFilter('all'); setTicketsPage(1); }}
                              >
                                Todos
                              </Button>
                              <Button 
                                variant={ticketsFilter === 'consultas' ? 'default' : 'outline'} 
                                size="sm"
                                onClick={() => { setTicketsFilter('consultas'); setTicketsPage(1); }}
                                className={ticketsFilter === 'consultas' ? 'bg-green-600' : ''}
                              >
                                Solo Consultas
                              </Button>
                              <Button 
                                variant={ticketsFilter === 'campanas' ? 'default' : 'outline'} 
                                size="sm"
                                onClick={() => { setTicketsFilter('campanas'); setTicketsPage(1); }}
                                className={ticketsFilter === 'campanas' ? 'bg-blue-600' : ''}
                              >
                                Solo Campañas
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  // Exportar CSV
                                  const filteredData = ticketsData.alumnos_top?.filter(a => {
                                    if (ticketsFilter === 'consultas') return a.consultas > 0;
                                    if (ticketsFilter === 'campanas') return a.campanas > 0;
                                    return true;
                                  }) || [];
                                  
                                  const csvHeaders = ['Alumno', 'Email', 'Semáforo', 'Consultas', 'Campañas', 'Total', 'Último Contacto'];
                                  const csvRows = filteredData.map(a => [
                                    a.nombre,
                                    a.email,
                                    a.semaforo,
                                    a.consultas,
                                    a.campanas,
                                    a.total_tickets,
                                    a.ultimo_contacto ? new Date(a.ultimo_contacto).toLocaleDateString('es-ES') : ''
                                  ]);
                                  
                                  const csvContent = [csvHeaders, ...csvRows]
                                    .map(row => row.map(cell => `"${cell}"`).join(','))
                                    .join('\n');
                                  
                                  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                                  const url = URL.createObjectURL(blob);
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.download = `alumnos_tickets_${ticketsFilter}_${new Date().toISOString().split('T')[0]}.csv`;
                                  link.click();
                                  URL.revokeObjectURL(url);
                                }}
                                className="gap-1"
                              >
                                <Download className="w-4 h-4" />
                                Exportar
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          {(() => {
                            const filteredAlumnos = ticketsData.alumnos_top?.filter(a => {
                              if (ticketsFilter === 'consultas') return a.consultas > 0;
                              if (ticketsFilter === 'campanas') return a.campanas > 0;
                              return true;
                            }) || [];
                            const totalPages = Math.ceil(filteredAlumnos.length / TICKETS_PAGE_SIZE);
                            const paginatedAlumnos = filteredAlumnos.slice(
                              (ticketsPage - 1) * TICKETS_PAGE_SIZE,
                              ticketsPage * TICKETS_PAGE_SIZE
                            );
                            
                            return (
                              <>
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>Alumno</TableHead>
                                      <TableHead>Email</TableHead>
                                      <TableHead className="text-center">Semáforo</TableHead>
                                      <TableHead className="text-center">Consultas</TableHead>
                                      <TableHead className="text-center">Campañas</TableHead>
                                      <TableHead className="text-center">Total</TableHead>
                                      <TableHead>Último Contacto</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {paginatedAlumnos.map((alumno, idx) => (
                                      <TableRow 
                                        key={idx} 
                                        className="cursor-pointer hover:bg-slate-50"
                                        onClick={() => {
                                          setSearchTerm(alumno.email);
                                          setActiveTab('explorer');
                                        }}
                                      >
                                        <TableCell className="font-medium">{alumno.nombre}</TableCell>
                                        <TableCell className="text-slate-500 text-sm">{alumno.email}</TableCell>
                                        <TableCell className="text-center">
                                          <Badge className={`
                                            ${alumno.semaforo === 'green' ? 'bg-emerald-100 text-emerald-700' : ''}
                                            ${alumno.semaforo === 'blue' ? 'bg-blue-100 text-blue-700' : ''}
                                            ${alumno.semaforo === 'purple' ? 'bg-purple-100 text-purple-700' : ''}
                                            ${alumno.semaforo === 'yellow' ? 'bg-yellow-100 text-yellow-700' : ''}
                                            ${alumno.semaforo === 'orange' ? 'bg-orange-100 text-orange-700' : ''}
                                            ${alumno.semaforo === 'red' ? 'bg-red-100 text-red-700' : ''}
                                            ${alumno.semaforo === 'dark_gray' ? 'bg-gray-300 text-gray-700' : ''}
                                            ${alumno.semaforo === 'black' ? 'bg-gray-800 text-white' : ''}
                                            ${alumno.semaforo === 'gray' ? 'bg-gray-200 text-gray-600' : ''}
                                          `}>
                                            {alumno.semaforo === 'green' ? '● Con hitos' :
                                             alumno.semaforo === 'blue' ? '● Acompañar' :
                                             alumno.semaforo === 'purple' ? '● Sin hitos' :
                                             alumno.semaforo === 'yellow' ? '● Sin hitos' :
                                             alumno.semaforo === 'orange' ? '● Sin activ.' :
                                             alumno.semaforo === 'red' ? '● Riesgo' :
                                             alumno.semaforo === 'dark_gray' ? '● Inactivo' :
                                             alumno.semaforo === 'black' ? '● No inició' : '● Sin datos'}
                                          </Badge>
                                        </TableCell>
                                        <TableCell className="text-center text-green-600 font-medium">{alumno.consultas}</TableCell>
                                        <TableCell className="text-center text-blue-600 font-medium">{alumno.campanas}</TableCell>
                                        <TableCell className="text-center font-bold">{alumno.total_tickets}</TableCell>
                                        <TableCell className="text-slate-500 text-sm">
                                          {alumno.ultimo_contacto ? new Date(alumno.ultimo_contacto).toLocaleDateString('es-ES') : '-'}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                                
                                {/* Paginación */}
                                {totalPages > 1 && (
                                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                                    <p className="text-sm text-slate-500">
                                      Mostrando {(ticketsPage - 1) * TICKETS_PAGE_SIZE + 1} - {Math.min(ticketsPage * TICKETS_PAGE_SIZE, filteredAlumnos.length)} de {filteredAlumnos.length}
                                    </p>
                                    <div className="flex items-center gap-2">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setTicketsPage(p => Math.max(1, p - 1))}
                                        disabled={ticketsPage === 1}
                                      >
                                        <ChevronLeft className="w-4 h-4" />
                                        Anterior
                                      </Button>
                                      <span className="text-sm text-slate-600 px-2">
                                        Página {ticketsPage} de {totalPages}
                                      </span>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setTicketsPage(p => Math.min(totalPages, p + 1))}
                                        disabled={ticketsPage === totalPages}
                                      >
                                        Siguiente
                                        <ChevronRight className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </CardContent>
                      </Card>
                    </>
                  )}
                    </div>
                  </TabsContent>
                </Tabs>
              </TabsContent>
            
            {/* TAB: CALIDAD DE AGENTE - Solo para superadmin, gestor_academico y auditor */}
            {(authUser?.role === 'superadmin' || authUser?.role === 'gestor_academico' || authUser?.role === 'auditor') && (
              <TabsContent value="agent-quality">
                <AgentQualityCalibration token={getAuthToken()} />
              </TabsContent>
            )}
            
            {/* TAB 12: MOTOR DE PERSISTENCIA - Solo para superadmin, gestor_academico, auditor, academia_servicio */}
            {(authUser?.role === 'superadmin' || authUser?.role === 'gestor_academico' || authUser?.role === 'auditor' || authUser?.role === 'academia_servicio') && (
              <TabsContent value="persistencia">
                <PersistenciaEngine token={getAuthToken()} />
              </TabsContent>
            )}
            
            {/* TAB 8: USER MANAGEMENT - Solo para superadmin */}
            {canManageUsers && (
              <TabsContent value="users">
                <UserManagement partners={partners} />
              </TabsContent>
            )}
            
            {/* Registro de descargas (hardening 2026-10) - Solo superadmin */}
            {authUser?.role === 'superadmin' && (
              <TabsContent value="export-audit-log">
                <ExportAuditLog />
              </TabsContent>
            )}
          </Tabs>
        )}
      </main>
      
      {/* Footer - Blanco */}
      <footer className="mt-12 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-center gap-3 text-sm text-gray-500">
          <img 
            src={capabiliaLogo} 
            alt="Capabilia" 
            style={{ 
              width: '24px', 
              height: '24px',
              ...logoStyleColor
            }}
          />
          <p>Persistencia 360 by Capabilia &copy; 2026</p>
        </div>
      </footer>
      
      {/* Create Partner Dialog */}
      <CreatePartnerDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onCreated={(partner) => {
          setPartners(prev => [...prev, partner]);
          setSelectedPartnerId(partner.id);
        }}
      />
      
      {/* ══════════════════════════════════════════════════════════════════════════════════════ */}
      {/* MODAL: Máster de Servicio - Lista de incidencias */}
      {/* ══════════════════════════════════════════════════════════════════════════════════════ */}
      {showMasterServicio && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b bg-indigo-50 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-indigo-900">Máster de Servicio - Clasificación de Incidencias</h3>
                <p className="text-sm text-indigo-600">{masterServicio.length} incidencias configuradas</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowMasterServicio(false)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="flex-1 overflow-auto p-4">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="text-left p-2 font-medium">Incidencia</th>
                    <th className="text-center p-2 font-medium w-20">FCR</th>
                    <th className="text-center p-2 font-medium w-20">Escala</th>
                    <th className="text-left p-2 font-medium w-32">Área Destino</th>
                    <th className="text-left p-2 font-medium">Motivo</th>
                    <th className="text-center p-2 font-medium w-16">Activo</th>
                    <th className="text-center p-2 font-medium w-20">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {masterServicio.map((item, idx) => (
                    <tr key={idx} className={`border-b hover:bg-slate-50 ${!item.activo ? 'opacity-50' : ''}`}>
                      <td className="p-2 font-medium">{item.incidencia}</td>
                      <td className="p-2 text-center">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          item.fcr === 'si' ? 'bg-green-100 text-green-700' :
                          item.fcr === 'no' ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {item.fcr}
                        </span>
                      </td>
                      <td className="p-2 text-center">
                        {item.escala ? <CheckCircle className="w-4 h-4 text-orange-500 mx-auto" /> : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="p-2 text-xs">{item.area_destino}</td>
                      <td className="p-2 text-xs text-slate-500">{item.motivo || '—'}</td>
                      <td className="p-2 text-center">
                        {item.activo ? <CheckCircle className="w-4 h-4 text-green-500 mx-auto" /> : <X className="w-4 h-4 text-red-400 mx-auto" />}
                      </td>
                      <td className="p-2 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setMasterServicioEditing(item)}
                          className="text-indigo-600 hover:text-indigo-800"
                        >
                          <Pencil className="w-3 h-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="p-4 border-t bg-slate-50 flex justify-between items-center">
              <p className="text-xs text-slate-500">
                <strong>FCR:</strong> si=debe resolverse en 1er contacto | no=escala por diseño | parcial=depende
              </p>
              <Button onClick={() => setShowMasterServicio(false)}>Cerrar</Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de edición de una incidencia del Máster */}
      {masterServicioEditing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold mb-4">Editar: {masterServicioEditing.incidencia}</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">FCR esperado</label>
                <select
                  value={masterServicioEditing.fcr}
                  onChange={(e) => setMasterServicioEditing({...masterServicioEditing, fcr: e.target.value})}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="si">Sí (debe resolverse en 1er contacto)</option>
                  <option value="no">No (escala por diseño)</option>
                  <option value="parcial">Parcial (depende del caso)</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Área destino</label>
                <select
                  value={masterServicioEditing.area_destino}
                  onChange={(e) => setMasterServicioEditing({...masterServicioEditing, area_destino: e.target.value})}
                  className="w-full border rounded-lg p-2"
                >
                  <option value="Servicio">Servicio</option>
                  <option value="Administración">Administración</option>
                  <option value="IT">IT</option>
                  <option value="MECA/Contenidos">MECA/Contenidos</option>
                  <option value="Área Académica">Área Académica</option>
                  <option value="Partner">Partner</option>
                  <option value="Data Entry">Data Entry</option>
                  <option value="Otro">Otro</option>
                </select>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="escala"
                  checked={masterServicioEditing.escala}
                  onChange={(e) => setMasterServicioEditing({...masterServicioEditing, escala: e.target.checked})}
                  className="rounded"
                />
                <label htmlFor="escala" className="text-sm">Escala por diseño</label>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="activo"
                  checked={masterServicioEditing.activo}
                  onChange={(e) => setMasterServicioEditing({...masterServicioEditing, activo: e.target.checked})}
                  className="rounded"
                />
                <label htmlFor="activo" className="text-sm">Activo</label>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Motivo</label>
                <input
                  type="text"
                  value={masterServicioEditing.motivo || ''}
                  onChange={(e) => setMasterServicioEditing({...masterServicioEditing, motivo: e.target.value})}
                  className="w-full border rounded-lg p-2"
                  placeholder="Razón de la clasificación"
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setMasterServicioEditing(null)}>Cancelar</Button>
              <Button
                onClick={async () => {
                  const authToken = getAuthToken();
                  try {
                    const res = await fetch(
                      `${API_URL}/api/master-servicio/${encodeURIComponent(masterServicioEditing.incidencia)}?instancia=${masterServicioEditing.instancia || 'global'}`,
                      {
                        method: 'PUT',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${authToken}`
                        },
                        body: JSON.stringify({
                          fcr: masterServicioEditing.fcr,
                          escala: masterServicioEditing.escala,
                          area_destino: masterServicioEditing.area_destino,
                          motivo: masterServicioEditing.motivo,
                          activo: masterServicioEditing.activo
                        })
                      }
                    );
                    if (!res.ok) throw new Error('Error guardando');
                    
                    toast.success('Incidencia actualizada');
                    setMasterServicioEditing(null);
                    
                    // Recargar lista
                    const listRes = await fetch(`${API_URL}/api/master-servicio?solo_activos=false`, {
                      headers: { 'Authorization': `Bearer ${authToken}` }
                    });
                    const listData = await listRes.json();
                    setMasterServicio(listData.items || []);
                  } catch (err) {
                    toast.error('Error guardando cambios');
                  }
                }}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                Guardar
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* PDF Preview Modal */}
      <Dialog open={showPDFPreview} onOpenChange={setShowPDFPreview}>
        <DialogContent className="max-w-[900px] max-h-[95vh] overflow-auto p-0">
          <DialogHeader className="p-4 border-b bg-white sticky top-0 z-10">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="flex items-center gap-2">
                  <FileDown className="w-5 h-5 text-green-600" />
                  Preview del Informe PDF
                </DialogTitle>
                <DialogDescription>
                  Vista previa del informe con datos reales del dashboard.
                </DialogDescription>
              </div>
              <Button
                onClick={() => {
                  toast.loading('Generando PDF...', { id: 'pdf-gen' });
                  
                  try {
                    // Use direct jsPDF generation (no html2canvas)
                    generatePDFDirect(
                      dashboard,
                      partners.find(p => p.id === selectedPartnerId)?.name || 'Partner',
                      commercialData?.por_mes,
                      null,
                      ticketsData
                    );
                    
                    toast.success('PDF descargado correctamente', { id: 'pdf-gen' });
                  } catch (error) {
                    console.error('Error generating PDF:', error);
                    toast.error('Error al generar el PDF', { id: 'pdf-gen' });
                  }
                }}
                className="bg-green-600 hover:bg-green-700 text-white gap-2"
              >
                <Download className="w-4 h-4" />
                Descargar PDF
              </Button>
            </div>
          </DialogHeader>
          <div className="p-6 bg-white flex justify-center overflow-auto">
            <div style={{ transform: 'scale(0.7)', transformOrigin: 'top center' }}>
              <PDFPreview 
                dashboardData={dashboard}
                partnerName={partners.find(p => p.id === selectedPartnerId)?.name || 'Partner'}
                comercialData={commercialData?.por_mes}
                npsData={null}
                ticketsData={ticketsData}
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* NPS Upload Result Modal */}
      <Dialog open={!!npsUploadResult} onOpenChange={() => setNpsUploadResult(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <CheckCircle className="w-5 h-5" />
              Carga Global de NPS Completada
            </DialogTitle>
            <DialogDescription>
              Resumen de la importación de encuestas NPS
            </DialogDescription>
          </DialogHeader>
          
          {npsUploadResult && (
            <div className="space-y-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-blue-700">{npsUploadResult.total_processed}</p>
                  <p className="text-xs text-blue-600">Total Procesados</p>
                </div>
                <div className="bg-emerald-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-emerald-700">{npsUploadResult.responses_created}</p>
                  <p className="text-xs text-emerald-600">Nuevos</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-amber-700">{npsUploadResult.responses_updated}</p>
                  <p className="text-xs text-amber-600">Actualizados</p>
                </div>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-3">
                <p className="text-sm text-slate-600">
                  <span className="font-semibold text-slate-700">{npsUploadResult.responses_linked}</span> respuestas vinculadas a estudiantes existentes
                </p>
              </div>
              
              {/* Distribution by Instance */}
              {npsUploadResult.summary_by_instance && npsUploadResult.summary_by_instance.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">Distribución por Instancia:</h4>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {npsUploadResult.summary_by_instance.map((inst, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-white border rounded-lg px-3 py-2">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-slate-400" />
                          <span className="text-sm font-medium text-slate-700">{inst.instance}</span>
                          {inst.has_partner && (
                            <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                              Partner vinculado
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-sm">
                          <span className="text-slate-600">{inst.total} respuestas</span>
                          <span className="text-emerald-600 text-xs">({inst.linked} vinculadas)</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Errors if any */}
              {npsUploadResult.errors && npsUploadResult.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm font-semibold text-red-700 mb-1">Errores encontrados:</p>
                  <ul className="text-xs text-red-600 space-y-1">
                    {npsUploadResult.errors.slice(0, 5).map((err, idx) => (
                      <li key={idx}>• {err}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              <Button 
                className="w-full" 
                onClick={() => setNpsUploadResult(null)}
                data-testid="close-nps-result-modal"
              >
                Cerrar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* NPS Instance Details Sheet (Drill-down) */}
      <Sheet open={!!selectedNpsInstance} onOpenChange={() => {
        setSelectedNpsInstance(null);
        setNpsInstanceDetails(null);
      }}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-600" />
              {selectedNpsInstance}
            </SheetTitle>
            <SheetDescription>
              Detalle de respuestas NPS de esta instancia
            </SheetDescription>
          </SheetHeader>
          
          {loadingInstanceDetails ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : npsInstanceDetails ? (
            <div className="mt-6 space-y-6">
              {/* Stats de la instancia */}
              <div className="grid grid-cols-2 gap-3">
                <Card className={`${
                  npsInstanceDetails.stats.nps_score >= 50 ? "bg-emerald-50" :
                  npsInstanceDetails.stats.nps_score >= 0 ? "bg-amber-50" :
                  "bg-red-50"
                }`}>
                  <CardContent className="py-4 text-center">
                    <p className={`text-3xl font-bold ${
                      npsInstanceDetails.stats.nps_score >= 50 ? "text-emerald-700" :
                      npsInstanceDetails.stats.nps_score >= 0 ? "text-amber-700" :
                      "text-red-700"
                    }`}>
                      {npsInstanceDetails.stats.nps_score > 0 ? "+" : ""}{npsInstanceDetails.stats.nps_score}
                    </p>
                    <p className="text-sm text-slate-600">NPS Score</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="py-4 text-center">
                    <p className="text-3xl font-bold text-slate-700">{npsInstanceDetails.stats.total_responses}</p>
                    <p className="text-sm text-slate-500">Respuestas</p>
                  </CardContent>
                </Card>
              </div>
              
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-emerald-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-emerald-700">{npsInstanceDetails.stats.promoters}</p>
                  <p className="text-xs text-emerald-600">Promotores</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-amber-700">{npsInstanceDetails.stats.passives}</p>
                  <p className="text-xs text-amber-600">Neutrales</p>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <p className="text-lg font-bold text-red-700">{npsInstanceDetails.stats.detractors}</p>
                  <p className="text-xs text-red-600">Detractores</p>
                </div>
              </div>
              
              {/* Lista de respuestas */}
              <div>
                <h4 className="font-semibold text-slate-700 mb-3">Todas las respuestas ({npsInstanceDetails.responses.length})</h4>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3 pr-4">
                    {npsInstanceDetails.responses.map((resp, idx) => (
                      <div 
                        key={idx} 
                        className={`border rounded-lg p-3 ${
                          resp.nps_group === "Detractor" ? "border-red-200 bg-red-50/50" :
                          resp.nps_group === "Passive" ? "border-amber-200 bg-amber-50/50" :
                          "border-emerald-200 bg-emerald-50/50"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-600 truncate">{resp.email}</p>
                            {resp.comment && (
                              <p className="text-sm text-slate-700 mt-1 italic">"{resp.comment}"</p>
                            )}
                          </div>
                          <Badge className={`shrink-0 ${
                            resp.nps_group === "Detractor" ? "bg-red-100 text-red-700" :
                            resp.nps_group === "Passive" ? "bg-amber-100 text-amber-700" :
                            "bg-emerald-100 text-emerald-700"
                          }`}>
                            {resp.score}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-400 mt-2">{resp.date}</p>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
      
      {/* Student 360 Profile Panel */}
      <Student360Profile 
        student={selectedStudent}
        open={showProfile360}
        onOpenChange={setShowProfile360}
        partnerSlug={selectedPartner?.slug}
      />
    </div>
  );
}

export default App;
